/**
 * MERCADO · Watchlist de licitaciones externas filtradas por CPV.
 *
 * Tab dentro de Ofertas Comercial. Muestra las 27 columnas solicitadas
 * por el cliente, con filtros, edición, flujo de estados y botón
 * "Pasar a estudio" (crea licitación en /licitaciones).
 * Columnas configurables con persistencia en localStorage.
 */
import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { useZona } from "@/context/ZonaContext";
import {
  RefreshCw, Plus, X, Trash2, Pencil, Filter, Loader2, ArrowRight, Check,
  ExternalLink, ShoppingCart, CheckCircle2, AlertTriangle,
  Download, Sparkles, ThumbsUp, ThumbsDown, HelpCircle, SlidersHorizontal,
  Upload, Globe, TrendingUp, MapPin, Tag, GitCompare, UserCheck,
  ClipboardCheck, Link2, Copy, ChevronDown, ChevronUp,
  Inbox, Eye, XCircle, Layers, Search, Info,
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { fmtFechaOferta } from "./ofertas_comercial/shared";
import MasterExcelImportModal from "@/components/tender360/MasterExcelImportModal";
import HistoricoImportModal from "@/components/historico/HistoricoImportModal";
import PlacspSyncModal from "@/components/tender360/PlacspSyncModal";
import AcceptFromMercadoModal from "@/components/mercado/AcceptFromMercadoModal";
import { DescarteModal } from "@/components/mercado/DescarteModal";
import { OfertaForm } from "@/pages/ofertas_comercial/OfertaForm";
import DGFilter, { useDGFilter, DG_LABELS } from "@/components/DGFilter";
import DGCompareCard from "@/components/DGCompareCard";
import { useAuth } from "@/context/AuthContext";
import { HelpTip } from "@/components/HelpTip";
import { ImporteRangeFilter } from "@/components/ImporteRangeFilter";
import ConciliacionPresentadasModal from "@/components/mercado/ConciliacionPresentadasModal";
import MercadoKanban from "@/components/mercado/MercadoKanban";
import SavedViewsBar from "@/components/crm/SavedViewsBar";
import ActiveFilterChips from "@/components/crm/ActiveFilterChips";

// ── Column definitions ─────────────────────────────────────────────────────
const MERCADO_COLS = [
  { id: "titulo",         label: "Título",             def: true,  align: "left"   },
  { id: "estado",         label: "Estado",             def: true,  align: "left"   },
  { id: "link",           label: "Enlace",             def: true,  align: "center" },
  { id: "expediente",     label: "Expediente",         def: false, align: "left"   },
  { id: "pais",           label: "País",               def: false, align: "left"   },
  { id: "provincia",      label: "Provincia",          def: true,  align: "left"   },
  { id: "ccaa",           label: "Comunidad",          def: true,  align: "left"   },
  { id: "plataforma",     label: "Plataforma",         def: true,  align: "left"   },
  { id: "cliente",        label: "Cliente",            def: true,  align: "left"   },
  { id: "objeto",         label: "Objeto del contrato",def: false, align: "left"   },
  { id: "cpv",            label: "CPV",                def: false, align: "left"   },
  { id: "dg",             label: "DG",                 def: true,  align: "left"   },
  { id: "actividad",      label: "Actividad",          def: true,  align: "left"   },
  { id: "win_score",      label: "Win %",              def: true,  align: "center" },
  { id: "enrich_timeline",label: "Enriquecimiento",    def: true,  align: "center" },
  { id: "dir_zona",       label: "Dir. Zona",          def: false, align: "left"   },
  { id: "tipo_contrato",  label: "Tipo contrato",      def: false, align: "left"   },
  { id: "tipo_cliente",   label: "Tipo cliente",       def: false, align: "left"   },
  { id: "procedimiento",  label: "Procedimiento",      def: false, align: "left"   },
  { id: "presupuesto",    label: "Presup.",            def: true,  align: "right"  },
  { id: "pres_ute_mm",    label: "Pres. UTE M€",      def: false, align: "right"  },
  { id: "pres_anual_ute", label: "Pres. Anual UTE",   def: false, align: "right"  },
  { id: "anios",          label: "Años",               def: false, align: "right"  },
  { id: "prorroga",       label: "Prórroga",           def: false, align: "right"  },
  { id: "f_presentacion", label: "F. Presentación",   def: true,  align: "left"   },
  { id: "limite_preg",    label: "Límite preg.",       def: false, align: "left"   },
  { id: "emp_g_valoriza", label: "Emp. G. Valoriza",  def: false, align: "left"   },
  { id: "pct_ute",        label: "% UTE VSM",          def: false, align: "right"  },
  { id: "emp_oferta",     label: "Emp. Oferta",        def: false, align: "left"   },
  { id: "incumbent",      label: "Incumbent",          def: true,  align: "left"   },
  { id: "winner",         label: "Winner",             def: true,  align: "left"   },
  { id: "ia",             label: "Pando",                 def: true,  align: "center" },
  { id: "assigned_to",    label: "Responsable",         def: true,  align: "left"   },
  { id: "motivo_no",      label: "Motivo NO",          def: false, align: "left"   },
];

const LS_KEY        = "mercado_visible_cols_v1";
const LS_ORDER_KEY  = "mercado_col_order_v1";
const LS_SORT_KEY   = "mercado_sort_v1";
const LS_WIDTHS_KEY = "mercado_col_widths_v1";
const LS_DENSITY_KEY = "mercado_density_v1";
const LS_PROFILES_KEY = "mercado_view_profiles_v1";
const DENSITY_OPTIONS = ["compact", "comfortable", "expanded"];
const DENSITY_LABEL = { compact: "Compacto", comfortable: "Cómodo", expanded: "Expandido" };
const DENSITY_CELL_CLASS = { compact: "px-1.5 py-0.5", comfortable: "px-2 py-1.5", expanded: "px-3 py-2.5" };
const DENSITY_TEXT = { compact: "text-[10px]", comfortable: "text-[11px]", expanded: "text-xs" };
const DEFAULT_COLS  = new Set(MERCADO_COLS.filter(c => c.def).map(c => c.id));
const DEFAULT_ORDER = MERCADO_COLS.map(c => c.id);
const DEFAULT_SORT  = { col: "presupuesto", dir: "desc" };
const DEFAULT_WIDTHS = {
  titulo: 340, estado: 110, link: 90, expediente: 200, win_score: 90,
  enrich_timeline: 130,
  pais: 90, provincia: 110, ccaa: 130, plataforma: 100, cliente: 200,
  objeto: 320, cpv: 110, dg: 130, actividad: 140, dir_zona: 130,
  tipo_contrato: 130, tipo_cliente: 140, procedimiento: 140,
  presupuesto: 130, pres_ute_mm: 130, pres_anual_ute: 140, anios: 70,
  prorroga: 90, f_presentacion: 120, limite_preg: 110, emp_g_valoriza: 150, assigned_to: 160,
  pct_ute: 90, emp_oferta: 150, incumbent: 140, winner: 140, ia: 90,
  motivo_no: 160,
};
const MIN_COL_W = 60;
const MAX_COL_W = 900;

// Mapa colId → función que devuelve el valor a ordenar
const SORT_ACCESSORS = {
  titulo:         (m) => (m.titulo_ia || m.objeto_contrato || "").toLowerCase(),
  estado:         (m) => m.estado_licitacion || "",
  link:           (m) => m.url_externa || "",
  expediente:     (m) => m.expediente || "",
  win_score:      (m) => Number(m.ai_win_score) || 0,
  pais:           (m) => m.pais || "",
  provincia:      (m) => m.provincia || "",
  ccaa:           (m) => m.comunidad_autonoma || "",
  plataforma:     (m) => m.plataforma_licitacion_electronica || "",
  cliente:        (m) => m.cliente || "",
  objeto:         (m) => m.objeto_contrato || "",
  cpv:            (m) => m.cpv || "",
  dg:             (m) => m.direccion_general || "",
  actividad:      (m) => m.actividad_informe_comercial || "",
  dir_zona:       (m) => m.direccion_zona || "",
  tipo_contrato:  (m) => m.tipo_contrato || "",
  tipo_cliente:   (m) => m.tipo_cliente || "",
  procedimiento:  (m) => m.procedimiento || "",
  presupuesto:    (m) => Number(m.presupuesto) || 0,
  pres_ute_mm:    (m) => Number(m.presupuesto_licitacion_ute_mm) || 0,
  pres_anual_ute: (m) => Number(m.presupuesto_anual_con_ute) || 0,
  anios:          (m) => Number(m.duracion_anios) || 0,
  prorroga:       (m) => Number(m.prorroga) || 0,
  f_presentacion: (m) => m.fecha_presentacion || "",
  limite_preg:    (m) => m.limite_preguntas || "",
  emp_g_valoriza: (m) => m.empresa_g_valoriza || "",
  pct_ute:        (m) => Number(m.porcentaje_empresa_g_valoriza) || 0,
  emp_oferta:     (m) => m.empresa_oferta || "",
  incumbent:      (m) => m.incumbent || "",
  winner:         (m) => m.winner || "",
  ia:             (m) => Number(m.ai_score) || 0,
  assigned_to:    (m) => (m.assigned_to || "").toLowerCase(),
  motivo_no:      (m) => m.motivo_no_presentacion || "",
};

const ESTADO_COLOR = {
  PROPUESTA_OPORTUNIDAD:       "bg-cyan-100 text-cyan-700",
  ANULADA:                     "bg-gray-100 text-gray-600",
  SUSPENDIDA:                  "bg-orange-100 text-orange-800",
  SUSPENDIDA_REACTIVABLE_CONTINUA: "bg-yellow-100 text-yellow-800",
  PUBLICADA:                   "bg-blue-100 text-blue-700",
  POR_DECIDIR:                 "bg-amber-100 text-amber-700",
  ESTUDIO:                     "bg-indigo-100 text-indigo-700",
  NO_ESTRATEGICA:              "bg-zinc-100 text-zinc-700",
  DESCARTADA:                  "bg-red-50 text-red-500",
  PRESENTADA:                  "bg-green-100 text-green-700",
  NO_PRESENTADA:               "bg-rose-100 text-rose-700",
};

// Iter206 · Estado "En estudio" unificado. Cualquier valor legacy EN_ESTUDIO
// se muestra como ESTUDIO (labels y transiciones sólo usan ESTUDIO).
const normalizeEstado = (e) => (e === "EN_ESTUDIO" ? "ESTUDIO"
  : (e === "SUSPENDIDA_REACTIVABLE" || e === "SUSPENDIDA_NO_REACTIVABLE") ? "SUSPENDIDA_REACTIVABLE_CONTINUA"
  : e);

// Iter166/208 · Transiciones permitidas por estado actual (frontend mirror del backend).
// Iter208 · NO_ESTRATEGICA sigue en el dropdown como "atajo" para marcar la
// oferta como no estratégica, pero el backend la traduce a PUBLICADA +
// `estrategica: false` (fiel al diagrama de flujo). Los docs históricos ya
// están migrados; nunca se creará un doc con `estado_licitacion=NO_ESTRATEGICA`.
const ESTADOS_INICIALES = ["PUBLICADA", "POR_DECIDIR", "ESTUDIO"];
const ESTADOS_FINALES = ["PRESENTADA", "NO_PRESENTADA", "SUSPENDIDA", "ANULADA"];

// David 2026-06 · Clasificador cliente de "No presentada" NO estratégica —
// mismo criterio que el backend (is_nopres_non_strategic): motivo "Non-strategic",
// flag estrategica=false, estado NO_ESTRATEGICA o SIN motivo. Mantiene la
// coherencia tarjeta↔lista al mover las no estratégicas al grupo Non-strategic.
const _normMarket = (s) => (s || "").toString().trim().toLowerCase().replace(/[\s_-]/g, "");
const isNoPresNonStrategic = (m) => {
  const est = (m.estado_licitacion || "").toUpperCase();
  // NO_PRESENTADA → se clasifica SÓLO por motivo (igual que el backend
  // is_nopres_non_strategic): "Non-strategic" o sin motivo → no estratégica.
  if (["NO_PRESENTADA", "NO PRESENTADA"].includes(est)) {
    const mn = _normMarket(m.motivo_no_presentacion);
    return !mn || mn === "nonstrategic";
  }
  // Oportunidades de mercado marcadas no estratégicas (flag/estado).
  if (est === "NO_ESTRATEGICA") return true;
  if (m.estrategica === false && est === "PUBLICADA") return true;
  return false;
};
const ALLOWED_TRANSITIONS = {
  PROPUESTA_OPORTUNIDAD: ["PUBLICADA", "POR_DECIDIR", "ESTUDIO", "NO_ESTRATEGICA", "DESCARTADA"],
  PUBLICADA:      [...ESTADOS_INICIALES, "NO_ESTRATEGICA", "DESCARTADA", ...ESTADOS_FINALES],
  POR_DECIDIR:    [...ESTADOS_INICIALES, "NO_ESTRATEGICA", "DESCARTADA", ...ESTADOS_FINALES],
  ESTUDIO:        [...ESTADOS_INICIALES, "NO_ESTRATEGICA", "DESCARTADA", ...ESTADOS_FINALES],
  DESCARTADA:     ["PUBLICADA", "POR_DECIDIR", "ESTUDIO"],
  PRESENTADA:     ["PUBLICADA", "POR_DECIDIR", "ESTUDIO"],
  NO_PRESENTADA:  ["PUBLICADA", "POR_DECIDIR", "ESTUDIO"],
  SUSPENDIDA:     ["PUBLICADA", "POR_DECIDIR", "ESTUDIO", "SUSPENDIDA_REACTIVABLE_CONTINUA", "ANULADA"],
  SUSPENDIDA_REACTIVABLE_CONTINUA: ["PUBLICADA", "POR_DECIDIR", "ESTUDIO", "ANULADA"],
  ANULADA:        ["PUBLICADA", "POR_DECIDIR", "ESTUDIO"],
  NO_ESTRATEGICA: ["PUBLICADA", "POR_DECIDIR", "ESTUDIO", "DESCARTADA"],  // legacy compat
};
const ESTADO_LABEL = {
  PROPUESTA_OPORTUNIDAD: "Detectada",
  PUBLICADA: "Publicada", POR_DECIDIR: "Por decidir",
  ESTUDIO: "En estudio", EN_ESTUDIO: "En estudio",
  NO_ESTRATEGICA: "No estratégica", DESCARTADA: "Descartada",
  PRESENTADA: "Presentada", NO_PRESENTADA: "No presentada", SUSPENDIDA: "Suspendida",
  ANULADA: "Anulada",
  SUSPENDIDA_REACTIVABLE_CONTINUA: "Susp. reactivable (continúa)",
};

const fmt = (n) => (n == null || n === "" ? "—" : new Intl.NumberFormat("es-ES").format(n));
const fmtEUR = (n) => (n == null || n === "" ? "—" : new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n));

export default function MercadoView() {
  const { zonaGlobal } = useZona();
  const [data, setData] = useState(null);
  // Iter228 · Declarado temprano para evitar TDZ en el useMemo `sortedItems`
  // que ya lo referencia.
  const [prepresLicits, setPrepresLicits] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { dg, setDg, filterByDG } = useDGFilter(user?.email);
  const [filters, setFilters] = useState(() => {
    const _curYear = String(new Date().getFullYear());
    try {
      const p = new URLSearchParams(window.location.search);
      const yParam = p.get("year");
      return {
        q: p.get("q") || "", estado: p.get("estado") || "", direccion_general: p.get("direccion_general") || "",
        actividad: p.get("actividad") || "", provincia: p.get("provincia") || "", ccaa: p.get("ccaa") || "",
        plataforma: p.get("plataforma") || "", cliente: p.get("cliente") || "",
        expediente: p.get("expediente") || "", tipo: p.get("tipo") || "",
        fecha_from: p.get("fecha_from") || "", fecha_to: p.get("fecha_to") || "",
        // Iter403 · año de Fecha de Presentación. "" = Todos (en URL se guarda como "all").
        year: yParam === "all" ? "" : (yParam || _curYear),
        importe_min: Number(p.get("importe_min") || 0),
        importe_max: Number(p.get("importe_max") || 0),
        trend_up_only: p.get("trend_up_only") === "1",
      };
    } catch {
      return {
        q: "", estado: "", direccion_general: "", actividad: "",
        provincia: "", ccaa: "", plataforma: "", cliente: "",
        expediente: "", tipo: "",
        fecha_from: "", fecha_to: "", year: _curYear, importe_min: 0, importe_max: 0,
        trend_up_only: false,
      };
    }
  });
  // SU03 (valoriza-verificacion-ola3-feedback-ofertas-su-2026-10-13.md,
  // H02/H03) · "buscar Alcobendas mostraba Torrejón de Ardoz" — causa
  // confirmada: cada pulsación disparaba un GET nuevo sin "debounce" ni
  // guarda de orden; si las respuestas llegaban desordenadas por latencia
  // de red, la última en llegar (no la del texto actual) pisaba el estado.
  // `qInput` da feedback instantáneo al escribir; sólo tras una pausa de
  // 300ms se traslada a `filters.q` (y por tanto a la petición real).
  const [qInput, setQInput] = useState(filters.q);
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters(f => (f.q === qInput ? f : { ...f, q: qInput }));
    }, 300);
    return () => clearTimeout(t);
  }, [qInput]); // eslint-disable-line react-hooks/exhaustive-deps
  // Iter179.1 · Foco por comercial: filtra por assigned_to == user.email
  // Persistido en localStorage por usuario. Default OFF ("Ver todo") — el
  // comercial que lo quiera lo activa manualmente y queda recordado.
  const [mineOnly, setMineOnly] = useState(() => {
    try {
      const key = `mercado-mine-${(user?.email || "").toLowerCase()}`;
      const v = localStorage.getItem(key);
      return v === "1";  // default OFF
    } catch { return false; }
  });
  useEffect(() => {
    try {
      const key = `mercado-mine-${(user?.email || "").toLowerCase()}`;
      localStorage.setItem(key, mineOnly ? "1" : "0");
    } catch { /* no-op */ }
  }, [mineOnly, user?.email]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editingOferta, setEditingOferta] = useState(null); // Iter279 · editar filas _fromPrepres (ofertas_comercial) con el form correcto
  const [showMasterImport, setShowMasterImport] = useState(false);
  const [showHistImport, setShowHistImport] = useState(false);
  const [showConciliacion, setShowConciliacion] = useState(false);
  const [showPlacspSync, setShowPlacspSync] = useState(false);
  const [acceptingOpp, setAcceptingOpp] = useState(null);   // Iter156 · modal accept
  const [showManualOfferForm, setShowManualOfferForm] = useState(false); // Iter192 · alta manual
  // Iter185.1 · Modal picker "Nueva oferta" desde toolbar Mercado
  const [showNewOfferPicker, setShowNewOfferPicker] = useState(false);
  // Iter157 · Pestaña "Aceptadas" muestra oportunidades ya convertidas a
  // licitación (con oferta_comercial_id). Persistida en URL/session para
  // no perder contexto al refrescar.
  const [viewMode, setViewMode] = useState(() => {
    try {
      const fromUrl = new URLSearchParams(window.location.search).get("view");
      const stored = fromUrl || sessionStorage.getItem("mercado-view-mode") || "propuestas";
      // Iter166/168 · Migra sesiones antiguas
      if (stored === "cartera") return "propuestas";
      if (stored === "cerradas") return "resueltas";
      if (stored === "aceptadas") return "strategic";
      if (stored === "descartadas") return "propuestas";
      return stored;
    } catch { return "propuestas"; }
  });
  useEffect(() => {
    try { sessionStorage.setItem("mercado-view-mode", viewMode); } catch (_e) { /* no-op */ }
  }, [viewMode]);
  // Iter162 · Ocultar por defecto oportunidades con fecha_presentacion pasada.
  // Toggle con contador; persistido en sessionStorage.
  const [hideExpired, setHideExpired] = useState(() => {
    try { return sessionStorage.getItem("mercado-hide-expired") !== "false"; }
    catch { return true; }
  });
  useEffect(() => {
    try { sessionStorage.setItem("mercado-hide-expired", String(hideExpired)); } catch (_e) { /* no-op */ }
  }, [hideExpired]);
  // Iter164 · Filtro rápido "vencen esta semana" activado desde la tarjeta KPI
  const [dueSoonOnly, setDueSoonOnly] = useState(false);
  // Iter180 · Filtro por market_type = ADDRESSABLE | NON_STRATEGIC. Persistido.
  const [marketTypeFilter, setMarketTypeFilter] = useState(() => {
    try { return sessionStorage.getItem("mercado-market-type") || ""; } catch { return ""; }
  });
  useEffect(() => {
    try { sessionStorage.setItem("mercado-market-type", marketTypeFilter); } catch (_e) { /* no-op */ }
  }, [marketTypeFilter]);
  // Iter170 · Filtro "Sin responsable"
  const [unassignedOnly, setUnassignedOnly] = useState(false);
  // Iter170/171 · Estado batch score GO/NO-GO (background + polling)
  const [batchScoreRunning, setBatchScoreRunning] = useState(false);
  const [batchScoreProgress, setBatchScoreProgress] = useState(null); // {progress, total}
  // Iter170 · Filtro por owner venido de Admin Ownership panel (query param ?owner=...)
  const [ownerFilter, setOwnerFilter] = useState(() => {
    try {
      const o = new URLSearchParams(window.location.search).get("owner");
      if (o === "__unassigned__") { return ""; } // se activa unassignedOnly abajo
      return o || "";
    } catch { return ""; }
  });
  useEffect(() => {
    // Si vino con __unassigned__ activamos el filtro sin responsable
    try {
      const o = new URLSearchParams(window.location.search).get("owner");
      if (o === "__unassigned__") setUnassignedOnly(true);
    } catch {}
  }, []);
  // Iter313 · G03 — filtros persistentes: igual que en OfertasComercial,
  // el estado de filtros/vista se refleja en la URL (replaceState) para
  // conservarlo al volver de una ficha o al compartir el enlace.
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const setOrDel = (key, val) => { if (val) url.searchParams.set(key, val); else url.searchParams.delete(key); };
      setOrDel("q", filters.q);
      setOrDel("estado", filters.estado);
      setOrDel("direccion_general", filters.direccion_general);
      setOrDel("actividad", filters.actividad);
      setOrDel("provincia", filters.provincia);
      setOrDel("ccaa", filters.ccaa);
      setOrDel("plataforma", filters.plataforma);
      setOrDel("cliente", filters.cliente);
      setOrDel("expediente", filters.expediente);
      setOrDel("tipo", filters.tipo);
      setOrDel("fecha_from", filters.fecha_from);
      setOrDel("fecha_to", filters.fecha_to);
      url.searchParams.set("year", filters.year || "all");
      setOrDel("importe_min", filters.importe_min ? String(filters.importe_min) : "");
      setOrDel("importe_max", filters.importe_max ? String(filters.importe_max) : "");
      setOrDel("trend_up_only", filters.trend_up_only ? "1" : "");
      setOrDel("view", viewMode !== "propuestas" ? viewMode : "");
      window.history.replaceState({}, "", url.toString());
    } catch { /* no-op */ }
  }, [filters, viewMode]);
  const todayISO = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const plus7ISO = useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() + 7);
    return d.toISOString().slice(0, 10);
  }, []);

  // #7 · Desglose vigentes/vencidas de los estados de pipeline. El contador
  // (data.kpis, de ofertas_comercial) incluye las VENCIDAS, pero el listado
  // oculta las de plazo pasado (hideExpired) → "la tarjeta dice 11 y salen 8".
  // Mostramos el desglose en la tarjeta para que cuadre visualmente.
  const strategicSplit = useMemo(() => {
    const res = {};
    for (const st of ["PUBLICADA", "POR_DECIDIR", "ESTUDIO"]) {
      const recs = (prepresLicits || []).filter(
        x => (x.estado_licitacion || "").toUpperCase().replace(/\s+/g, "_") === st,
      );
      const vencidas = recs.filter(x => {
        const f = (x.fecha_presentacion || "").slice(0, 10);
        return f && f < todayISO;
      }).length;
      res[st] = { total: recs.length, vencidas };
    }
    return res;
  }, [prepresLicits, todayISO]);
  const splitSub = (key) => {
    const s = strategicSplit[key];
    if (!s || s.vencidas <= 0) return null;
    return `${s.total - s.vencidas} vigentes · ${s.vencidas} vencidas`;
  };


  // Refetch cuando otro módulo dispara master-excel-imported o mercado-sync-completed
  useEffect(() => {
    const handler = () => { load(); };
    window.addEventListener("master-excel-imported", handler);
    window.addEventListener("mercado-sync-completed", handler);
    return () => {
      window.removeEventListener("master-excel-imported", handler);
      window.removeEventListener("mercado-sync-completed", handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [showForm, setShowForm] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [scoring, setScoring] = useState(null);  // id siendo scoreado
  const [scoreResult, setScoreResult] = useState(null);  // {item, result}
  const [batchScoring, setBatchScoring] = useState(false);

  // Column selector + orden + sort — todo persistido en localStorage
  const [visibleCols, setVisibleCols] = useState(() => {
    try {
      const saved = localStorage.getItem(LS_KEY);
      return saved ? new Set(JSON.parse(saved)) : DEFAULT_COLS;
    } catch { return DEFAULT_COLS; }
  });
  const [colOrder, setColOrder] = useState(() => {
    try {
      const saved = localStorage.getItem(LS_ORDER_KEY);
      if (!saved) return DEFAULT_ORDER;
      const parsed = JSON.parse(saved);
      // Añadir ids nuevos (que no estaban cuando el user guardó) al final
      const missing = DEFAULT_ORDER.filter(id => !parsed.includes(id));
      return [...parsed.filter(id => DEFAULT_ORDER.includes(id)), ...missing];
    } catch { return DEFAULT_ORDER; }
  });
  const [sortState, setSortState] = useState(() => {
    try {
      const saved = localStorage.getItem(LS_SORT_KEY);
      return saved ? JSON.parse(saved) : DEFAULT_SORT;
    } catch { return DEFAULT_SORT; }
  });
  // iter140 · anchos por columna, resize por drag persistido en localStorage
  const [colWidths, setColWidths] = useState(() => {
    try {
      const saved = localStorage.getItem(LS_WIDTHS_KEY);
      return saved ? { ...DEFAULT_WIDTHS, ...JSON.parse(saved) } : { ...DEFAULT_WIDTHS };
    } catch { return { ...DEFAULT_WIDTHS }; }
  });
  const resizeRef = useRef(null); // {id, startX, startW}
  const [colPickerOpen, setColPickerOpen] = useState(false);
  const colPickerRef = useRef(null);
  // Iter142 · toggle DG compare
  const [compareMode, setCompareMode] = useState(false);
  // Iter143 · densidad tabla persistida
  const [density, setDensity] = useState(() => {
    try {
      const v = localStorage.getItem(LS_DENSITY_KEY);
      return DENSITY_OPTIONS.includes(v) ? v : "comfortable";
    } catch { return "comfortable"; }
  });
  const setDensityAndPersist = (v) => {
    setDensity(v);
    try { localStorage.setItem(LS_DENSITY_KEY, v); } catch { /* no-op */ }
  };
  // Iter143 · perfiles de vista guardados (nombre → snapshot)
  const [profiles, setProfiles] = useState(() => {
    try {
      const s = localStorage.getItem(LS_PROFILES_KEY);
      return s ? JSON.parse(s) : {};
    } catch { return {}; }
  });
  const saveProfile = (name) => {
    const trimmed = (name || "").trim().slice(0, 30);
    if (!trimmed) return;
    const snapshot = {
      visibleCols: [...visibleCols],
      colOrder:    [...colOrder],
      colWidths:   { ...colWidths },
      density,
    };
    setProfiles(prev => {
      const next = { ...prev, [trimmed]: snapshot };
      // Máx 3 perfiles: si excede, borra el más antiguo (por orden de inserción)
      const keys = Object.keys(next);
      if (keys.length > 3) {
        const oldest = keys[0];
        delete next[oldest];
      }
      try { localStorage.setItem(LS_PROFILES_KEY, JSON.stringify(next)); } catch { /* no-op */ }
      return next;
    });
  };
  const loadProfile = (name) => {
    const p = profiles[name];
    if (!p) return;
    setVisibleCols(new Set(p.visibleCols || []));
    setColOrder(p.colOrder || DEFAULT_ORDER);
    setColWidths({ ...DEFAULT_WIDTHS, ...(p.colWidths || {}) });
    setDensityAndPersist(p.density || "comfortable");
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(p.visibleCols || []));
      localStorage.setItem(LS_ORDER_KEY, JSON.stringify(p.colOrder || DEFAULT_ORDER));
      localStorage.setItem(LS_WIDTHS_KEY, JSON.stringify(p.colWidths || {}));
    } catch { /* no-op */ }
    toast.success(`Perfil '${name}' cargado`);
  };
  const deleteProfile = (name) => {
    setProfiles(prev => {
      const next = { ...prev };
      delete next[name];
      try { localStorage.setItem(LS_PROFILES_KEY, JSON.stringify(next)); } catch { /* no-op */ }
      return next;
    });
  };

  const toggleCol = (id) => {
    setVisibleCols(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      localStorage.setItem(LS_KEY, JSON.stringify([...next]));
      return next;
    });
  };
  const resetCols = () => {
    setVisibleCols(DEFAULT_COLS);
    setColOrder(DEFAULT_ORDER);
    setSortState(DEFAULT_SORT);
    localStorage.setItem(LS_KEY, JSON.stringify([...DEFAULT_COLS]));
    localStorage.setItem(LS_ORDER_KEY, JSON.stringify(DEFAULT_ORDER));
    localStorage.setItem(LS_SORT_KEY, JSON.stringify(DEFAULT_SORT));
  };
  const showAllCols = () => {
    const all = new Set(MERCADO_COLS.map(c => c.id));
    setVisibleCols(all);
    localStorage.setItem(LS_KEY, JSON.stringify([...all]));
  };
  const moveCol = (id, delta) => {
    setColOrder(prev => {
      const idx = prev.indexOf(id);
      if (idx < 0) return prev;
      const target = Math.max(0, Math.min(prev.length - 1, idx + delta));
      if (target === idx) return prev;
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      localStorage.setItem(LS_ORDER_KEY, JSON.stringify(next));
      return next;
    });
  };
  const toggleSort = (colId) => {
    setSortState(prev => {
      const next = prev.col === colId
        ? { col: colId, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { col: colId, dir: "desc" };
      localStorage.setItem(LS_SORT_KEY, JSON.stringify(next));
      return next;
    });
  };

  // iter140 · resize columnas por drag
  const startResize = (e, colId) => {
    e.preventDefault();
    e.stopPropagation();
    resizeRef.current = {
      id: colId,
      startX: e.clientX,
      startW: colWidths[colId] || DEFAULT_WIDTHS[colId] || 120,
    };
    const onMove = (ev) => {
      if (!resizeRef.current) return;
      const { id, startX, startW } = resizeRef.current;
      const w = Math.max(MIN_COL_W, Math.min(MAX_COL_W, startW + (ev.clientX - startX)));
      setColWidths(prev => ({ ...prev, [id]: w }));
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      // Persist final widths
      setColWidths(prev => {
        try { localStorage.setItem(LS_WIDTHS_KEY, JSON.stringify(prev)); } catch { /* no-op */ }
        return prev;
      });
      resizeRef.current = null;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };
  const resetWidths = () => {
    setColWidths({ ...DEFAULT_WIDTHS });
    try { localStorage.removeItem(LS_WIDTHS_KEY); } catch { /* no-op */ }
  };

  // Close col picker on outside click
  useEffect(() => {
    if (!colPickerOpen) return;
    const handler = (e) => { if (colPickerRef.current && !colPickerRef.current.contains(e.target)) setColPickerOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [colPickerOpen]);

  // activeCols: respeta el orden persistido y filtra por visibleCols
  const activeCols = useMemo(() => {
    const byId = Object.fromEntries(MERCADO_COLS.map(c => [c.id, c]));
    return colOrder.filter(id => visibleCols.has(id) && byId[id]).map(id => byId[id]);
  }, [colOrder, visibleCols]);

  // Iter234 · TDZ fix: declaramos pendingReviewItems ANTES del useMemo que lo
  // referencia (antes vivía más abajo y provocaba ReferenceError: Cannot
  // access 'pendingReviewItems' before initialization al renderizar Mercado).
  const [pendingReviewItems, setPendingReviewItems] = useState([]);
  const [pendingReviewLoading, setPendingReviewLoading] = useState(false);

  // sortedItems: aplica el sort persistido sobre data.items
  const sortedItems = useMemo(() => {
    // Iter234 · "Pendientes de revisar" usa su propio fetch (loadPendingReview,
    // fuera del límite de 500 de `data.items`) en vez de data.items/prepresLicits.
    if (viewMode === "pending_review") {
      let items = filterByDG(pendingReviewItems || []);
      if (filters.trend_up_only) {
        items = items.filter(m => {
          const last3 = (m.score_history || []).slice(-3);
          if (last3.length < 3) return false;
          return last3[0].score < last3[1].score && last3[1].score < last3[2].score;
        });
      }
      if (filters.ccaa) items = items.filter(m => (m.comunidad_autonoma || "") === filters.ccaa);
      if (filters.expediente) {
        const qx = filters.expediente.toLowerCase().trim();
        items = items.filter(m => `${m.expediente_num || ""} ${m.expediente || ""}`.toLowerCase().includes(qx));
      }
      if (filters.tipo) {
        items = items.filter(m => {
          const t = (m.tipo_contratacion || m.tipo_contrato || "").toUpperCase().trim();
          return (t === "AD" ? "ADC" : t) === filters.tipo;
        });
      }
      const { field, dir } = sortState || {};
      if (field) {
        items = [...items].sort((a, b) => {
          const av = a[field], bv = b[field];
          if (av == null && bv == null) return 0;
          if (av == null) return 1;
          if (bv == null) return -1;
          if (av < bv) return dir === "desc" ? 1 : -1;
          if (av > bv) return dir === "desc" ? -1 : 1;
          return 0;
        });
      }
      return items;
    }
    let items = data?.items || [];
    // Iter228/229 · Mergear las Licitaciones estratégicas + post-presentación
    // (PUBLICADA/POR DECIDIR/ESTUDIO/PRESENTADA/NO_PRESENTADA/SUSPENDIDA)
    // cuando el tab actual las necesita. El usuario las verá en Mercado sin
    // saltar a Licitaciones.
    const needsLicits = viewMode === "strategic" || dueSoonOnly || viewMode === "resueltas" || viewMode === "non-strategic";
    if (needsLicits && prepresLicits.length > 0) {
      // Si hay filtro por estado activo, respetarlo también en el merged set
      let mergeSet = prepresLicits;
      if (filters.estado) {
        const wanted = filters.estado.toUpperCase();
        mergeSet = mergeSet.filter(x => {
          const est = (x.estado_licitacion || "").toUpperCase();
          if (wanted === "ESTUDIO" || wanted === "EN_ESTUDIO") return est === "ESTUDIO" || est === "EN_ESTUDIO";
          return est === wanted;
        });
      }
      // Iter230 · Feedback usuario ("los filtros no funcionan"): estos
      // registros vienen de ofertas_comercial vía /prepres-licitaciones, que
      // sólo acepta direccion_general — el resto de filtros del panel
      // (fecha, cliente, provincia, plataforma, actividad) nunca se les
      // aplicaba, así que en vistas con muchos registros mergeados (p.ej.
      // "No presentadas") el filtro parecía no hacer nada. Replicamos aquí
      // los mismos filtros que el backend ya aplica a `data.items`.
      if (filters.direccion_general) {
        mergeSet = mergeSet.filter(x => (x.direccion_general || "") === filters.direccion_general);
      }
      if (filters.actividad) {
        mergeSet = mergeSet.filter(x => (x.actividad_informe_comercial || "") === filters.actividad);
      }
      if (filters.plataforma) {
        mergeSet = mergeSet.filter(x => (x.plataforma_licitacion_electronica || "") === filters.plataforma);
      }
      if (filters.cliente) {
        const q = filters.cliente.toLowerCase();
        mergeSet = mergeSet.filter(x => (x.cliente || "").toLowerCase().includes(q));
      }
      if (filters.provincia) {
        const q = filters.provincia.toLowerCase();
        mergeSet = mergeSet.filter(x => (x.provincia || "").toLowerCase().includes(q));
      }
      if (filters.fecha_from || filters.fecha_to) {
        mergeSet = mergeSet.filter(x => {
          const fp = (x.fecha_presentacion || "").slice(0, 10);
          if (filters.fecha_from && fp < filters.fecha_from) return false;
          if (filters.fecha_to && fp > filters.fecha_to) return false;
          return true;
        });
      }
      const known = new Set(items.map(x => x.id));
      // Iter235 · Fix discrepancia KPI vs lista ("KPI dice 3, la lista enseña
      // 5"): marcamos estos registros como `_fromPrepres` para que el filtro
      // de más abajo (por `filters.estado`) no les reaplique `!oferta_comercial_id`
      // — ya vienen pre-filtrados 1:1 con la definición oficial del KPI
      // (ofertas_comercial), así que no deben tratarse como mercado_opportunities.
      items = [
        ...mergeSet.filter(x => !known.has(x.id)).map(x => ({ ...x, _fromPrepres: true })),
        ...items,
      ];
    }
    // iter 134 · Filtro global por DG (Residuos/Servicios Urbanos)
    items = filterByDG(items);
    // iter 130 · filtro cliente-side "trend_up_only": últimos 3 scores monótonos crecientes
    if (filters.trend_up_only) {
      items = items.filter(m => {
        const h = m.ai_score_history || [];
        if (h.length < 3) return false;
        const last3 = h.slice(-3);
        return last3[0].score < last3[1].score && last3[1].score < last3[2].score;
      });
    }
    // iter 133 · filtro cliente-side "ccaa": comunidad autónoma exacta
    if (filters.ccaa) {
      items = items.filter(m => (m.comunidad_autonoma || "") === filters.ccaa);
    }
    // Iter166/168 · Tabs · propuestas (default con opps nuevas) / strategic /
    // non-strategic / resueltas / archivadas.
    // (Aceptadas eliminado, Descartadas eliminado — Descartar = hard delete.)
    const STRATEGIC = ["PUBLICADA", "POR_DECIDIR", "EN_ESTUDIO", "ESTUDIO"];
    const FINALES = ["PRESENTADA", "NO_PRESENTADA", "SUSPENDIDA", "ANULADA"];
    // Iter229.1 · Si el usuario tiene un filtro por estado explícito (via KPI
    // pills o botones), respetarlo como la fuente única de verdad — sea cual
    // sea el viewMode. Esto evita que "resueltas" restrinja a FINALES o que
    // "strategic" restrinja a STRATEGIC cuando el usuario ha pedido explícitamente
    // ver un estado concreto (PRESENTADA, NO_PRESENTADA, EN_ESTUDIO…).
    if (filters.estado && ["strategic", "resueltas"].includes(viewMode)) {
      const wanted = filters.estado.toUpperCase();
      // Iter235 · Fix "KPI dice 3, la lista enseña 5": estos estados son
      // "oficiales" — su única fuente de verdad es ofertas_comercial (ver
      // mercado.py `oc_by_estado`, de donde sale el número del KPI y el de
      // Agenda). Si dejamos que el filtro genérico de abajo también acepte
      // mercado_opportunities sueltas con el mismo estado_licitacion (pero
      // JAMÁS promovidas a Licitación), la lista cuela registros huérfanos
      // que el KPI nunca contó → desajuste. Para estos estados mostramos
      // EXCLUSIVAMENTE los registros `_fromPrepres` (ofertas_comercial).
      const OFFICIAL_KPI_STATES = new Set([
        "PUBLICADA", "POR_DECIDIR", "ESTUDIO", "EN_ESTUDIO", "PRESENTADA",
        "NO_PRESENTADA", "ANULADA", "SUSPENDIDA",
      ]);
      if (OFFICIAL_KPI_STATES.has(wanted)) {
        items = items.filter(m => m._fromPrepres === true);
        // David 2026-06 · "No presentadas" excluye las no estratégicas (se
        // muestran en el grupo Non-strategic) para cuadrar con la tarjeta.
        if (wanted === "NO_PRESENTADA") {
          items = items.filter(m => !isNoPresNonStrategic(m));
        }
      } else {
        items = items.filter(m => {
          const est = (m.estado_licitacion || "").toUpperCase();
          return est === wanted && !m.oferta_comercial_id && m.is_expired !== true;
        });
      }
    } else if (viewMode === "propuestas") {
      items = items.filter(m => m.estado_licitacion === "PROPUESTA_OPORTUNIDAD" && !m.oferta_comercial_id);
    } else if (viewMode === "archivadas") {
      items = items.filter(m => m.is_expired === true && !m.oferta_comercial_id);
    } else if (viewMode === "descartadas") {
      items = items.filter(m => m.estado_licitacion === "DESCARTADA");
    } else if (viewMode === "non-strategic") {
      // David 2026-06 · El backend ya devuelve EXACTAMENTE el conjunto no
      // estratégico (only_nonstrategic) → mostramos todas esas filas tal cual,
      // más las "No presentadas" no estratégicas de ofertas_comercial (prepres).
      // Así el contador (nonstrategic_count) y la lista cuadran 1:1.
      items = items.filter(m => (m._fromPrepres ? isNoPresNonStrategic(m) : true));
    } else if (viewMode === "resueltas") {
      items = items.filter(m => FINALES.includes(m.estado_licitacion));
    } else if (dueSoonOnly) {
      items = items.filter(m => !m.oferta_comercial_id && m.is_expired !== true);
    } else {
      // strategic (default sin filtro estado): PUBLICADA / POR_DECIDIR / EN_ESTUDIO / ESTUDIO
      items = items.filter(m =>
        STRATEGIC.includes(m.estado_licitacion) &&
        !m.oferta_comercial_id &&
        m.is_expired !== true,
      );
    }
    // Iter162/175/207/229 · Ocultar oportunidades vencidas por fecha en todos
    // los tabs activos EXCEPTO cuando el usuario filtra explícitamente por un
    // estado post-presentación (PRESENTADA / NO_PRESENTADA / SUSPENDIDA):
    // esos hitos son históricos por definición y esconderlos vaciaría la vista.
    const POST_PRES_ESTADO = new Set(["PRESENTADA", "NO_PRESENTADA", "SUSPENDIDA"]);
    const historicalFilter = filters.estado && POST_PRES_ESTADO.has(filters.estado.toUpperCase());
    if (hideExpired && !historicalFilter && ["propuestas", "strategic"].includes(viewMode)) {
      items = items.filter(m => {
        const f = (m.fecha_presentacion || "").slice(0, 10);
        if (!f) return true;   // sin fecha → siempre visible (para poder rellenarla)
        return f >= todayISO;
      });
    }
    // Iter164 · Filtro "vencen esta semana" (0-7 días desde hoy)
    if (dueSoonOnly) {
      items = items.filter(m => {
        const f = (m.fecha_presentacion || "").slice(0, 10);
        return f && f >= todayISO && f <= plus7ISO;
      });
    }
    // Iter170 · Filtro "Sin responsable"
    if (unassignedOnly) {
      items = items.filter(m => !(m.assigned_to || "").trim());
    }
    // Iter170 · Filtro por owner concreto (deep-link desde Admin)
    if (ownerFilter) {
      items = items.filter(m => (m.assigned_to || "").toLowerCase() === ownerFilter.toLowerCase());
    }
    // Filtro de importe por rango (M€) — total de licitación, pedido del usuario
    if (filters.importe_min > 0 || filters.importe_max > 0) {
      items = items.filter(m => {
        const mm = m.value_eur != null ? Number(m.value_eur) / 1_000_000
          : Number(m.presupuesto_licitacion_ute_mm || (m.presupuesto || 0) / 1_000_000 || 0);
        if (filters.importe_min > 0 && mm < filters.importe_min) return false;
        if (filters.importe_max > 0 && mm > filters.importe_max) return false;
        return true;
      });
    }
    // Filtro por expediente (coincidencia parcial) y por tipo de contratación
    // NC/ADC/R — pedido del usuario. El tipo sólo lo traen las licitaciones ya
    // clasificadas (source=licitaciones / ofertas_comercial); las oportunidades
    // de mercado sin clasificar se ocultan al filtrar por tipo (comportamiento
    // esperado: "quiero ver sólo las NC").
    if (filters.expediente) {
      const qx = filters.expediente.toLowerCase().trim();
      items = items.filter(m => `${m.expediente_num || ""} ${m.expediente || ""}`.toLowerCase().includes(qx));
    }
    if (filters.tipo) {
      items = items.filter(m => {
        const t = (m.tipo_contratacion || m.tipo_contrato || "").toUpperCase().trim();
        return (t === "AD" ? "ADC" : t) === filters.tipo;
      });
    }
    const accessor = SORT_ACCESSORS[sortState.col];
    if (!accessor) return items;
    const mul = sortState.dir === "asc" ? 1 : -1;
    return [...items].sort((a, b) => {
      const va = accessor(a);
      const vb = accessor(b);
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * mul;
      return String(va).localeCompare(String(vb), "es", { numeric: true, sensitivity: "base" }) * mul;
    });
  }, [data, prepresLicits, pendingReviewItems, sortState, filters.trend_up_only, filters.ccaa, filters.estado, filters.direccion_general, filters.actividad, filters.plataforma, filters.cliente, filters.provincia, filters.expediente, filters.tipo, filters.fecha_from, filters.fecha_to, filters.importe_min, filters.importe_max, filterByDG, viewMode, hideExpired, dueSoonOnly, unassignedOnly, ownerFilter, todayISO, plus7ISO]);

  // Iter179.5 · `dgKpis` deshabilitado. Antes recomputábamos KPIs client-side
  // sobre `data.items` (paginado a 500) para respetar el pill DG, pero eso
  // ignoraba la contribución de `ofertas_comercial` (PRESENTADA/NO_PRESENTADA
  // ~140 filas) → al activar "Servicios Urbanos" los KPIs desaparecían.
  // Ahora el backend ya aplica el filtro DG a AMBAS colecciones (`qry` va con
  // `direccion_general=SERVICIOS` y `n_ofertas` respeta el mismo filtro), así
  // que confiamos siempre en `data.kpis`.
  const dgKpis = null;

  // Iter142 · Métricas para DGCompareCard: Residuos vs Servicios Urbanos
  const compareMetrics = useMemo(() => {
    const items = data?.items || [];
    const tra = items.filter(i => i.direccion_general === "TRATAMIENTO");
    const ser = items.filter(i => i.direccion_general === "SERVICIOS");
    const sum = (arr, k) => arr.reduce((s, x) => s + (Number(x[k]) || 0), 0);
    const cnt = (arr, k, v) => arr.filter(x => x[k] === v).length;
    const scored = (arr) => arr.filter(x => typeof x.ai_win_score === "number");
    const avgScore = (arr) => {
      const s = scored(arr);
      if (!s.length) return 0;
      return Math.round(s.reduce((t, x) => t + x.ai_win_score, 0) / s.length);
    };
    return [
      { key: "total", label: "Total oportunidades", tra: tra.length, ser: ser.length },
      { key: "publicadas", label: "Publicadas activas", tra: cnt(tra, "estado_licitacion", "PUBLICADA"), ser: cnt(ser, "estado_licitacion", "PUBLICADA") },
      { key: "budget",     label: "Presupuesto total (M€)",
        tra: sum(tra, "presupuesto") / 1_000_000,
        ser: sum(ser, "presupuesto") / 1_000_000,
        formatter: v => `${(v || 0).toFixed(2)} M€` },
      { key: "win", label: "Win Score medio (Pando)",
        tra: avgScore(tra), ser: avgScore(ser),
        formatter: v => `${v || 0}%`,
        hint: "Media sobre oportunidades con score Pando" },
    ];
  }, [data]);


  const ccaaOptions = useMemo(() => {
    const set = new Set();
    (data?.items || []).forEach(m => { if (m.comunidad_autonoma) set.add(m.comunidad_autonoma); });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "es"));
  }, [data]);


  const qs = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (k === "trend_up_only") return;   // cliente-side, no va al backend
      if (k === "ccaa") return;            // cliente-side (comunidad_autonoma)
      if (v) p.set(k, v);
    });
    // Iter140 · el pill DG global también viaja al backend para paginación
    // honesta. Sin esto sólo veíamos los 500 más recientes y el pill filtraba
    // client-side sobre esa página.
    if (dg && !filters.direccion_general) p.set("direccion_general", dg);
    // Iter179.1 · Foco por comercial
    if (mineOnly && user?.email) p.set("mine", "1");
    // Iter180 · market_type ADDRESSABLE / NON_STRATEGIC
    if (marketTypeFilter) p.set("market_type", marketTypeFilter);
    return p.toString();
  }, [filters, dg, mineOnly, user?.email, marketTypeFilter]);

  // Iter228 · Fetch adicional de Licitaciones estratégicas pre-presentación
  // (PUBLICADA / POR DECIDIR / ESTUDIO) para poder mostrarlas juntas en el
  // tab Mercado / Strategic — el usuario espera verlas aquí, no tener que
  // saltar a la pestaña Licitaciones. (State declarado arriba para evitar TDZ.)
  const loadSeq = useRef(0);
  const load = async () => {
    const mySeq = ++loadSeq.current;
    // David 2026-06 · En la pestaña Non-strategic pedimos TODO el conjunto no
    // estratégico (sin tope de 500) para que el contador y la lista cuadren 1:1.
    const parts = [qs];
    if (viewMode === "non-strategic") parts.push("only_nonstrategic=true", "limit=5000");
    const query = parts.filter(Boolean).join("&");
    const r = await axios.get(`${API}/mercado${query ? `?${query}` : ""}`);
    // SU03 (H02/H03) · descarta la respuesta si YA hay una petición más
    // reciente en vuelo (p.ej. el usuario siguió escribiendo) — evita que
    // una respuesta lenta y desordenada sobrescriba el resultado correcto
    // del término de búsqueda actual.
    if (mySeq === loadSeq.current) setData(r.data);
  };
  useEffect(() => { load(); }, [qs, viewMode]); // eslint-disable-line

  const loadPrepresLicits = async () => {
    try {
      const params = new URLSearchParams();
      if (dg && dg !== "all") params.append("direccion_general", dg);
      const r = await axios.get(`${API}/mercado/prepres-licitaciones${params.toString() ? `?${params}` : ""}`);
      setPrepresLicits(r.data || []);
    } catch { /* endpoint opcional, no bloqueante */ }
  };
  useEffect(() => { loadPrepresLicits(); }, [dg]);  // eslint-disable-line

  // Iter234 · Feedback usuario: "las oportunidades PLACSP nuevas van a una
  // pestaña Pendientes de revisar separada de los KPIs oficiales, y sólo al
  // aprobarlas pasan a sumar". `data.items` viene con `limit` (500), y hay
  // >1500 pendientes sin revisar — así que esta pestaña usa un fetch propio
  // con `only_pending_review=true` (respeta el resto de filtros vía `qs`).
  const loadPendingReview = async () => {
    setPendingReviewLoading(true);
    try {
      const sep = qs ? "&" : "";
      const r = await axios.get(`${API}/mercado?only_pending_review=true&limit=1000${sep}${qs}`);
      setPendingReviewItems(r.data?.items || []);
    } catch { toast.error("No se pudieron cargar las pendientes de revisar"); }
    finally { setPendingReviewLoading(false); }
  };
  useEffect(() => {
    if (viewMode === "pending_review") loadPendingReview();
  }, [viewMode, qs]);  // eslint-disable-line
  useEffect(() => {
    axios.get(`${API}/mercado/catalog`).then(r => setCatalog(r.data));
  }, []);
  // Iter165 · Lista de usuarios asignables (comerciales). Reutiliza el
  // endpoint /horas/users que ya devuelve users activos + colaboradores.
  const [assignableUsers, setAssignableUsers] = useState([]);
  useEffect(() => {
    axios.get(`${API}/horas/users`)
      .then(r => setAssignableUsers((r.data || []).filter(u => u.email)))
      .catch(() => setAssignableUsers([]));
  }, []);
  const assign = async (mid, email) => {
    try {
      await axios.patch(`${API}/mercado/${mid}/assign`, { assigned_to: email });
      toast.success(email ? `Asignada a ${email.split("@")[0]}` : "Desasignada");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo asignar");
    }
  };

  // Iter220 · Descartar con modal de feedback (chips motivos + comentario libre).
  // El motivo va al audit log para que el comité detecte patrones y sugiera
  // nuevos CPVs a excluir. El modal es OPCIONAL: usuario puede cerrarlo y
  // descartar sin motivo (compatibilidad con flujo anterior).
  const [descarteModal, setDescarteModal] = useState(null); // opp | null

  const descartar = (mid) => {
    // Buscamos la opp para pasarla al modal (data.items = lista paginada)
    const opp = (data?.items || []).find(o => o.id === mid);
    if (!opp) {
      // Fallback: descartar sin modal si no la encontramos
      if (!window.confirm("¿Marcar como DESCARTADA?")) return;
      axios.patch(`${API}/mercado/${mid}/estado`, { estado: "DESCARTADA" })
        .then(() => { toast.success("Oportunidad descartada"); load(); })
        .catch(e => toast.error(e.response?.data?.detail || "No se pudo descartar"));
      return;
    }
    setDescarteModal(opp);
  };

  const confirmarDescarte = async ({ motivo_descarte, comment }) => {
    if (!descarteModal) return;
    const mid = descarteModal.id;
    setDescarteModal(null);
    try {
      await axios.patch(`${API}/mercado/${mid}/estado`, {
        estado: "DESCARTADA",
        motivo_descarte: motivo_descarte || [],
        comment: comment || "",
      });
      toast.success("Oportunidad descartada");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo descartar");
    }
  };

  // Iter169 · Modal de motivos (sustituye window.prompt)
  const [motivoModal, setMotivoModal] = useState(null); // {mid, tipo:'NE'|'NP', current}
  const openMotivoModal = (mid, tipo, current = []) => {
    setMotivoModal({ mid, tipo, current });
  };
  const submitMotivo = async (motivos) => {
    if (!motivoModal) return;
    const { mid, tipo } = motivoModal;
    setMotivoModal(null);
    try {
      const body = tipo === "NE"
        ? { estado: "NO_ESTRATEGICA", motivo_no_estrategica: motivos }
        : { estado: "NO_PRESENTADA", motivo_no_presentacion: motivos[0] || "" };
      const r = await axios.patch(`${API}/mercado/${mid}/estado`, body);
      const linked = r.data?.oferta_comercial_id;
      if (linked && tipo === "NP") {
        toast.success(`Estado → No presentada · vinculada en Licitaciones`);
      } else {
        toast.success(`Estado → ${tipo === "NE" ? "No estratégica" : "No presentada"}`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo cambiar el estado");
    }
  };

  // Iter166 · Cambio de estado con validación backend (transiciones permitidas)
  const changeEstado = async (mid, nuevo) => {
    if (nuevo === "NO_ESTRATEGICA") {
      openMotivoModal(mid, "NE", []);
      return;
    }
    if (nuevo === "NO_PRESENTADA") {
      openMotivoModal(mid, "NP", []);
      return;
    }
    if (nuevo === "DESCARTADA" && !window.confirm("¿Marcar como DESCARTADA? Saldrá de los análisis Strategic y Non-strategic.")) {
      return;
    }
    try {
      const r = await axios.patch(`${API}/mercado/${mid}/estado`, { estado: nuevo });
      const linked = r.data?.oferta_comercial_id;
      if (linked && ESTADOS_FINALES.includes(nuevo)) {
        toast.success(`Estado → ${ESTADO_LABEL[nuevo] || nuevo} · vinculada en Licitaciones`);
      } else {
        toast.success(`Estado → ${ESTADO_LABEL[nuevo] || nuevo}`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo cambiar el estado");
    }
  };

  const resetFilters = () => {
    setQInput("");
    setFilters({
      q: "", estado: "", direccion_general: "", actividad: "",
      provincia: "", ccaa: "", plataforma: "", cliente: "", expediente: "", tipo: "", fecha_from: "", fecha_to: "",
      year: String(new Date().getFullYear()),
      importe_min: 0, importe_max: 0, trend_up_only: false,
    });
  };
  // `year` no cuenta como "filtro activo" (tiene su propio selector siempre visible
  // y por defecto es el año en curso).
  const activeFilters = Object.entries(filters).filter(([k, v]) => k !== "year" && Boolean(v)).length;

  // CRM-R05 · Aplicar una vista guardada (merge sobre el estado por defecto).
  const applySavedView = (f) => {
    const base = { q: "", estado: "", direccion_general: "", actividad: "", provincia: "",
      ccaa: "", plataforma: "", cliente: "", expediente: "", tipo: "", fecha_from: "", fecha_to: "",
      importe_min: 0, importe_max: 0, trend_up_only: false };
    const merged = { ...base, ...(f || {}) };
    setFilters(merged);
    setQInput(merged.q || "");
  };
  const FILTER_CHIP_LABELS = { estado: "Estado", direccion_general: "DG", actividad: "Actividad",
    provincia: "Provincia", ccaa: "CCAA", plataforma: "Plataforma", cliente: "Cliente",
    expediente: "Expediente", tipo: "Tipo", fecha_from: "Desde", fecha_to: "Hasta" };
  const activeChips = useMemo(() => {
    const chips = [];
    Object.entries(FILTER_CHIP_LABELS).forEach(([k, lbl]) => {
      if (filters[k]) chips.push({ key: k, label: `${lbl}: ${filters[k]}`, onClear: () => setFilters(f => ({ ...f, [k]: "" })) });
    });
    if (filters.importe_min || filters.importe_max) chips.push({ key: "importe", label: `Importe: ${filters.importe_min || 0}-${filters.importe_max || "∞"} M€`, onClear: () => setFilters(f => ({ ...f, importe_min: 0, importe_max: 0 })) });
    if (filters.trend_up_only) chips.push({ key: "trend", label: "Tendencia positiva", onClear: () => setFilters(f => ({ ...f, trend_up_only: false })) });
    return chips;
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const del = async (id) => {
    if (!window.confirm("¿Eliminar oportunidad?")) return;
    await axios.delete(`${API}/mercado/${id}`);
    toast.success("Eliminada");
    load();
  };

  const pasarAEstudio = async (id) => {
    if (!window.confirm("Pasar a ESTUDIO creará una entrada en Licitaciones. ¿Continuar?")) return;
    try {
      const r = await axios.post(`${API}/mercado/${id}/pasar-a-estudio`);
      toast.success(`Licitación creada: ${r.data.expediente || r.data.licitacion_id}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al pasar a estudio");
    }
  };

  // Iter158 · Sugerencias Pando en lote sobre EN_CARTERA
  const [batchPando, setBatchPando] = useState(false);
  const runBatchPando = async () => {
    if (!window.confirm("Pando analizará las 20 oportunidades DETECTADAS sin sugerencia previa (ordenadas por Win Score). Puede tardar 30-60 s.\n\n¿Continuar?")) return;
    setBatchPando(true);
    try {
      const r = await axios.post(`${API}/mercado/batch-suggestions?limit=20&only_missing=true`);
      const { processed, ai, heuristic, failed, candidates } = r.data || {};
      if (candidates === 0) {
        toast.info("No hay oportunidades DETECTADAS sin sugerencia Pando.");
      } else {
        toast.success(`Pando procesó ${processed}/${candidates} · Pando: ${ai} · Heurística: ${heuristic}${failed ? ` · Fallos: ${failed}` : ""}`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error en Pando batch");
    } finally { setBatchPando(false); }
  };

  // Iter156 · Sync con rango configurable (1, 2, 7 o 30 días). Default 7.
  const [syncDaysBack, setSyncDaysBack] = useState(() => {
    try { return parseInt(localStorage.getItem("mercado_sync_days") || "7", 10) || 7; }
    catch { return 7; }
  });
  useEffect(() => { try { localStorage.setItem("mercado_sync_days", String(syncDaysBack)); } catch { /* localStorage no disponible */ } }, [syncDaysBack]);

  const syncNow = async (daysOverride) => {
    setSyncing(true);
    const days = daysOverride ?? syncDaysBack;
    try {
      await axios.post(`${API}/mercado/sync-now?days=${days}`);
      const label = days === 1 ? "1 día" : days === 2 ? "2 días" : days === 7 ? "1 semana" : `${days} días`;
      toast.success(`Sincronización iniciada (últimos ${label}). Las nuevas oportunidades aparecerán 'DETECTADAS' en 1-2 min.`);
      // Refrescar en 20s y 60s
      setTimeout(load, 20000);
      setTimeout(load, 60000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al sincronizar");
    } finally {
      setSyncing(false);
    }
  };

  const exportExcel = async () => {
    try {
      // iter135 · aplicar filtro DG global al export
      const params = new URLSearchParams(qs);
      if (dg) params.set("direccion_general", dg);
      if (filters.ccaa) params.set("comunidad_autonoma", filters.ccaa);
      const query = params.toString();
      const r = await axios.get(`${API}/mercado/export.xlsx${query ? `?${query}` : ""}`, {
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      const ts = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, "");
      const suffix = dg ? `_${DG_LABELS[dg].replace(/\s/g, "")}` : "";
      a.download = `mercado_${ts}${suffix}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success(`Excel descargado${dg ? " · " + DG_LABELS[dg] : ""} (${sortedItems.length} filas)`);
    } catch (e) {
      toast.error("Error al exportar");
    }
  };

  // Iter163 · Export de vencidas del último trimestre (archivo comercial).
  const exportExpired = async () => {
    try {
      const r = await axios.get(`${API}/mercado/export.xlsx?expired_last_months=3`, {
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      const ts = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, "");
      a.download = `mercado_vencidas_ultimo_trimestre_${ts}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("Excel de vencidas del último trimestre descargado");
    } catch (e) {
      toast.error("Error al exportar vencidas");
    }
  };

  const scoreWithAI = async (item) => {
    setScoring(item.id);
    try {
      // Patrón async: HTTP 202 + polling /ai-jobs/{id}
      const r = await axios.post(`${API}/mercado/${item.id}/score-ia`);
      const jobId = r.data.job_id;
      if (!jobId) throw new Error("Sin job_id en respuesta");
      // Polling cada 2s hasta done o failed (timeout 5 min)
      let waited = 0;
      while (waited < 300) {
        await new Promise(res => setTimeout(res, 2000));
        waited += 2;
        const s = await axios.get(`${API}/ai-jobs/${jobId}`);
        if (s.data.status === "done") {
          setScoreResult({ item, result: s.data.result });
          load();
          return;
        }
        if (s.data.status === "failed") {
          toast.error("Error Pando: " + (s.data.error || "desconocido"));
          return;
        }
      }
      toast.error("El análisis tardó más de 5 minutos");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error del análisis Pando");
    } finally {
      setScoring(null);
    }
  };

  const batchScoreAI = async () => {
    if (!window.confirm("Scoreará automáticamente hasta 20 oportunidades pendientes con Claude (tarda ~60-120s). ¿Continuar?")) return;
    setBatchScoring(true);
    try {
      const r = await axios.post(`${API}/mercado/batch-score-ia?limit=20`);
      if (r.data.status === "empty") {
        toast.info(r.data.message);
      } else {
        toast.success(`${r.data.queued} oportunidades encoladas · refrescando en 60s`);
        setTimeout(load, 30000);
        setTimeout(load, 60000);
        setTimeout(load, 120000);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error batch score");
    } finally {
      setBatchScoring(false);
    }
  };

  if (!data) return <div className="text-xs text-gray-500"><Loader2 className="animate-spin inline mr-1" size={12}/>Cargando MERCADO…</div>;

  // ── renderCell ────────────────────────────────────────────────────────────
  const reloadFn = load;
  const renderCell = (colId, m) => {
    switch (colId) {
      case "titulo": {
        const t = m.titulo_ia || m.objeto_contrato || "—";
        const hasIA = !!m.titulo_ia;
        return (
          <div title={t}>
            <div className="font-semibold text-slate-900 text-[11px] leading-tight line-clamp-2">
              {t}
            </div>
            {hasIA && (
              <span className="text-[8px] font-bold bg-violet-100 text-violet-700 px-1 py-0.5 rounded mt-0.5 inline-block" title="Título inferido por Pando a partir del pliego">Pando</span>
            )}
          </div>
        );
      }
      case "link":
        return m.url_externa ? (
          <a
            href={m.url_externa}
            target="_blank"
            rel="noreferrer"
            data-testid={`mercado-link-${m.id}`}
            title="Abrir ficha oficial en la plataforma"
            className="inline-flex items-center gap-1 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 rounded-md px-1.5 py-0.5 text-[10px] font-semibold">
            <ExternalLink size={11}/> Abrir
          </a>
        ) : <span className="text-slate-300 text-[10px]">—</span>;
      case "win_score":
        return <WinScoreCell m={m} onScore={reloadFn}/>;
      case "enrich_timeline":
        return <EnrichmentTimeline m={m}/>;
      case "estado": {
        // Iter166/168 · En Propuestas mostramos 5 botones de decisión rápida.
        // En Strategic un selector con transiciones válidas. En el resto badge.
        const est = m.estado_licitacion || "PUBLICADA";
        const badge = (
          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${ESTADO_COLOR[est] || "bg-gray-100"}`}>
            {(ESTADO_LABEL[est] || est.replace("_", " "))}
          </span>
        );
        if (viewMode === "propuestas" && est === "PROPUESTA_OPORTUNIDAD") {
          const suggested = m.suggested_estado;
          const btn = "text-[9px] font-semibold px-1.5 py-0.5 rounded border transition";
          return (
            <div className="flex flex-wrap gap-1" data-testid={`mercado-decidir-${m.id}`}>
              <button className={`${btn} border-blue-300 text-blue-700 hover:bg-blue-50 ${suggested === "PUBLICADA" ? "ring-1 ring-blue-400" : ""}`}
                      onClick={() => changeEstado(m.id, "PUBLICADA")} title="Publicada">P</button>
              <button className={`${btn} border-amber-300 text-amber-700 hover:bg-amber-50`}
                      onClick={() => changeEstado(m.id, "POR_DECIDIR")} title="Por decidir">PD</button>
              <button className={`${btn} border-indigo-300 text-indigo-700 hover:bg-indigo-50`}
                      onClick={() => changeEstado(m.id, "EN_ESTUDIO")} title="En estudio">EE</button>
              <button className={`${btn} border-zinc-300 text-zinc-700 hover:bg-zinc-50 ${suggested === "NO_ESTRATEGICA" ? "ring-1 ring-zinc-400" : ""}`}
                      onClick={() => changeEstado(m.id, "NO_ESTRATEGICA")} title="No estratégica">NE</button>
              <button className={`${btn} border-red-300 text-red-600 hover:bg-red-50`}
                      onClick={() => descartar(m.id)} title="Descartar (borrar)">✕</button>
            </div>
          );
        }
        if (viewMode === "archivadas" || m.oferta_comercial_id || m.source === "licitaciones") {
          return (
            <div className="flex items-center gap-1">
              {badge}
              {m.source === "licitaciones" && (
                <a
                  href={`/ofertas-comercial?tab=licitaciones&highlight=${m.origin_licitacion_id || m.id}`}
                  onClick={e => e.stopPropagation()}
                  title="Registro ya presente en Licitaciones · abrir para editar"
                  className="text-[8px] font-semibold px-1 py-0.5 rounded bg-blue-100 text-blue-700 border border-blue-200 hover:bg-blue-200"
                  data-testid={`mercado-row-source-licit-${m.origin_licitacion_id || m.id}`}>
                  Licit.
                </a>
              )}
            </div>
          );
        }
        const allowed = ALLOWED_TRANSITIONS[est] || [];
        const options = Array.from(new Set([est, ...allowed]));
        return (
          <select
            className={`text-[9px] font-bold border rounded px-1 py-0.5 cursor-pointer ${ESTADO_COLOR[est] || "bg-gray-100"}`}
            value={est}
            onChange={(e) => changeEstado(m.id, e.target.value)}
            data-testid={`mercado-estado-${m.id}`}
            title={`Estado actual: ${ESTADO_LABEL[est] || est}`}
          >
            {options.map(o => (
              <option key={o} value={o}>{ESTADO_LABEL[o] || o.replace("_"," ")}</option>
            ))}
          </select>
        );
      }
      case "expediente":
        return <span className="font-mono text-[9px]" title={m.expediente}>{(m.expediente_num || m.expediente || "").slice(0, 40) || "—"}</span>;
      case "pais":
        return m.pais || "España";
      case "provincia":
        return m.provincia || "—";
      case "ccaa":
        return m.comunidad_autonoma
          ? <span className="text-[9px] bg-blue-50 text-blue-700 px-1 py-0.5 rounded font-semibold whitespace-nowrap">{m.comunidad_autonoma}</span>
          : "—";
      case "plataforma":
        return <span className="text-[9px] bg-slate-100 text-slate-700 px-1 py-0.5 rounded font-mono">{m.plataforma_licitacion_electronica || "—"}</span>;
      case "cliente":
        return <span className="font-semibold" title={m.cliente}>{m.cliente || "—"}</span>;
      case "objeto":
        return m.url_externa
          ? <a href={m.url_externa} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-0.5" title={m.objeto_contrato}>{m.objeto_contrato?.slice(0,80)}<ExternalLink size={9}/></a>
          : <span title={m.objeto_contrato}>{m.objeto_contrato || "—"}</span>;
      case "cpv":
        return <span className="font-mono text-[9px]">{m.cpv || "—"}</span>;
      case "dg":
        return m.direccion_general ? <span className="text-[9px] bg-green-50 text-green-700 px-1 py-0.5 rounded font-semibold">{m.direccion_general}</span> : "—";
      case "actividad":
        return m.actividad_informe_comercial ? <span className="text-[9px] bg-purple-50 text-purple-700 px-1 py-0.5 rounded font-semibold">{m.actividad_informe_comercial}</span> : "—";
      case "dir_zona":       return m.direccion_zona || "—";
      case "tipo_contrato":  return m.tipo_contrato || "—";
      case "tipo_cliente":   return m.tipo_cliente || "—";
      case "procedimiento":  return m.procedimiento || "—";
      case "presupuesto":    return <span className="font-mono">{fmtEUR(m.presupuesto)}</span>;
      case "pres_ute_mm":    return m.presupuesto_licitacion_ute_mm != null ? <span className="font-mono">{m.presupuesto_licitacion_ute_mm} M€</span> : "—";
      case "pres_anual_ute": return <span className="font-mono">{fmtEUR(m.presupuesto_anual_con_ute)}</span>;
      case "anios":          return m.duracion_anios ?? "—";
      case "prorroga":       return m.prorroga ?? "—";
      case "f_presentacion": return <span className="whitespace-nowrap">{fmtFechaOferta(m.fecha_presentacion)}</span>;
      case "limite_preg":    return <span className="whitespace-nowrap">{m.limite_preguntas || "—"}</span>;
      case "emp_g_valoriza": return m.empresa_g_valoriza || "—";
      case "pct_ute":        return m.porcentaje_empresa_g_valoriza != null ? `${m.porcentaje_empresa_g_valoriza}%` : "—";
      case "emp_oferta":     return m.empresa_oferta || "—";
      case "incumbent":
        return <IncumbentWinnerCell m={m} field="incumbent" onSaved={load} />;
      case "winner":
        return <IncumbentWinnerCell m={m} field="winner" onSaved={load} />;
      case "ia":
        return m.ai_recommendation ? (
          <div className="inline-flex items-center gap-1">
          <button onClick={() => setScoreResult({ item: m, result: { recommendation: m.ai_recommendation, score: m.ai_score, confidence: m.ai_confidence, summary: m.ai_summary, strengths: m.ai_strengths, risks: m.ai_risks, suggested_strategy: m.ai_strategy }})}
            className={`text-[9px] font-bold px-1.5 py-0.5 rounded inline-flex items-center gap-0.5 ${m.ai_recommendation === "GO" ? "bg-green-100 text-green-700" : m.ai_recommendation === "NO-GO" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}
            title={`Pando · ${m.ai_recommendation} · score ${m.ai_score}${m.ai_confidence != null ? ` · confianza ${m.ai_confidence}%` : ""}`}>
            {m.ai_recommendation === "GO" ? <ThumbsUp size={8}/> : m.ai_recommendation === "NO-GO" ? <ThumbsDown size={8}/> : <HelpCircle size={8}/>}
            {m.ai_recommendation} · {m.ai_score}
            {m.ai_confidence != null && (
              <span
                data-testid={`mercado-ia-conf-${m.id}`}
                className={`ml-1 px-1 py-0.5 rounded text-[8px] font-bold ${
                  m.ai_confidence >= 80 ? "bg-white/70 text-green-800" :
                  m.ai_confidence >= 60 ? "bg-white/70 text-amber-700" :
                                          "bg-white/70 text-rose-700"
                }`}>
                {m.ai_confidence}%
              </span>
            )}
          </button>
          {/* iter 129 · inline mini sparkline (12×40px) — sólo si ≥2 puntos */}
          {(m.ai_score_history && m.ai_score_history.length >= 2) && (
            <MiniSparkline
              data={m.ai_score_history}
              onClick={() => setScoreResult({ item: m, result: { recommendation: m.ai_recommendation, score: m.ai_score, confidence: m.ai_confidence, summary: m.ai_summary, strengths: m.ai_strengths, risks: m.ai_risks, suggested_strategy: m.ai_strategy }})}
              testId={`mercado-sparkline-${m.id}`}
            />
          )}
          </div>
        ) : (
          <button onClick={() => scoreWithAI(m)} disabled={scoring === m.id}
            className="text-[9px] text-indigo-600 hover:bg-indigo-50 px-1.5 py-0.5 rounded inline-flex items-center gap-0.5 disabled:opacity-50"
            data-testid={`mercado-score-${m.id}`} title="Analizar con Pando (GO/NO-GO)">
            {scoring === m.id ? <Loader2 size={10} className="animate-spin"/> : <Sparkles size={9}/>}
            {scoring === m.id ? "..." : "Score"}
          </button>
        );
      case "motivo_no":      return <span className="text-[10px]">{m.motivo_no_presentacion || "—"}</span>;
      case "assigned_to": {
        // Iter165/166 · Selector inline de responsable comercial (solo tab strategic).
        // Aceptadas/Archivadas/Cerradas/Descartadas/Non-strategic: lectura únicamente.
        const current = (m.assigned_to || "").toLowerCase();
        if (viewMode !== "strategic") {
          return current
            ? <span className="text-[11px] font-medium text-emerald-700" data-testid={`mercado-owner-ro-${m.id}`}>{current.split("@")[0]}</span>
            : <span className="text-[10px] text-gray-400">—</span>;
        }
        return (
          <select
            className="text-[10px] border rounded px-1 py-0.5 bg-white hover:border-emerald-400 focus:outline-none focus:ring-1 focus:ring-emerald-300 max-w-[140px]"
            value={current}
            onChange={(e) => assign(m.id, e.target.value)}
            data-testid={`mercado-assign-${m.id}`}
            title={current ? `Asignada a ${current}` : "Sin asignar"}
          >
            <option value="">— Sin asignar —</option>
            {assignableUsers.map((u) => (
              <option key={u.email} value={u.email.toLowerCase()}>
                {u.name || u.email.split("@")[0]}
              </option>
            ))}
          </select>
        );
      }
      default:               return "—";
    }
  };

  return (
    <div className="space-y-4" data-testid="mercado-view">
      {zonaGlobal && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800" data-testid="mercado-zona-aviso">
          <Info size={14} className="mt-0.5 flex-shrink-0 text-amber-600" />
          <span>El <strong>filtro de zona</strong> no aplica a Mercado: las detecciones pre-tender no tienen zona comercial asignada. Se muestran todas las oportunidades del barrido.</span>
        </div>
      )}
      {/* Header con KPIs */}
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-4">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div>
            <h2 className="text-base font-bold text-emerald-900 flex items-center gap-2">
              <ShoppingCart size={16}/> MERCADO · Watchlist de licitaciones
            </h2>
            <p className="text-xs text-emerald-700 mt-0.5">
              Barrido diario de PLACE · GENCAT · VORTAL filtrado por {catalog?.cpv_count || 0} códigos CPV
            </p>
            <div className="mt-2">
              <DGFilter value={dg} onChange={setDg} size="sm" testidPrefix="mercado-dg-filter"/>
            </div>
          </div>
          <div className="flex gap-2">
            <LimboBadge onActioned={() => { load(); loadPrepresLicits(); }}/>
            {/* Iter233 · Feedback usuario: "duplicados cross-source, el botón
                que salga solo en mercado" — antes vivía en Licitaciones. */}
            <CrossSourceDupesBadge/>
            {/* Iter185.1 · Botón físico "Nueva oferta" en Mercado. Abre un modal
                picker que lista oportunidades sin oferta comercial vinculada
                (buscables), para escoger la de origen y luego el flujo
                AcceptFromMercadoModal habitual. */}
            <button
              onClick={() => setShowNewOfferPicker(true)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1 shadow-sm"
              data-testid="mercado-new-oferta-btn"
              title="Crea una nueva oferta comercial escogiendo la oportunidad de Mercado origen">
              <Plus size={12}/> Nueva oferta
            </button>
            <button
              onClick={() => setShowMasterImport(true)}
              className="bg-[#007934] hover:bg-[#006027] text-white text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1 shadow-sm"
              data-testid="mercado-import-excel-btn"
              title="Importar Excel maestro Cierre CONTRATACIÓN">
              <Upload size={12}/> Importar Excel
            </button>
            <button
              onClick={() => setShowHistImport(true)}
              className="bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1 shadow-sm"
              data-testid="mercado-import-anio-anterior-btn"
              title="Importar el Excel de un año anterior (2025 y anteriores) para las comparativas — se guarda aparte, no pisa el dato vivo">
              <Upload size={12}/> Importar año anterior
            </button>
            <button
              onClick={() => setShowPlacspSync(true)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1 shadow-sm"
              data-testid="mercado-sync-placsp-btn"
              title="Sincronizar con PLACSP (Estado) · rango + solo abiertas">
              <Globe size={12}/> Sync PLACSP
            </button>
            <button
              onClick={batchScoreAI}
              disabled={batchScoring}
              className="bg-indigo-100 hover:bg-indigo-200 text-indigo-700 text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1 disabled:opacity-50"
              data-testid="mercado-batch-score-btn"
              title="Score Pando a 20 oportunidades pendientes (background)">
              {batchScoring ? <Loader2 size={12} className="animate-spin"/> : <Sparkles size={12}/>}
              Batch Score Pando
            </button>
            <button
              onClick={runBatchPando}
              disabled={batchPando}
              className="bg-violet-100 hover:bg-violet-200 text-violet-800 text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1 disabled:opacity-50"
              data-testid="mercado-batch-pando-btn"
              title="Sugerencias Pando en lote sobre las 20 DETECTADAS con mayor Win Score">
              {batchPando ? <Loader2 size={12} className="animate-spin"/> : <Sparkles size={12}/>}
              Batch Pando
            </button>
            {/* Column picker */}
            <div className="relative" ref={colPickerRef}>
              <button
                onClick={() => setColPickerOpen(o => !o)}
                className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1"
                data-testid="mercado-col-picker-btn"
                title="Mostrar/ocultar columnas">
                <SlidersHorizontal size={12}/> Columnas ({visibleCols.size})
              </button>
              {colPickerOpen && (
                <div className="absolute right-0 top-8 z-50 w-64 bg-white border border-gray-200 rounded-xl shadow-xl p-3" data-testid="mercado-col-picker-panel">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-bold text-gray-700 uppercase tracking-wider">Columnas visibles</span>
                    <div className="flex gap-1">
                      <button onClick={showAllCols} className="text-[10px] text-blue-600 hover:underline">Todas</button>
                      <span className="text-gray-300">·</span>
                      <button onClick={resetCols} className="text-[10px] text-gray-500 hover:underline">Reset</button>
                      <span className="text-gray-300">·</span>
                      <button onClick={resetWidths} data-testid="mercado-reset-widths" className="text-[10px] text-emerald-700 hover:underline">Anchos</button>
                    </div>
                  </div>

                  {/* Iter143 · Densidad */}
                  <div className="mb-2 pb-2 border-b border-gray-100" data-testid="mercado-density-picker">
                    <div className="text-[10px] font-bold text-gray-700 uppercase tracking-wider mb-1">Densidad</div>
                    <div className="flex gap-1">
                      {DENSITY_OPTIONS.map(opt => (
                        <button
                          key={opt}
                          onClick={() => setDensityAndPersist(opt)}
                          data-testid={`mercado-density-${opt}`}
                          className={`flex-1 text-[10px] font-semibold rounded px-2 py-1 border transition ${
                            density === opt
                              ? "bg-slate-900 text-white border-slate-900"
                              : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                          }`}>
                          {DENSITY_LABEL[opt]}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Iter143 · Perfiles de vista */}
                  <div className="mb-2 pb-2 border-b border-gray-100" data-testid="mercado-view-profiles">
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-[10px] font-bold text-gray-700 uppercase tracking-wider">Perfiles</div>
                      <button
                        onClick={() => {
                          const n = window.prompt("Nombre del perfil (máx 30 chars). Máx 3 perfiles guardados:", "");
                          if (n) saveProfile(n);
                        }}
                        data-testid="mercado-save-profile-btn"
                        className="text-[10px] text-emerald-700 hover:underline">
                        Guardar actual
                      </button>
                    </div>
                    {Object.keys(profiles).length === 0 ? (
                      <div className="text-[10px] text-slate-400 italic">Sin perfiles guardados</div>
                    ) : (
                      <div className="space-y-1">
                        {Object.entries(profiles).map(([name, snap]) => (
                          <div key={name} className="flex items-center justify-between text-[10px] bg-slate-50 rounded px-1.5 py-0.5">
                            <button
                              onClick={() => loadProfile(name)}
                              data-testid={`mercado-load-profile-${name}`}
                              className="text-slate-700 hover:text-indigo-700 font-semibold truncate flex-1 text-left"
                              title={`${snap.visibleCols?.length || 0} col · densidad ${DENSITY_LABEL[snap.density] || "—"}`}>
                              ▸ {name}
                            </button>
                            <button
                              onClick={() => deleteProfile(name)}
                              data-testid={`mercado-delete-profile-${name}`}
                              className="text-rose-400 hover:text-rose-600"
                              title="Borrar perfil">✕</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
                    {colOrder.map((colId, idx) => {
                      const col = MERCADO_COLS.find(c => c.id === colId);
                      if (!col) return null;
                      return (
                        <div key={col.id} className="flex items-center gap-2 hover:bg-gray-50 rounded px-1 py-0.5">
                          <input
                            type="checkbox"
                            checked={visibleCols.has(col.id)}
                            onChange={() => toggleCol(col.id)}
                            className="accent-[#007934] w-3.5 h-3.5 cursor-pointer"
                            data-testid={`col-toggle-${col.id}`} />
                          <span className="text-xs text-gray-700 flex-1">{col.label}</span>
                          <button
                            onClick={() => moveCol(col.id, -1)}
                            disabled={idx === 0}
                            className="p-0.5 text-gray-400 hover:text-indigo-600 disabled:opacity-20"
                            data-testid={`col-up-${col.id}`}
                            title="Subir">▲</button>
                          <button
                            onClick={() => moveCol(col.id, 1)}
                            disabled={idx === colOrder.length - 1}
                            className="p-0.5 text-gray-400 hover:text-indigo-600 disabled:opacity-20"
                            data-testid={`col-down-${col.id}`}
                            title="Bajar">▼</button>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-2 pt-2 border-t border-gray-200 flex items-center justify-between text-[10px] text-gray-500">
                    <span>Orden actual: {activeCols.length} col.</span>
                    <span className="italic">Sort: {sortState.col} {sortState.dir === "desc" ? "↓" : "↑"}</span>
                  </div>
                </div>
              )}
            </div>
            <button
              onClick={exportExcel}
              className="bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1"
              data-testid="mercado-export-btn"
              title="Exportar a Excel con filtros aplicados">
              <Download size={12}/> Excel
            </button>
            <button
              onClick={exportExpired}
              className="bg-white border border-amber-300 text-amber-800 hover:bg-amber-50 text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1"
              data-testid="mercado-export-expired-btn"
              title="Excel de oportunidades vencidas en los últimos 3 meses (archivo comercial)">
              <Download size={12}/> Vencidas 3M
            </button>
            <button
              onClick={syncNow}
              disabled={syncing}
              className="bg-[#007934] text-white text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1 disabled:opacity-50"
              data-testid="mercado-sync-btn">
              {syncing ? <Loader2 size={12} className="animate-spin"/> : <RefreshCw size={12}/>}
              Sincronizar ahora
            </button>
          </div>
        </div>

        {/* Iter164 · Tarjeta KPI "Vencen esta semana". Click activa filtro
            que aísla las opps con fecha en los próximos 7 días. */}
        {(data.kpis?.due_soon ?? 0) > 0 && (
          <button
            type="button"
            onClick={() => { setDueSoonOnly(v => !v); setViewMode("strategic"); }}
            data-testid="mercado-kpi-due-soon"
            className={`mb-3 w-full text-left rounded-xl p-3 flex items-center gap-3 border transition ${
              dueSoonOnly
                ? "bg-amber-100 border-amber-500 shadow-md"
                : "bg-gradient-to-r from-amber-50 to-white border-amber-300 hover:from-amber-100"
            }`}>
            <div className="w-11 h-11 rounded-full bg-amber-200 flex items-center justify-center text-amber-800 font-black text-xl">
              {data.kpis.due_soon}
            </div>
            <div className="flex-1">
              <div className="text-sm font-bold text-amber-900">Vencen esta semana</div>
              <div className="text-[11px] text-amber-700">
                {dueSoonOnly
                  ? "Filtro ACTIVO · pulsa para quitarlo"
                  : "Presentación en los próximos 7 días. Pulsa para verlas ya."}
              </div>
            </div>
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${dueSoonOnly ? "bg-amber-800 text-white" : "bg-amber-200 text-amber-900"}`}>
              {dueSoonOnly ? "Filtrado" : "Ver ahora →"}
            </span>
          </button>
        )}

        {/* Iter166/168 · Tabs Propuestas (default) / Strategic / Non-strategic / Resueltas / Archivadas */}
        <div className="mb-3 flex items-center gap-2 border-b border-emerald-200/60 flex-wrap" data-testid="mercado-view-tabs">
          <button
            type="button"
            onClick={() => setViewMode("propuestas")}
            data-testid="mercado-tab-propuestas"
            className={`text-xs font-semibold px-3 py-1.5 border-b-2 -mb-px transition ${
              viewMode === "propuestas"
                ? "border-cyan-600 text-cyan-800"
                : "border-transparent text-gray-500 hover:text-cyan-700"
            }`}>
            Detectadas
            <span className="ml-1.5 text-[10px] bg-cyan-100 text-cyan-800 px-1.5 py-0.5 rounded-full font-mono" data-testid="mercado-tab-propuestas-count">
              {data.kpis.propuestas_count ?? 0}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setViewMode("strategic")}
            data-testid="mercado-tab-strategic"
            className={`text-xs font-semibold px-3 py-1.5 border-b-2 -mb-px transition ${
              viewMode === "strategic"
                ? "border-emerald-600 text-emerald-800"
                : "border-transparent text-gray-500 hover:text-emerald-700"
            }`}>
            Strategic Market
            <span className="ml-1.5 text-[10px] bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded-full font-mono" data-testid="mercado-tab-strategic-count">
              {data.kpis.strategic_count ?? 0}
            </span>
          </button>
          <button
            type="button"
            onClick={() => { setViewMode("non-strategic"); setFilters(f => ({ ...f, estado: "" })); }}
            data-testid="mercado-tab-non-strategic"
            className={`text-xs font-semibold px-3 py-1.5 border-b-2 -mb-px transition ${
              viewMode === "non-strategic"
                ? "border-zinc-600 text-zinc-800"
                : "border-transparent text-gray-500 hover:text-zinc-700"
            }`}>
            Non-strategic
            <span className="ml-1.5 text-[10px] bg-zinc-100 text-zinc-800 px-1.5 py-0.5 rounded-full font-mono" data-testid="mercado-tab-non-strategic-count">
              {data.kpis.nonstrategic_count ?? 0}
            </span>
          </button>
          {/* Iter192 · Tab "Resueltas" quitada por feedback usuario. Las
              ofertas resueltas están en Licitaciones. */}
          <button
            type="button"
            onClick={() => setViewMode("archivadas")}
            data-testid="mercado-tab-archivadas"
            className={`text-xs font-semibold px-3 py-1.5 border-b-2 -mb-px transition ${
              viewMode === "archivadas"
                ? "border-gray-500 text-gray-800"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}>
            Archivadas
            <span className="ml-1.5 text-[10px] bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded-full font-mono" data-testid="mercado-tab-archivadas-count">
              {data.kpis.archived ?? 0}
            </span>
          </button>
          {/* Iter207 · Tab "Descartadas" (triadas). Muestra las oportunidades
              descartadas manualmente + las auto-descartadas por no ser
              actividad VSM (CPV sin mapeo). Aquí el usuario puede revisar y
              revertir a PROPUESTA_OPORTUNIDAD si el auto-triaje se equivocó. */}
          <button
            type="button"
            onClick={() => setViewMode("descartadas")}
            data-testid="mercado-tab-descartadas"
            className={`text-xs font-semibold px-3 py-1.5 border-b-2 -mb-px transition ${
              viewMode === "descartadas"
                ? "border-red-500 text-red-700"
                : "border-transparent text-gray-500 hover:text-red-600"
            }`}
            title="Oportunidades descartadas manualmente o por auto-triaje (CPV sin actividad VSM)">
            Descartadas
            <span className="ml-1.5 text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-mono" data-testid="mercado-tab-descartadas-count">
              {data.kpis.descartadas_count ?? 0}
            </span>
          </button>

          {/* Iter234 · Feedback usuario: "los KPIs de mercado los saco del
              Excel... las nuevas de PLACE aparecen en una pestaña Pendientes
              de revisar separada, y sólo al aprobarlas pasan a sumar". Nunca
              cuentan en los KPIs oficiales de arriba (que ahora vienen del
              Excel/Licitaciones) hasta que un humano las Acepta. */}
          <button
            type="button"
            onClick={() => setViewMode("pending_review")}
            data-testid="mercado-tab-pending-review"
            className={`text-xs font-semibold px-3 py-1.5 border-b-2 -mb-px transition ${
              viewMode === "pending_review"
                ? "border-indigo-500 text-indigo-700"
                : "border-transparent text-gray-500 hover:text-indigo-600"
            }`}
            title="Oportunidades PLACSP detectadas por el scraper que aún NO ha aceptado ningún humano — no cuentan en los KPIs oficiales (Excel) hasta que se acepten">
            Pendientes de revisar
            <span className="ml-1.5 text-[10px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full font-mono" data-testid="mercado-tab-pending-review-count">
              {data.kpis.pending_review_count ?? 0}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setViewMode("kanban")}
            data-testid="mercado-tab-kanban"
            className={`text-xs font-semibold px-3 py-1.5 border-b-2 -mb-px transition ${
              viewMode === "kanban"
                ? "border-fuchsia-500 text-fuchsia-700"
                : "border-transparent text-gray-500 hover:text-fuchsia-600"
            }`}
            title="Tablero Kanban: arrastra oportunidades entre estados (mismas validaciones del formulario)">
            Kanban
          </button>

          {/* Iter170 · Filtro rápido "Sin responsable" y batch score GO/NO-GO */}
          <div className="ml-auto flex items-center gap-2">
            {ownerFilter && (
              <div className="text-[11px] font-semibold px-2 py-1 rounded-md bg-emerald-100 text-emerald-800 border border-emerald-300 inline-flex items-center gap-1"
                   data-testid="mercado-owner-filter-chip">
                Owner: {ownerFilter.split("@")[0]}
                <button
                  onClick={() => { setOwnerFilter(""); window.history.replaceState({}, "", window.location.pathname); }}
                  className="ml-1 text-emerald-600 hover:text-red-600"
                  title="Quitar filtro">✕</button>
              </div>
            )}
            {(viewMode === "strategic" || viewMode === "propuestas") && (
              <button
                type="button"
                onClick={() => setUnassignedOnly(v => !v)}
                data-testid="mercado-filter-unassigned"
                title="Muestra sólo opps sin responsable comercial asignado"
                className={`text-[11px] font-semibold px-2.5 py-1 rounded-md border inline-flex items-center gap-1.5 transition ${
                  unassignedOnly
                    ? "bg-orange-500 text-white border-orange-500"
                    : "bg-white text-gray-600 border-gray-300 hover:border-orange-400 hover:text-orange-600"
                }`}>
                {unassignedOnly ? "✓ Sin responsable" : "Sin responsable"}
              </button>
            )}
            {viewMode === "propuestas" && (
              <button
                type="button"
                onClick={async () => {
                  if (!window.confirm("Ejecutar Pando en lote sobre las Propuestas para generar recomendación GO / NO-GO. Se ejecutará en segundo plano (~30-60s). ¿Continuar?")) return;
                  setBatchScoreRunning(true);
                  setBatchScoreProgress({ progress: 0, total: 20 });
                  try {
                    // Iter171 · Lanzamos background + polling para evitar timeouts Cloudflare
                    const r = await axios.post(`${API}/mercado/batch-score-gonogo?limit=20&only_missing=true&background=true`);
                    const jobId = r.data.job_id;
                    const total = r.data.total || 20;
                    setBatchScoreProgress({ progress: 0, total });
                    toast.info(`Pando puntuando ${total} propuestas en segundo plano…`);
                    // Poll cada 3s
                    const poll = async () => {
                      try {
                        const j = await axios.get(`${API}/mercado/batch-score-gonogo/${jobId}`);
                        const s = j.data.status;
                        setBatchScoreProgress({ progress: j.data.progress || 0, total: j.data.total || total });
                        if (s === "done") {
                          toast.success(`Pando: ${j.data.GO ?? 0} GO · ${j.data.NO_GO ?? 0} NO-GO · ${j.data.CONDITIONAL ?? 0} CONDITIONAL${j.data.failed ? ` · ${j.data.failed} fallidos` : ""}`);
                          setBatchScoreRunning(false);
                          setBatchScoreProgress(null);
                          load();
                        } else if (s === "failed") {
                          toast.error("Batch fallido");
                          setBatchScoreRunning(false);
                          setBatchScoreProgress(null);
                        } else {
                          setTimeout(poll, 3000);
                        }
                      } catch (e) {
                        toast.error("Error consultando estado del batch");
                        setBatchScoreRunning(false);
                        setBatchScoreProgress(null);
                      }
                    };
                    setTimeout(poll, 2000);
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Error en batch score");
                    setBatchScoreRunning(false);
                    setBatchScoreProgress(null);
                  }
                }}
                disabled={batchScoreRunning}
                data-testid="mercado-batch-score-btn"
                title="Pando evalúa las 20 primeras Propuestas y asigna GO / NO-GO / CONDITIONAL"
                className="text-[11px] font-semibold px-2.5 py-1 rounded-md border bg-gradient-to-r from-indigo-500 to-purple-500 text-white border-indigo-500 hover:from-indigo-600 hover:to-purple-600 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5">
                {batchScoreRunning
                  ? (batchScoreProgress ? `Puntuando ${batchScoreProgress.progress}/${batchScoreProgress.total}…` : "Puntuando…")
                  : "✨ Batch GO/NO-GO"}
              </button>
            )}
          </div>

          {/* Toggle vencidas: aplica en propuestas / strategic / non-strategic */}
          {["propuestas", "strategic", "non-strategic"].includes(viewMode) && (() => {
            // Iter175 · Alcance según tab activo
            const inScope = (m) => {
              if (m.is_expired === true) return false;
              if (viewMode === "propuestas") {
                return m.estado_licitacion === "PROPUESTA_OPORTUNIDAD" && !m.oferta_comercial_id;
              }
              if (viewMode === "non-strategic") {
                return m.estado_licitacion === "NO_ESTRATEGICA" && !m.oferta_comercial_id;
              }
              // strategic
              return !m.oferta_comercial_id;
            };
            const scope = (data?.items || []).filter(inScope);
            const expired = scope.filter(m => {
              const f = (m.fecha_presentacion || "").slice(0, 10);
              return f && f < todayISO;
            }).length;
            if (expired === 0 && hideExpired) return null;
            return (
              <button
                type="button"
                onClick={() => setHideExpired(v => !v)}
                data-testid="mercado-toggle-expired"
                title={hideExpired
                  ? `Hay ${expired} oportunidad(es) con fecha de presentación pasada. Púlsalo para mostrarlas.`
                  : "Ocultar de nuevo las oportunidades vencidas"}
                className={`ml-auto text-[11px] font-semibold px-2.5 py-1 rounded-md border inline-flex items-center gap-1.5 transition ${
                  hideExpired
                    ? "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                    : "bg-amber-100 border-amber-400 text-amber-900 hover:bg-amber-200"
                }`}>
                {hideExpired
                  ? <>Anteriores <span className="text-[10px] bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded-full font-mono">{expired}</span></>
                  : <>Ocultar anteriores</>}
              </button>
            );
          })()}
        </div>

        {/* Iter169 · Panel de análisis Strategic Market · nº y € por estado + motivos */}
        {viewMode === "strategic" && data?.kpis && (() => {
          const k = data.kpis;
          const fmtCompactEUR = (n) => (n == null || n === 0)
            ? "0 €"
            : new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", notation: "compact", maximumFractionDigits: 1 }).format(n);
          const totalStrategic = (k.strategic_count || 0) + (k.PRESENTADA || 0) + (k.NO_PRESENTADA || 0);
          const totalImporte = (k.strategic_importe || 0) + (k.presentadas_importe || 0) + (k.no_presentadas_importe || 0);
          const pctPres = totalImporte > 0 ? Math.round((k.presentadas_importe / totalImporte) * 100) : 0;
          const pctNoPres = totalImporte > 0 ? Math.round((k.no_presentadas_importe / totalImporte) * 100) : 0;
          const motivosNP = k.motivos_no_presentacion || {};
          const totalMotNP = Object.values(motivosNP).reduce((a, b) => a + b, 0);
          const MOTIVO_LABEL = {
            "No Chance": "No Chance",
            "Non-strategic": "No estratégico",
            "Profitability": "No económicamente viable",
            "Workload (Resources)": "Falta de recursos",
            "Solvency": "Solvencia",
            "sin_motivo": "Sin motivo",
          };
          const MOTIVO_COLOR = {
            "No Chance": "bg-red-100 text-red-700",
            "Non-strategic": "bg-zinc-100 text-zinc-700",
            "Profitability": "bg-orange-100 text-orange-700",
            "Workload (Resources)": "bg-amber-100 text-amber-700",
            "Solvency": "bg-purple-100 text-purple-700",
            "sin_motivo": "bg-gray-100 text-gray-500",
          };
          return (
            <div className="mb-4 bg-gradient-to-br from-emerald-50/70 via-white to-white border border-emerald-200 rounded-xl p-4 space-y-3"
                 data-testid="mercado-strategic-analysis">
              <div className="flex items-baseline gap-2">
                <h3 className="text-xs font-bold uppercase tracking-wide text-emerald-900">Análisis Strategic Market</h3>
                <span className="text-[10px] text-emerald-600">Universo total: {totalStrategic} opps · {fmtCompactEUR(totalImporte)}</span>
                <button
                  type="button"
                  onClick={() => setShowConciliacion(true)}
                  data-testid="mercado-conciliar-presentadas-btn"
                  title="Explica, expediente a expediente, por qué el importe de Presentadas difiere del de Licitaciones (importe total vs participación VSM/UTE)"
                  className="ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-md border border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-50 inline-flex items-center gap-1">
                  <GitCompare size={11} /> Conciliar Presentadas vs Licitaciones
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div className="bg-white rounded-lg p-3 border border-emerald-200" data-testid="strategic-kpi-en-mercado">
                  <div className="text-[9px] uppercase text-emerald-600 font-bold tracking-wide">En mercado (activas)</div>
                  <div className="text-xl font-bold text-emerald-800 mt-1">{k.strategic_count || 0}</div>
                  <div className="text-[11px] text-emerald-600 font-mono">{fmtCompactEUR(k.strategic_importe || 0)}</div>
                </div>
                <button
                  type="button"
                  onClick={() => { setViewMode("strategic"); setFilters(f => ({ ...f, estado: "PRESENTADA" })); }}
                  data-testid="strategic-kpi-presentadas"
                  title="Filtrar Presentadas en Mercado (unifica mercado_opportunities + ofertas_comercial)"
                  className="bg-white rounded-lg p-3 border border-green-200 text-left hover:bg-green-50 hover:border-green-300 transition cursor-pointer">
                  <div className="text-[9px] uppercase text-green-700 font-bold tracking-wide flex items-center gap-1">
                    Presentadas <span className="text-green-500">↓</span>
                  </div>
                  <div className="text-xl font-bold text-green-800 mt-1">{k.PRESENTADA || 0} <span className="text-xs text-green-500 font-normal">({pctPres}%)</span></div>
                  <div className="text-[11px] text-green-700 font-mono">{fmtCompactEUR(k.presentadas_importe || 0)}</div>
                </button>
                <button
                  type="button"
                  onClick={() => { setViewMode("strategic"); setFilters(f => ({ ...f, estado: "NO_PRESENTADA" })); }}
                  data-testid="strategic-kpi-no-presentadas"
                  title="Filtrar No Presentadas en Mercado"
                  className="bg-white rounded-lg p-3 border border-rose-200 text-left hover:bg-rose-50 hover:border-rose-300 transition cursor-pointer">
                  <div className="text-[9px] uppercase text-rose-700 font-bold tracking-wide flex items-center gap-1">
                    No presentadas <span className="text-rose-500">↓</span>
                  </div>
                  <div className="text-xl font-bold text-rose-800 mt-1">{k.NO_PRESENTADA || 0} <span className="text-xs text-rose-500 font-normal">({pctNoPres}%)</span></div>
                  <div className="text-[11px] text-rose-700 font-mono">{fmtCompactEUR(k.no_presentadas_importe || 0)}</div>
                </button>
                <div className="bg-white rounded-lg p-3 border border-gray-200" data-testid="strategic-kpi-cerradas">
                  <div className="text-[9px] uppercase text-gray-600 font-bold tracking-wide">Suspendidas · Anuladas</div>
                  <div className="text-xl font-bold text-gray-800 mt-1">{(k.SUSPENDIDA || 0) + (k.ANULADA || 0)}</div>
                  <div className="text-[11px] text-gray-500 font-mono">del histórico</div>
                </div>
              </div>
              {totalMotNP > 0 && (
                <div className="pt-2">
                  <div className="text-[10px] uppercase font-bold text-rose-800 tracking-wide mb-1.5">Motivos NO presentación · {totalMotNP} ofertas</div>
                  <div className="flex flex-wrap gap-1.5" data-testid="strategic-motivos-np">
                    {Object.entries(motivosNP)
                      .sort((a, b) => b[1] - a[1])
                      .map(([m, n]) => {
                        const pct = Math.round((n / totalMotNP) * 100);
                        return (
                          <div key={m}
                               className={`text-[10px] px-2 py-1 rounded-full font-semibold ${MOTIVO_COLOR[m] || "bg-gray-100 text-gray-700"}`}
                               title={`${n} ofertas · ${pct}%`}
                               data-testid={`motivo-np-${m.replace(/\W+/g, '_')}`}>
                            {MOTIVO_LABEL[m] || m} <span className="opacity-70">· {n}</span>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Iter234 · Feedback usuario: "los KPIs de mercado los saco del Excel
            porque sino no sabemos si lo que clasifica el sistema es correcto".
            Estos KPIs ahora vienen de `ofertas_comercial` (= el Excel Mercado
            2026 importado), no de la detección automática PLACSP. */}
        <div className="flex items-center gap-1.5 text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1 mb-1.5 w-fit" data-testid="mercado-kpi-excel-note">
          <CheckCircle2 size={11}/> KPIs oficiales = Mercado {filters.year ? filters.year : "(todos los años)"} · por Fecha de Presentación. Las oportunidades PLACSP sin aceptar están aparte, en "Pendientes de revisar".
        </div>
        {/* C02 (CRM audit 2026-10-13) · Al buscar, el listado de abajo se filtra
            pero los contadores/Total siguen siendo de TODA la cartera. Antes esto
            confundía (1 resultado de "Alcobendas" junto a un Total de 377). Se
            delimita explícitamente qué es búsqueda y qué es cartera. */}
        {filters.q && filters.q.trim() && (
          <div className="flex items-center gap-1.5 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1 mb-1.5" data-testid="mercado-search-scope-note">
            <Search size={11}/>
            Búsqueda «<b>{filters.q.trim()}</b>»: filtra sólo el <b>listado de abajo</b> ({sortedItems.length} resultado{sortedItems.length === 1 ? "" : "s"}). Los contadores y el Total de arriba siguen siendo de <b>toda la cartera</b>, no de la búsqueda.
          </div>
        )}
        {/* KPIs por estado. Iter206 · Todas las KPIs filtran INLINE (misma pestaña):
            click actualiza `viewMode` y `filters.estado` sin navegar fuera. */}
        <div className="grid grid-cols-3 md:grid-cols-6 lg:grid-cols-12 gap-2">
          <KPI label="Total"         value={(dgKpis || data.kpis).total}
                                     importe={((dgKpis || data.kpis).publicadas_importe || 0) + ((dgKpis || data.kpis).por_decidir_importe || 0) + ((dgKpis || data.kpis).estudio_importe || 0) + ((dgKpis || data.kpis).presentadas_importe || 0) + ((dgKpis || data.kpis).no_presentadas_importe || 0) + ((dgKpis || data.kpis).anuladas_importe || 0) + ((dgKpis || data.kpis).suspendidas_importe || 0)}
                                     color="bg-white text-gray-800 border-gray-200" testid="mercado-kpi-total"
                                     onClick={() => { setViewMode("propuestas"); setFilters(f => ({ ...f, estado: "" })); }}
                                     hint="Click: ver todas · Importe = suma EXACTA de los 7 estados con tarjeta (Publicadas+Por decidir+En estudio+Presentadas+No presentadas+Anuladas+Suspendidas), igual que el recuento. Detectadas y No estratégica no entran en el Total. Borrador/Desistida quedan fuera, ver aviso debajo."/>
          <KPI label="Detectadas"    value={(dgKpis || data.kpis).propuestas_count ?? (dgKpis || data.kpis).PROPUESTA_OPORTUNIDAD}
                                     importe={(dgKpis || data.kpis).propuestas_importe}
                                     color="bg-cyan-50 text-cyan-700 border-cyan-200"
                                     testid="mercado-kpi-propuestas"
                                     onClick={() => { setViewMode("propuestas"); setFilters(f => ({ ...f, estado: "PROPUESTA_OPORTUNIDAD" })); }}
                                     hint="Click: ver el listado de oportunidades detectadas (nuevas del sync)"/>
          <KPI label="Publicadas"    value={(dgKpis || data.kpis).PUBLICADA}
                                     importe={(dgKpis || data.kpis).publicadas_importe}
                                     color="bg-blue-50 text-blue-700 border-blue-200" testid="mercado-kpi-publicadas"
                                     sub={splitSub("PUBLICADA")}
                                     onClick={() => { setViewMode("strategic"); setFilters(f => ({ ...f, estado: "PUBLICADA" })); }}
                                     hint="Click: ver Publicadas · el total incluye vencidas (plazo pasado); el listado muestra las vigentes"/>
          <KPI label="Por decidir"   value={(dgKpis || data.kpis).POR_DECIDIR}
                                     importe={(dgKpis || data.kpis).por_decidir_importe}
                                     color="bg-amber-50 text-amber-700 border-amber-200" testid="mercado-kpi-por-decidir"
                                     sub={splitSub("POR_DECIDIR")}
                                     onClick={() => { setViewMode("strategic"); setFilters(f => ({ ...f, estado: "POR_DECIDIR" })); }}
                                     hint="Click: ver Por decidir · el total incluye vencidas (plazo pasado); el listado muestra las vigentes"/>
          <KPI label="En estudio"    value={(dgKpis || data.kpis).ESTUDIO}
                                     importe={(dgKpis || data.kpis).estudio_importe}
                                     color="bg-indigo-50 text-indigo-700 border-indigo-200" testid="mercado-kpi-estudio"
                                     sub={splitSub("ESTUDIO")}
                                     onClick={() => { setViewMode("strategic"); setFilters(f => ({ ...f, estado: "ESTUDIO" })); }}
                                     hint="Click: ver En estudio · el total incluye vencidas (plazo pasado); el listado muestra las vigentes"/>
          <KPI label="Presentadas"   value={(dgKpis || data.kpis).PRESENTADA}
                                     importe={(dgKpis || data.kpis).presentadas_importe}
                                     color="bg-green-50 text-green-700 border-green-200" testid="mercado-kpi-presentadas"
                                     onClick={() => { setViewMode("resueltas"); setFilters(f => ({ ...f, estado: "PRESENTADA" })); }}
                                     hint="Click: filtra el listado por Presentadas"/>
          <KPI label="No presentadas" value={(dgKpis || data.kpis).NO_PRESENTADA}
                                     importe={(dgKpis || data.kpis).no_presentadas_importe}
                                     color="bg-rose-50 text-rose-700 border-rose-200" testid="mercado-kpi-no-presentadas"
                                     onClick={() => { setViewMode("resueltas"); setFilters(f => ({ ...f, estado: "NO_PRESENTADA" })); }}
                                     hint="Click: filtra el listado por No presentadas"/>
          <KPI label="Anuladas"      value={(dgKpis || data.kpis).ANULADA}
                                     importe={(dgKpis || data.kpis).anuladas_importe}
                                     color="bg-gray-50 text-gray-600 border-gray-200" testid="mercado-kpi-anuladas"
                                     onClick={() => { setViewMode("resueltas"); setFilters(f => ({ ...f, estado: "ANULADA" })); }}
                                     hint="Click: filtra el listado por Anuladas"/>
          <KPI label="Suspendidas"   value={(dgKpis || data.kpis).SUSPENDIDA}
                                     importe={(dgKpis || data.kpis).suspendidas_importe}
                                     color="bg-gray-50 text-gray-500 border-gray-200" testid="mercado-kpi-suspendidas"
                                     onClick={() => { setViewMode("resueltas"); setFilters(f => ({ ...f, estado: "SUSPENDIDA" })); }}
                                     hint="Click: filtra el listado por Suspendidas"/>
          <KPI label="No estratégica" value={(dgKpis || data.kpis).nonstrategic_count ?? 0}
                                     importe={(dgKpis || data.kpis).nonstrategic_importe}
                                     color="bg-orange-50 text-orange-700 border-orange-200"
                                     testid="mercado-kpi-no-estrategica"
                                     onClick={() => { setViewMode("non-strategic"); setFilters(f => ({ ...f, estado: "" })); }}
                                     hint="Click: ver las no estratégicas — incluye las 'No presentadas' con motivo Non-strategic (o sin motivo), que salen del embudo de No presentadas"/>
          <SinDGCard testid="mercado-sin-dg" role={user?.role} onDrillDown={() => setDg("SIN_DG")}/>
        </div>
        {/* Iter156 · Desglose DG que suma el total (bug reportado: 92 + 288 ≠ 500) */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]" data-testid="mercado-by-dg-breakdown">
          <span className="font-semibold text-gray-700">Por DG:</span>
          <span data-testid="mercado-dg-servicios"><b className="text-emerald-700">{(data.kpis.by_dg?.SERVICIOS ?? 0)}</b> Servicios</span>
          <span className="text-gray-300">·</span>
          <span data-testid="mercado-dg-tratamiento"><b className="text-blue-700">{(data.kpis.by_dg?.TRATAMIENTO ?? 0)}</b> Tratamiento</span>
          <span className="text-gray-300">·</span>
          <span data-testid="mercado-dg-sin"><b className="text-amber-700">{(data.kpis.by_dg?.SIN_DG ?? 0)}</b> Sin DG</span>
          <span className="text-gray-400 ml-2">= <b>{(data.kpis.by_dg?.SERVICIOS ?? 0) + (data.kpis.by_dg?.TRATAMIENTO ?? 0) + (data.kpis.by_dg?.SIN_DG ?? 0)}</b> total ({data.kpis.total})</span>
        </div>

        {/* SU02 (valoriza-verificacion-ola3-feedback-ofertas-su-2026-10-13.md,
            H01) · el TOTAL es exactamente la suma de los 7 estados con
            tarjeta (regla explícita del informe). Borrador y Desistida NO
            cuentan en el Total pero tampoco desaparecen — se muestran aquí,
            nunca ocultos ni reclasificados a otro estado para cuadrar. */}
        {((data.kpis.fuera_de_total?.borrador ?? 0) + (data.kpis.fuera_de_total?.desistida ?? 0)) > 0 && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] p-1.5 rounded bg-amber-50 border border-amber-200" data-testid="mercado-fuera-de-total">
            <span className="font-semibold text-amber-700">Fuera del Total (no son ninguno de los 7 estados operativos):</span>
            <span data-testid="mercado-fuera-de-total-borrador"><b className="text-amber-700">{data.kpis.fuera_de_total?.borrador ?? 0}</b> en Borrador</span>
            <span className="text-amber-300">·</span>
            <span data-testid="mercado-fuera-de-total-desistida"><b className="text-amber-700">{data.kpis.fuera_de_total?.desistida ?? 0}</b> Desistidas</span>
            <span className="text-amber-500 ml-2">→ universo completo: <b>{data.kpis.universo_completo ?? data.kpis.total}</b></span>
          </div>
        )}

        {/* David 2026-06 · "No presentadas" (embudo) = sólo las ESTRATÉGICAS
            (estudiadas, no llegaron por No Chance/Profitability/Workload/
            Solvency). Las no estratégicas (motivo "Non-strategic" o sin motivo)
            salen del embudo y se cuentan en el grupo Non-strategic. */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]" data-testid="mercado-no-presentada-breakdown">
          <span className="font-semibold text-gray-700">No presentadas = {data.kpis.no_presentada_breakdown?.estrategica ?? 0} estratégicas</span>
          <span data-testid="mercado-no-pres-estrategica" className="text-gray-500">(estudiadas, no llegaron por No Chance/Profitability/Workload/Solvency)</span>
          {((data.kpis.no_presentada_breakdown?.no_estrategica ?? 0) + (data.kpis.no_presentada_breakdown?.sin_motivo ?? 0)) > 0 && (
            <>
              <span className="text-gray-300">·</span>
              <span data-testid="mercado-no-pres-no-estrategica"><b className="text-orange-700">{(data.kpis.no_presentada_breakdown?.no_estrategica ?? 0) + (data.kpis.no_presentada_breakdown?.sin_motivo ?? 0)}</b> no estratégicas → movidas al grupo Non-strategic</span>
            </>
          )}
        </div>

        {/* SU07 (valoriza-verificacion-ola3-feedback-ofertas-su-2026-10-13.md)
            · "Mover accesos de duplicados, posibles vínculos y motivos
            pendientes a un bloque «Calidad y revisión» dentro de Mercado." */}
        <CalidadRevisionBlock />

        {/* Iter180 · Clasificación Estratégico / No estratégico = Addressable / Non-strategic
            del Excel de negocio. Debe cuadrar exactamente con el Total de Mercado. */}
        <div className="mt-2 flex flex-wrap items-center gap-2 p-2 rounded-lg bg-gradient-to-r from-emerald-50 via-white to-zinc-50 border border-emerald-200" data-testid="mercado-market-type-breakdown">
          <span className="text-[11px] font-semibold text-slate-700 mr-1">Clasificación mercado:</span>
          <HelpTip text={'Esta clasificación (Estratégico/No estratégico "Addressable") viene de la columna MARKET del Excel de negocio y es DISTINTA de la tarjeta "No estratégica" de arriba (que usa el flag manual estrategica=false en el pipeline de Mercado). Pueden dar números distintos porque miden cosas distintas.'} />
          <button
            type="button"
            onClick={() => setMarketTypeFilter(mt => mt === "ADDRESSABLE" ? "" : "ADDRESSABLE")}
            data-testid="mercado-chip-addressable"
            className={`text-[11px] px-2.5 py-1 rounded-full font-semibold inline-flex items-center gap-1.5 transition ${
              marketTypeFilter === "ADDRESSABLE"
                ? "bg-emerald-600 text-white border border-emerald-700 shadow-sm"
                : "bg-emerald-100 text-emerald-800 border border-emerald-300 hover:bg-emerald-200"
            }`}
            title="Estratégico = Addressable · click para filtrar">
            <span>Estratégico (Addressable)</span>
            <b className="font-mono">{data.kpis.addressable_count ?? 0}</b>
          </button>
          <button
            type="button"
            onClick={() => setMarketTypeFilter(mt => mt === "NON_STRATEGIC" ? "" : "NON_STRATEGIC")}
            data-testid="mercado-chip-non-strategic"
            className={`text-[11px] px-2.5 py-1 rounded-full font-semibold inline-flex items-center gap-1.5 transition ${
              marketTypeFilter === "NON_STRATEGIC"
                ? "bg-zinc-700 text-white border border-zinc-800 shadow-sm"
                : "bg-zinc-100 text-zinc-800 border border-zinc-300 hover:bg-zinc-200"
            }`}
            title="No estratégico = Non-strategic · click para filtrar">
            <span>No estratégico</span>
            <b className="font-mono">{data.kpis.non_addressable_count ?? 0}</b>
          </button>
          <span className="text-[11px] text-gray-500">
            = <b className="text-slate-800 font-mono">{data.kpis.market_total_count ?? 0}</b> total mercado
          </span>
          {/* C02 (CRM audit 2026-10-13) · "Addressable" se calcula sobre un
              universo MÁS AMPLIO (escaneo de mercado PLACSP `mercado_opportunities`
              + pipeline CRM) que el Total operativo de arriba (sólo los 7 estados
              del CRM). Antes se marcaba "⚠ ≠ total" como si fuera un error; son
              universos distintos y no tienen por qué coincidir. */}
          <HelpTip text={`"Total mercado" (${data.kpis.market_total_count ?? 0}) es el universo de mercado completo: escaneo PLACSP + oportunidades del CRM. El "Total" de arriba (${data.kpis.total}) es sólo el subconjunto operativo que trabajamos (los 7 estados del CRM). Son universos distintos — el de mercado es un superconjunto, por eso es mayor; no es un descuadre.`} />
          {marketTypeFilter && (
            <button
              type="button"
              onClick={() => setMarketTypeFilter("")}
              data-testid="mercado-chip-market-clear"
              className="ml-auto text-[10px] text-slate-500 hover:text-red-600">
              ✕ limpiar
            </button>
          )}
        </div>
        {dg && (
          <div className="mt-2 text-[11px] text-slate-500" data-testid="mercado-dg-active-note">
            Mostrando sólo <b className="text-slate-700">{DG_LABELS[dg]}</b>: los contadores y el Total de arriba cuentan únicamente las oportunidades con esa Dirección General asignada (las de otra DG o sin DG no entran en estas cifras mientras el filtro esté activo).
          </div>
        )}

        {/* Iter142 · Toggle comparativa DG */}
        <div className="mt-3 flex justify-end">
          <button
            onClick={() => setCompareMode(m => !m)}
            data-testid="mercado-dg-compare-toggle"
            title="Comparar KPIs de Residuos vs Servicios Urbanos"
            className={`text-[11px] rounded-full px-3 py-1 border inline-flex items-center gap-1 font-semibold transition-all ${
              compareMode
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-600 border-slate-200 hover:border-slate-400 hover:text-slate-800"
            }`}>
            <GitCompare size={11}/> {compareMode ? "Ocultar comparativa DG" : "Comparar DG"}
          </button>
        </div>
        {compareMode && (
          <div className="mt-3">
            <DGCompareCard metrics={compareMetrics} onClose={() => setCompareMode(false)}/>
          </div>
        )}

        {/* Iter136 · Ranking territorial: top 5 CCAA por presupuesto de la DG activa */}
        <CCAARanking items={sortedItems} dg={dg}/>
      </div>

      {/* Filtros */}
      <div className="flex items-center gap-2 flex-wrap">
        <input
          placeholder="Buscar (tolera tildes): objeto, cliente, expediente, lote…"
          value={qInput}
          onChange={e=>setQInput(e.target.value)}
          className="flex-1 min-w-[240px] border rounded-lg px-3 py-1.5 text-xs"
          data-testid="mercado-filter-q"
        />
        {/* Iter403 · Filtro por AÑO (Fecha de Presentación). Por defecto el año en curso. */}
        <div className="flex items-center gap-1 shrink-0" title="Contextualiza Mercado por el año de la Fecha de Presentación de oferta">
          <span className="text-[11px] text-gray-500 font-semibold">Año</span>
          <select
            value={filters.year}
            onChange={e => setFilters(f => ({ ...f, year: e.target.value }))}
            data-testid="mercado-year-select"
            className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-semibold bg-white text-gray-700 cursor-pointer">
            {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i).map(y => (
              <option key={y} value={String(y)}>{y}</option>
            ))}
            <option value="">Todos los años</option>
          </select>
        </div>
        <button
          onClick={()=>setFiltersOpen(v=>!v)}
          className={`text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1 border ${activeFilters>0?"bg-indigo-50 border-indigo-200 text-indigo-700":"bg-white border-gray-200 text-gray-600"}`}
          data-testid="mercado-filter-toggle">
          <Filter size={12}/> Filtros {activeFilters>0 && <span className="bg-indigo-600 text-white text-[10px] px-1.5 rounded-full">{activeFilters}</span>}
        </button>
        {activeFilters>0 && <button onClick={resetFilters} className="text-xs text-gray-500 hover:text-gray-700" data-testid="mercado-filter-clear-all">Limpiar</button>}
        <SavedViewsBar screen="mercado" currentFilters={filters} onApply={applySavedView} />
        {/* Rango de importe (M€, total de licitación) — pedido del usuario */}
        <ImporteRangeFilter
          min={filters.importe_min}
          max={filters.importe_max}
          onChange={(mn, mx) => setFilters({ ...filters, importe_min: mn, importe_max: mx })}
          testidPrefix="mercado-filter-importe"
        />
        {/* Iter179.1 · Foco por comercial */}
        <button
          onClick={() => setMineOnly(v => !v)}
          data-testid="mercado-mine-toggle"
          title={mineOnly ? "Mostrando solo las asignadas a ti. Click para ver todo el equipo." : "Mostrando todo el equipo. Click para ver solo las tuyas."}
          className={`text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1 border ${
            mineOnly
              ? "bg-[#007934] border-[#007934] text-white hover:bg-[#006528]"
              : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
          }`}>
          <UserCheck size={12}/> {mineOnly ? "Solo mías" : "Ver todo"}
        </button>
        {/* iter 130 · Sparkline trend filter */}
        <button
          onClick={() => setFilters({ ...filters, trend_up_only: !filters.trend_up_only })}
          data-testid="mercado-trend-up-filter"
          title="Sólo oportunidades cuyo score Pando sube en los últimos 3 rescores"
          className={`text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1 border ${
            filters.trend_up_only
              ? "bg-emerald-100 border-emerald-400 text-emerald-800"
              : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
          }`}>
          <TrendingUp size={12}/> Sólo tendencia positiva
        </button>
      </div>

      <ActiveFilterChips chips={activeChips} onClearAll={activeChips.length ? resetFilters : null} />

      {filtersOpen && catalog && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs" data-testid="mercado-filter-panel">
          <L label="Estado" help="Estado de la oportunidad en el pipeline de Mercado (propuesta, en estudio, por decidir, publicada, presentada, etc.).">
            <select value={filters.estado} onChange={e=>setFilters({...filters, estado: e.target.value})} className="w-full border rounded px-2 py-1 bg-white" data-testid="mercado-filter-estado">
              <option value="">(todos)</option>
              {catalog.estados.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
            </select>
          </L>
          <L label="Dirección General" help="Dirección General responsable: SERVICIOS (servicios urbanos) o TRATAMIENTO (residuos).">
            <select value={filters.direccion_general} onChange={e=>setFilters({...filters, direccion_general: e.target.value})} className="w-full border rounded px-2 py-1 bg-white">
              <option value="">(todas)</option>
              <option value="SERVICIOS">SERVICIOS</option>
              <option value="TRATAMIENTO">TRATAMIENTO</option>
            </select>
          </L>
          <L label="Actividad" help="Actividad de negocio: C&W, GA, MOB, Light Capex o PPP.">
            <select value={filters.actividad} onChange={e=>setFilters({...filters, actividad: e.target.value})} className="w-full border rounded px-2 py-1 bg-white">
              <option value="">(todas)</option>
              <option value="C&W">C&amp;W</option>
              <option value="GA">GA</option>
              <option value="MOB">MOB</option>
              <option value="Light Capex">Light Capex</option>
              <option value="PPP">PPP</option>
            </select>
          </L>
          <L label="Plataforma" help="Plataforma o fuente de origen de la oportunidad (PLACSP, BOE, TED, scraping sectorial…).">
            <select value={filters.plataforma} onChange={e=>setFilters({...filters, plataforma: e.target.value})} className="w-full border rounded px-2 py-1 bg-white">
              <option value="">(todas)</option>
              {catalog.plataformas.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </L>
          <L label="Cliente" help="Filtra por el nombre del cliente u órgano de contratación (coincidencia parcial)."><input value={filters.cliente} onChange={e=>setFilters({...filters, cliente: e.target.value})} className="w-full border rounded px-2 py-1"/></L>
          <L label="Expediente" help="Filtra por el código de expediente (coincidencia parcial)."><input value={filters.expediente} onChange={e=>setFilters({...filters, expediente: e.target.value})} className="w-full border rounded px-2 py-1" data-testid="mercado-filter-expediente" placeholder="p.ej. 2026/123"/></L>
          <L label="Tipo contratación" help="NC (Nueva Contratación), ADC (Adicional) o R (Renovación). Solo aplica a licitaciones ya clasificadas (las oportunidades de mercado sin clasificar se ocultan al filtrar por tipo).">
            <select value={filters.tipo} onChange={e=>setFilters({...filters, tipo: e.target.value})} className="w-full border rounded px-2 py-1 bg-white" data-testid="mercado-filter-tipo">
              <option value="">(todos)</option>
              <option value="NC">NC · Nueva Contratación</option>
              <option value="ADC">ADC · Adicional</option>
              <option value="R">R · Renovación</option>
            </select>
          </L>
          <L label="Provincia" help="Filtra por la provincia del municipio del contrato."><input value={filters.provincia} onChange={e=>setFilters({...filters, provincia: e.target.value})} className="w-full border rounded px-2 py-1"/></L>
          <L label="Comunidad autónoma" help="Filtra por la comunidad autónoma del contrato.">
            <select
              value={filters.ccaa}
              onChange={e=>setFilters({...filters, ccaa: e.target.value})}
              data-testid="mercado-ccaa-filter"
              className="w-full border rounded px-2 py-1 bg-white">
              <option value="">(todas)</option>
              {ccaaOptions.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </L>
          <L label="F. Presentación desde" help="Muestra oportunidades cuya fecha límite de presentación es igual o posterior a esta fecha."><input type="date" value={filters.fecha_from} onChange={e=>setFilters({...filters, fecha_from: e.target.value})} className="w-full border rounded px-2 py-1"/></L>
          <L label="F. Presentación hasta" help="Muestra oportunidades cuya fecha límite de presentación es igual o anterior a esta fecha."><input type="date" value={filters.fecha_to} onChange={e=>setFilters({...filters, fecha_to: e.target.value})} className="w-full border rounded px-2 py-1"/></L>
        </div>
      )}

      {/* CRM-R05 · Vista Kanban: arrastrar tarjetas entre estados reutiliza el
          mismo changeEstado del formulario (mismas validaciones y modales). */}
      {viewMode === "kanban" && (
        <MercadoKanban
          items={sortedItems}
          onMove={(it, target) => changeEstado(it.id, target)}
          canMove={(from, to) => (ALLOWED_TRANSITIONS[from] || []).includes(to)}
        />
      )}

      {/* Tabla con columnas configurables y sort persistido */}
      <div className={`bg-white rounded-xl border border-gray-200 overflow-auto table-sticky-scroll ${viewMode === "kanban" ? "hidden" : ""}`} data-testid="mercado-table-scroll">
        <table className={`${DENSITY_TEXT[density]}`} style={{ tableLayout: "fixed", width: "max-content", minWidth: "100%" }}>
          <colgroup>
            {activeCols.map(col => (
              <col key={col.id} style={{ width: `${colWidths[col.id] || DEFAULT_WIDTHS[col.id] || 120}px` }} />
            ))}
            <col style={{ width: "90px" }} />
          </colgroup>
          <thead className="bg-gray-50 text-[9px] uppercase text-gray-600 sticky top-0 z-10">
            <tr>
              {activeCols.map((col, idx) => {
                const isActive = sortState.col === col.id;
                const arrow = !isActive ? "↕" : (sortState.dir === "asc" ? "▲" : "▼");
                // Iter169 · Sticky columna Objeto/Título (la primera de esos IDs)
                const stickyIdx = activeCols.findIndex(c => c.id === "titulo" || c.id === "objeto");
                const isSticky = idx === stickyIdx && stickyIdx !== -1;
                return (
                  <th
                    key={col.id}
                    className={`text-${col.align} ${DENSITY_CELL_CLASS[density]} select-none hover:bg-gray-100 ${isActive ? "bg-indigo-50 text-indigo-800" : ""} relative align-bottom ${isSticky ? "sticky left-0 z-30 bg-gray-50 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]" : ""}`}
                    data-testid={`mercado-th-${col.id}`}
                    title={`Ordenar por ${col.label}`}
                  >
                    <span onClick={() => toggleSort(col.id)} className="cursor-pointer inline-block break-words leading-tight align-middle">
                      {col.label} <span className={`ml-1 ${isActive ? "text-indigo-600" : "text-gray-300"}`}>{arrow}</span>
                    </span>
                    <span
                      onMouseDown={(e) => startResize(e, col.id)}
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`mercado-th-resize-${col.id}`}
                      title="Arrastrar para redimensionar"
                      className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-indigo-400/60 active:bg-indigo-500"
                    />
                  </th>
                );
              })}
              <th className={`text-center ${DENSITY_CELL_CLASS[density]} whitespace-nowrap sticky right-0 bg-gray-50`}>Acc.</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sortedItems.map(m => {
              // Iter163 · Highlight amarillo si fecha_presentacion < 7 días
              const fp = (m.fecha_presentacion || "").slice(0, 10);
              const days = fp
                ? Math.floor((new Date(fp) - new Date(todayISO)) / 86400000)
                : null;
              const soon = days !== null && days >= 0 && days <= 7;
              const past = days !== null && days < 0;
              const rowCls = soon
                ? "bg-amber-50 hover:bg-amber-100"
                : past
                  ? "bg-rose-50/40 hover:bg-rose-100/50"
                  : "hover:bg-gray-50";
              const rowBgSticky = soon ? "bg-amber-50" : past ? "bg-rose-50" : "bg-white";
              const stickyIdx = activeCols.findIndex(c => c.id === "titulo" || c.id === "objeto");
              return (
              <tr key={m.id} className={rowCls} data-testid={`mercado-row-${m.id}`}>
                {activeCols.map((col, idx) => {
                  const isSticky = idx === stickyIdx && stickyIdx !== -1;
                  return (
                    <td key={col.id}
                        className={`${DENSITY_CELL_CLASS[density]} text-${col.align} overflow-hidden ${isSticky ? `sticky left-0 z-20 ${rowBgSticky} shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]` : ""}`}
                        style={{ maxWidth: `${colWidths[col.id] || DEFAULT_WIDTHS[col.id] || 120}px` }}>
                      <div className="truncate">{renderCell(col.id, m)}</div>
                    </td>
                  );
                })}
                <td className={`${DENSITY_CELL_CLASS[density]} text-center whitespace-nowrap sticky right-0 bg-white`}>
                  <button onClick={()=>{ if (m._fromPrepres) { setEditingOferta(m); } else { setEditing(m); } }} title="Editar" className="text-indigo-600 hover:bg-indigo-50 p-1 rounded mr-1" data-testid={`mercado-edit-${m.id}`}><Pencil size={10}/></button>
                  {/* Feedback 2026-10 · retirado el botón "Nueva oferta" por fila
                      (confundía y duplicaba el botón verde de arriba, que ya
                      permite elegir una oportunidad o crear desde cero). */}
                  {m.oferta_comercial_id && (
                    <button
                      type="button"
                      onClick={() => navigate(`/ofertas-comercial?highlight=${m.oferta_comercial_id}`)}
                      title={`Abrir licitación · oferta ${m.oferta_comercial_id}`}
                      className="text-blue-700 hover:bg-blue-50 p-1 rounded mr-1 inline-flex items-center gap-0.5 font-bold"
                      data-testid={`mercado-open-oferta-${m.id}`}>
                      <ExternalLink size={10}/>
                    </button>
                  )}
                  {!m._fromPrepres && (
                    <button onClick={()=>del(m.id)} title="Eliminar" className="text-rose-500 hover:bg-rose-50 p-1 rounded" data-testid={`mercado-delete-${m.id}`}><Trash2 size={10}/></button>
                  )}
                </td>
              </tr>
              );
            })}
            {!sortedItems.length && (
              <tr><td colSpan={activeCols.length + 1} className="p-6 text-center text-gray-400">
                {viewMode === "resueltas" && (data?.kpis?.cerradas_count || 0) > 0 ? (
                  <div className="space-y-2" data-testid="mercado-resueltas-empty-hint">
                    <div className="text-gray-600 text-sm">
                      Las <b>{data.kpis.cerradas_count}</b> ofertas resueltas también se pueden filtrar aquí (Presentadas, No presentadas, Suspendidas, Anuladas).
                    </div>
                    <div className="flex flex-wrap justify-center gap-2">
                      <button type="button"
                         onClick={() => { setViewMode("strategic"); setFilters(f => ({ ...f, estado: "PRESENTADA" })); }}
                         className="text-xs font-semibold px-3 py-1.5 bg-green-100 hover:bg-green-200 text-green-800 rounded-lg border border-green-200"
                         data-testid="mercado-goto-presentadas">
                         Filtrar Presentadas ({data.kpis.PRESENTADA || 0})
                      </button>
                      <button type="button"
                         onClick={() => { setViewMode("strategic"); setFilters(f => ({ ...f, estado: "NO_PRESENTADA" })); }}
                         className="text-xs font-semibold px-3 py-1.5 bg-rose-100 hover:bg-rose-200 text-rose-800 rounded-lg border border-rose-200"
                         data-testid="mercado-goto-no-presentadas">
                         Filtrar No presentadas ({data.kpis.NO_PRESENTADA || 0})
                      </button>
                      <a href="/ofertas-comercial?tab=licitaciones"
                         className="text-xs font-semibold px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg border border-gray-200"
                         data-testid="mercado-goto-licitaciones">
                         → Abrir Licitaciones (tab)
                      </a>
                    </div>
                  </div>
                ) : viewMode === "strategic" && (data?.kpis?.strategic_count || 0) > 0 ? (
                  <div className="space-y-2" data-testid="mercado-strategic-empty-hint">
                    <div className="text-gray-600 text-sm">
                      Filtra el bloque estratégico aquí ({(data.kpis.PUBLICADA || 0)} publicadas · {(data.kpis.POR_DECIDIR || 0)} por decidir · {(data.kpis.ESTUDIO || 0)} en estudio · {(data.kpis.PRESENTADA || 0)} presentadas). Origen: Mercado + Licitaciones.
                    </div>
                    <div className="flex flex-wrap justify-center gap-2">
                      {(data.kpis.PUBLICADA || 0) > 0 && (
                        <button type="button"
                           onClick={() => { setViewMode("strategic"); setFilters(f => ({ ...f, estado: "PUBLICADA" })); }}
                           className="text-xs font-semibold px-3 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-800 rounded-lg border border-blue-200"
                           data-testid="mercado-strategic-empty-goto-publicadas">
                           Publicadas ({data.kpis.PUBLICADA})
                        </button>
                      )}
                      {(data.kpis.POR_DECIDIR || 0) > 0 && (
                        <button type="button"
                           onClick={() => { setViewMode("strategic"); setFilters(f => ({ ...f, estado: "POR_DECIDIR" })); }}
                           className="text-xs font-semibold px-3 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-800 rounded-lg border border-amber-200"
                           data-testid="mercado-strategic-empty-goto-pordecidir">
                           Por decidir ({data.kpis.POR_DECIDIR})
                        </button>
                      )}
                      {(data.kpis.ESTUDIO || 0) > 0 && (
                        <button type="button"
                           onClick={() => { setViewMode("strategic"); setFilters(f => ({ ...f, estado: "EN_ESTUDIO" })); }}
                           className="text-xs font-semibold px-3 py-1.5 bg-indigo-100 hover:bg-indigo-200 text-indigo-800 rounded-lg border border-indigo-200"
                           data-testid="mercado-strategic-empty-goto-estudio">
                           En estudio ({data.kpis.ESTUDIO})
                        </button>
                      )}
                    </div>
                  </div>
                ) : (activeFilters > 0
                    ? "Sin resultados con los filtros actuales"
                    : "Sin oportunidades · Pulsa 'Sincronizar ahora' para empezar")}
              </td></tr>
            )}
          </tbody>
        </table>
        {/* Iter179.8 · (retirada 2026-10-20 por feedback) La barra "N más en
            Licitaciones → Ver Publicadas/Por decidir/En estudio" duplicaba las
            tarjetas KPI de arriba, que ya filtran MERCADO con un clic. */}
      </div>

      {(showForm || editing) && catalog && (
        <MercadoForm
          initial={editing}
          catalog={catalog}
          onClose={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}

      {scoreResult && (
        <ScoreIAModal
          item={scoreResult.item}
          result={scoreResult.result}
          onClose={() => setScoreResult(null)}
          onPasarEstudio={() => { pasarAEstudio(scoreResult.item.id); setScoreResult(null); }}
          onRescore={async () => {
            setScoreResult(null);
            await scoreWithAI(scoreResult.item);
          }}
        />
      )}

      <MasterExcelImportModal open={showMasterImport} onClose={() => { setShowMasterImport(false); load(); }} />
      {showHistImport && <HistoricoImportModal onClose={() => setShowHistImport(false)} onImported={() => toast.success("Año anterior importado. Míralo en YTD Board → Comparativa interanual.")} />}
      {showConciliacion && <ConciliacionPresentadasModal onClose={() => setShowConciliacion(false)} />}
      <PlacspSyncModal        open={showPlacspSync}   onClose={() => setShowPlacspSync(false)} />
      {acceptingOpp && (
        <AcceptFromMercadoModal
          opportunity={acceptingOpp}
          onClose={() => setAcceptingOpp(null)}
          onAccepted={() => load()}
        />
      )}
      {/* Iter169 · Modal de motivos (sustituye window.prompt) */}
      {motivoModal && (
        <MotivoModal
          tipo={motivoModal.tipo}
          initial={motivoModal.current}
          onClose={() => setMotivoModal(null)}
          onSubmit={submitMotivo}
        />
      )}
      {/* Iter220 · Modal descarte con motivos preset + comentario */}
      <DescarteModal
        open={!!descarteModal}
        opp={descarteModal}
        onCancel={() => setDescarteModal(null)}
        onConfirm={confirmarDescarte}
      />
      {/* Iter185.1 · Modal picker de "Nueva oferta". Muestra oportunidades sin
          oferta_comercial_id (sin licitación vinculada) y al elegir una abre
          el AcceptFromMercadoModal habitual. */}
      {showNewOfferPicker && (
        <NewOfferPickerModal
          items={data?.items || []}
          onClose={() => setShowNewOfferPicker(false)}
          onPick={(opp) => {
            setShowNewOfferPicker(false);
            if (opp === null) {
              // Iter192 · Alta manual: abrir formulario en blanco
              setShowManualOfferForm(true);
            } else {
              setAcceptingOpp(opp);
            }
          }}
        />
      )}
      {showManualOfferForm && (
        <OfertaForm
          data={{}}
          onClose={() => setShowManualOfferForm(false)}
          onSaved={() => {
            setShowManualOfferForm(false);
            load();
          }}
        />
      )}
      {editingOferta && (
        <OfertaForm
          data={editingOferta}
          onClose={() => setEditingOferta(null)}
          onSaved={() => {
            setEditingOferta(null);
            load();
            toast.success("Oferta actualizada");
          }}
          onDeleted={() => {
            setEditingOferta(null);
            load();
          }}
        />
      )}
      </div>
  );
}

// Iter185.1 · Modal picker de oportunidades para el botón físico "Nueva oferta"
// en la toolbar de Mercado. Filtra las oportunidades sin `oferta_comercial_id`
// (aún no convertidas en licitación) y ofrece un buscador simple sobre título,
// expediente y cliente.
function NewOfferPickerModal({ items, onClose, onPick }) {
  const [q, setQ] = useState("");
  const disponibles = useMemo(() => {
    const raw = (items || []).filter(m => !m.oferta_comercial_id);
    const term = q.trim().toLowerCase();
    if (!term) return raw.slice(0, 50);
    return raw.filter(m => {
      const hay = `${m.titulo || ""} ${m.objeto_contrato || ""} ${m.objeto || ""} ${m.expediente || ""} ${m.cliente || ""} ${m.publicacion || ""}`.toLowerCase();
      return hay.includes(term);
    }).slice(0, 50);
  }, [items, q]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose} data-testid="mercado-new-oferta-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-emerald-50 to-white">
          <div>
            <h2 className="font-bold text-slate-800 text-sm">Nueva oferta desde Mercado</h2>
            <p className="text-[11px] text-slate-500">Elige la oportunidad de origen. Se prellenarán título, cliente, DG, importe y fecha.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-rose-600 p-1"><X size={18}/></button>
        </div>
        <div className="px-4 py-2 border-b bg-slate-50">
          <input
            type="text"
            autoFocus
            placeholder="Buscar por título, expediente, cliente..."
            value={q}
            onChange={e => setQ(e.target.value)}
            data-testid="mercado-new-oferta-search"
            className="w-full text-xs px-3 py-1.5 border border-slate-200 rounded focus:outline-none focus:border-emerald-500"
          />
        </div>
        {/* Iter192 · Alta manual desde cero (sin escoger opp de mercado) */}
        <div className="px-4 py-2 border-b bg-emerald-50/50">
          <button
            type="button"
            onClick={() => onPick(null)}
            data-testid="mercado-new-oferta-manual"
            className="w-full text-left rounded border border-emerald-300 bg-white px-3 py-2 hover:bg-emerald-50 transition flex items-center gap-2"
          >
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-600 text-white"><Plus size={13}/></span>
            <span>
              <div className="text-xs font-semibold text-emerald-800">Crear desde cero (alta manual)</div>
              <div className="text-[10px] text-emerald-700/80">Abre el formulario completo sin vincular a una oportunidad existente.</div>
            </span>
          </button>
        </div>
        <div className="overflow-auto flex-1">
          {disponibles.length === 0 && (
            <div className="p-8 text-center text-xs text-slate-500">
              {q ? "Sin resultados para tu búsqueda." : "No hay oportunidades disponibles sin oferta vinculada."}
            </div>
          )}
          {disponibles.map(m => (
            <button
              key={m.id}
              type="button"
              onClick={() => onPick(m)}
              data-testid={`mercado-new-oferta-pick-${m.id}`}
              className="w-full text-left px-4 py-2.5 border-b border-slate-100 hover:bg-emerald-50 transition"
            >
              <div className="text-xs font-semibold text-slate-800 line-clamp-1">{m.titulo || m.objeto_contrato || m.objeto || "(sin título)"}</div>
              <div className="text-[10px] text-slate-500 flex flex-wrap gap-x-3 mt-0.5">
                <span>{m.publicacion || m.cliente || "—"}</span>
                <span>Exp: {m.expediente || "—"}</span>
                <span>DG: {m.direccion_general || "—"}</span>
                {m.presupuesto > 0 && (
                  <span className="font-mono">{(Number(m.presupuesto)/1_000_000).toFixed(2)} M€</span>
                )}
                {m.fecha_presentacion && <span>Presenta: {String(m.fecha_presentacion).slice(0,10)}</span>}
              </div>
            </button>
          ))}
        </div>
        <div className="px-4 py-2 border-t bg-slate-50 text-[11px] text-slate-500 flex items-center justify-between">
          <span>Mostrando <b>{disponibles.length}</b> {q ? "resultado(s)" : "oportunidad(es) disponible(s)"}</span>
          <span className="text-slate-400">Consejo: filtra en la tabla de Mercado y volverán a aparecer aquí</span>
        </div>
      </div>
    </div>
  );
}

// Iter169 · Modal de motivos para NO_ESTRATEGICA (checkboxes) y NO_PRESENTADA (radio)
function MotivoModal({ tipo, initial, onClose, onSubmit }) {
  const [selected, setSelected] = useState(initial || []);
  const isNE = tipo === "NE";
  const OPTIONS_NE = [
    { value: "escaso_importe", label: "Escaso importe (< 50 000 €)" },
    { value: "corta_duracion", label: "Corta duración (≤ 12 meses)" },
    { value: "fuera_zona", label: "Fuera de zona activa Valoriza" },
  ];
  const OPTIONS_NP = [
    { value: "No Chance", label: "No Chance" },
    { value: "Non-strategic", label: "No estratégico" },
    { value: "Profitability", label: "No económicamente viable" },
    { value: "Workload (Resources)", label: "Falta de recursos" },
    { value: "Solvency", label: "Falta de solvencia técnica" },
  ];
  const OPTIONS = isNE ? OPTIONS_NE : OPTIONS_NP;

  const toggle = (v) => {
    if (isNE) {
      setSelected(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]);
    } else {
      setSelected([v]);
    }
  };
  const canSubmit = selected.length > 0;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="mercado-motivo-modal">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
        <div className="p-5 border-b flex items-center justify-between">
          <h2 className="text-base font-bold text-gray-900">
            {isNE ? "Motivo · No estratégica" : "Motivo · No presentada"}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid="motivo-modal-close">✕</button>
        </div>
        <div className="p-5 space-y-2">
          <p className="text-xs text-gray-500 mb-3">
            {isNE ? "Selecciona uno o varios motivos (obligatorio)." : "Selecciona el motivo principal."}
          </p>
          {OPTIONS.map(opt => (
            <label key={opt.value}
                   className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition ${
                     selected.includes(opt.value) ? "bg-emerald-50 border-emerald-300" : "border-gray-200 hover:bg-gray-50"
                   }`}
                   data-testid={`motivo-opt-${opt.value.replace(/\W+/g, '_')}`}>
              <input
                type={isNE ? "checkbox" : "radio"}
                checked={selected.includes(opt.value)}
                onChange={() => toggle(opt.value)}
                className="accent-emerald-600"
              />
              <span className="text-sm">{opt.label}</span>
            </label>
          ))}
        </div>
        <div className="p-4 border-t flex justify-end gap-2 bg-gray-50 rounded-b-2xl">
          <button onClick={onClose} className="text-xs font-semibold px-4 py-2 border rounded-lg hover:bg-gray-100">Cancelar</button>
          <button onClick={() => onSubmit(selected)} disabled={!canSubmit}
                  data-testid="motivo-modal-submit"
                  className="text-xs font-semibold px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg disabled:opacity-50">
            Confirmar
          </button>
        </div>
      </div>
    </div>
  );
}

function ScoreIAModal({ item, result, onClose, onPasarEstudio, onRescore }) {
  const rec = result.recommendation;
  const headerColor = rec === "GO" ? "from-emerald-500 to-green-600"
                    : rec === "NO-GO" ? "from-rose-500 to-red-600"
                    : "from-amber-500 to-orange-600";
  const recIcon = rec === "GO" ? <ThumbsUp size={18}/>
                 : rec === "NO-GO" ? <ThumbsDown size={18}/>
                 : <HelpCircle size={18}/>;
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState(null);

  useEffect(() => {
    if (!showHistory || history !== null) return;
    axios.get(`${API}/mercado/opportunities/${item.id}/score-history`)
      .then(r => setHistory(r.data.history || []))
      .catch(() => setHistory([]));
  }, [showHistory, item.id, history]);
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e=>e.stopPropagation()}>
        <div className={`bg-gradient-to-r ${headerColor} p-5 text-white`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 rounded-lg p-2">{recIcon}</div>
              <div>
                <h3 className="text-lg font-bold">Recomendación Pando · {rec}</h3>
                <p className="text-xs opacity-90">Claude Sonnet · confianza {result.confidence ?? "—"}% · score {result.score}/100</p>
              </div>
            </div>
            <button onClick={onClose} className="text-white/70 hover:text-white"><X size={18}/></button>
          </div>
          <p className="mt-4 text-sm opacity-95">{result.summary}</p>
        </div>

        <div className="p-5 space-y-4 text-xs">
          {item.ai_stale && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg px-3 py-2 flex items-start gap-2" data-testid="mercado-ai-stale-banner">
              <AlertTriangle size={14} className="text-amber-600 mt-0.5 shrink-0" />
              <p className="text-[11px] text-amber-800">
                <b>Análisis obsoleto.</b> {item.ai_stale_reason || "Se generó con la regla antigua que trataba un incumbente desconocido como ventaja. Desconocer al incumbente es un dato ausente (tarea de investigación), nunca una ventaja."} Regenéralo antes de usarlo para decidir.
              </p>
            </div>
          )}
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-[10px] uppercase text-gray-500 font-semibold mb-1">Licitación analizada</p>
            <p className="font-semibold text-sm text-gray-800">{item.cliente}</p>
            <p className="text-gray-600 line-clamp-2">{item.objeto_contrato}</p>
            <p className="text-[10px] text-gray-500 mt-1">
              {item.cpv && `CPV ${item.cpv} · `}
              {item.direccion_general && `${item.direccion_general} / `}
              {item.actividad_informe_comercial || "—"}
              {item.presupuesto && ` · ${new Intl.NumberFormat("es-ES",{style:"currency",currency:"EUR",maximumFractionDigits:0}).format(item.presupuesto)}`}
            </p>
          </div>

          {result.strengths?.length > 0 && (
            <div>
              <h4 className="text-[11px] font-bold text-emerald-700 uppercase mb-1.5 flex items-center gap-1"><ThumbsUp size={11}/> Por qué SÍ</h4>
              <ul className="space-y-1 pl-4">
                {result.strengths.map((s, i) => <li key={i} className="list-disc text-gray-700">{s}</li>)}
              </ul>
            </div>
          )}

          {result.risks?.length > 0 && (
            <div>
              <h4 className="text-[11px] font-bold text-rose-700 uppercase mb-1.5 flex items-center gap-1"><AlertTriangle size={11}/> Riesgos / Red flags</h4>
              <ul className="space-y-1 pl-4">
                {result.risks.map((s, i) => <li key={i} className="list-disc text-gray-700">{s}</li>)}
              </ul>
            </div>
          )}

          {result.suggested_strategy && (
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
              <p className="text-[10px] uppercase font-bold text-indigo-700">Estrategia sugerida</p>
              <p className="text-indigo-900 font-semibold mt-0.5">{result.suggested_strategy}</p>
            </div>
          )}

          {/* iter 128 · Historia del scoring Pando */}
          <div className="border-t border-slate-100 pt-3" data-testid="scoreia-history-section">
            <button
              onClick={() => setShowHistory(v => !v)}
              className="text-[11px] font-bold text-slate-700 hover:text-indigo-700 flex items-center gap-1"
              data-testid="scoreia-history-toggle">
              <TrendingUp size={12}/> {showHistory ? "Ocultar historia" : "Ver historia del score Pando"}
            </button>
            {showHistory && (
              <div className="mt-3" data-testid="scoreia-history-content">
                {!history && <div className="text-xs text-slate-400 italic">Cargando…</div>}
                {history && history.length === 0 && (
                  <div className="text-xs text-slate-400 italic">Sin historial (aún no se ha rescoreado).</div>
                )}
                {history && history.length > 0 && (
                  <ScoreHistoryChart history={history}/>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 p-4 border-t bg-gray-50">
          <button onClick={onRescore} className="text-xs text-gray-600 hover:text-gray-900 flex items-center gap-1">
            <RefreshCw size={11}/> Reanalizar
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} className="text-xs border border-gray-200 px-3 py-1.5 rounded hover:bg-white">Cerrar</button>
            {rec !== "NO-GO" && item.estado_licitacion !== "ESTUDIO" && (
              <button
                onClick={onPasarEstudio}
                className="bg-[#007934] hover:bg-[#006528] text-white text-xs font-semibold px-3 py-1.5 rounded flex items-center gap-1"
                data-testid="scoreia-pasar-estudio">
                <ArrowRight size={11}/> Pasar a ESTUDIO
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function KPI({ label, value, importe, color, testid, hint, onClick, sub }) {
  const clickable = typeof onClick === "function";
  const importeStr = importe != null && importe > 0
    ? new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", notation: "compact", maximumFractionDigits: 1 }).format(importe)
    : null;
  return (
    <div
      className={`border rounded-lg px-2 py-1.5 text-center ${color} ${clickable ? "cursor-pointer hover:shadow-sm hover:brightness-95 transition" : ""}`}
      title={hint || label}
      data-testid={testid}
      onClick={clickable ? onClick : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}>
      <div className="text-[9px] uppercase opacity-70">{label}</div>
      <div className="text-base font-bold leading-tight">{value ?? 0}</div>
      {sub && <div className="text-[9px] font-medium opacity-80 mt-0.5 leading-tight" data-testid={testid ? `${testid}-sub` : undefined}>{sub}</div>}
      {importeStr && <div className="text-[9px] font-mono opacity-70 mt-0.5">{importeStr}</div>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Iter147 · EnrichmentTimeline: pipeline visual "PLACSP → geo → CPV → DG →
// Win Score" con marcas ✓/✗ y fechas por etapa.
// ═══════════════════════════════════════════════════════════════════════════
function EnrichmentTimeline({ m }) {
  const [open, setOpen] = useState(false);
  const shortDate = (iso) => (iso || "").slice(0, 10);
  const stages = [
    { id: "placsp", label: "PLACSP",  done: !!(m.placsp_enriched_at || m.origen === "placsp" || m.created_at),
      at: m.placsp_enriched_at || m.created_at },
    { id: "geo",    label: "Geo",     done: !!m.provincia, at: m.provincia ? (m.updated_at || m.created_at) : null },
    { id: "cpv",    label: "CPV",     done: !!m.cpv,       at: m.cpv ? (m.updated_at || m.created_at) : null },
    { id: "dg",     label: "DG",      done: !!m.direccion_general, at: m.dg_classified_at },
    { id: "win",    label: "Win",     done: typeof m.ai_win_score === "number", at: m.ai_win_scored_at },
  ];
  const complete = stages.filter(s => s.done).length;
  return (
    <div className="relative inline-block" data-testid={`enrich-timeline-${m.id}`}>
      <button
        onClick={() => setOpen(o => !o)}
        title={`Enriquecimiento ${complete}/5 · click para detalles`}
        className="inline-flex items-center gap-0.5 hover:bg-slate-100 rounded px-1 py-0.5"
      >
        {stages.map((s, i) => (
          <React.Fragment key={s.id}>
            <span
              title={`${s.label} · ${s.done ? "✓ " + shortDate(s.at) : "pendiente"}`}
              className={`inline-block w-2 h-2 rounded-full ${
                s.done ? (s.id === "win" ? "bg-emerald-500" : "bg-indigo-500") : "bg-slate-300"
              }`} />
            {i < stages.length - 1 && (
              <span className={`inline-block w-1.5 h-px ${stages[i + 1].done ? "bg-indigo-300" : "bg-slate-200"}`} />
            )}
          </React.Fragment>
        ))}
        <span className="text-[9px] font-semibold text-slate-500 ml-1">{complete}/5</span>
      </button>
      {open && (
        <div
          onClick={(e) => e.stopPropagation()}
          className="absolute z-40 top-full right-0 mt-1 w-56 bg-white border border-slate-200 rounded-lg shadow-xl p-2 text-left"
          data-testid={`enrich-timeline-detail-${m.id}`}
        >
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] font-bold text-slate-800 uppercase tracking-wider">Enriquecimiento</span>
            <button onClick={() => setOpen(false)} className="text-[14px] text-slate-400 hover:text-slate-700 leading-none">×</button>
          </div>
          <div className="space-y-1">
            {stages.map(s => (
              <div key={s.id} className="flex items-center gap-2 text-[10px]">
                <span className={`inline-block w-2 h-2 rounded-full ${s.done ? "bg-emerald-500" : "bg-slate-300"}`}></span>
                <span className="font-mono text-slate-700 w-14">{s.label}</span>
                <span className={s.done ? "text-emerald-700 font-semibold" : "text-slate-400"}>
                  {s.done ? "✓" : "✗"}
                </span>
                <span className="text-slate-400 ml-auto font-mono">{s.at ? shortDate(s.at) : "—"}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Iter181/233 · Duplicados cross-source (PLACSP+TED, `external_opportunities`).
// Distinto de "duplicados comerciales" (mismo expediente en ofertas_comercial,
// que se gestiona desde Licitaciones). Feedback usuario: "el botón que salga
// solo en mercado" — antes vivía (por error) en el header de Licitaciones.
// ═══════════════════════════════════════════════════════════════════════════
function CrossSourceDupesBadge() {
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/ofertas-comercial/health-stats`)
      .then(r => setCount(r.data?.cross_source_dupes || 0))
      .catch(() => setCount(0));
  }, []);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    axios.get(`${API}/ofertas-comercial/dupes-detail`)
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(() => toast.error("No se pudo cargar el detalle de duplicados"));
    return () => { cancelled = true; };
  }, [open]);

  if (count === 0) return null;
  const items = data?.items || [];

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="bg-amber-50 hover:bg-amber-100 border border-amber-300 text-amber-800 text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1.5 shadow-sm transition"
        data-testid="hs-dupes"
        title="Ver duplicados cross-source (PLACSP+TED) excluidos de emails">
        <AlertTriangle size={12} className="text-amber-600"/>
        {count} dups cross-source
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="dupes-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-amber-50 to-white">
              <div>
                <h2 className="font-bold text-slate-800">Duplicados cross-source detectados</h2>
                <p className="text-[11px] text-slate-500">Oportunidades marcadas como duplicadas y excluidas de emails</p>
              </div>
              <button onClick={() => { setOpen(false); setData(null); }} className="text-slate-400 hover:text-rose-600 p-1" data-testid="dupes-close">
                <X size={18}/>
              </button>
            </div>
            <div className="overflow-auto flex-1 p-3">
              {!data && <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Cargando…</div>}
              {data && items.length === 0 && (
                <div className="text-sm text-slate-500 text-center py-8">No hay duplicados registrados actualmente.</div>
              )}
              {items.length > 0 && (
                <table className="w-full text-xs">
                  <thead className="bg-slate-100 text-slate-700 sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-1.5">Título</th>
                      <th className="text-left px-2 py-1.5">Fuente</th>
                      <th className="text-left px-2 py-1.5">Organismo</th>
                      <th className="text-right px-2 py-1.5">Importe</th>
                      <th className="text-left px-2 py-1.5">Duplicado de</th>
                      <th className="text-center px-2 py-1.5">Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((d, i) => (
                      <tr key={i} className="border-b hover:bg-slate-50">
                        <td className="px-2 py-1.5">{d.title || d.external_id}</td>
                        <td className="px-2 py-1.5"><span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200">{d.source || "—"}</span></td>
                        <td className="px-2 py-1.5 text-slate-600">{d.organism || "—"}</td>
                        <td className="px-2 py-1.5 text-right font-mono">
                          {d.value_eur != null ? `${(Number(d.value_eur)/1_000_000).toFixed(2)} M€` : "—"}
                        </td>
                        <td className="px-2 py-1.5 text-slate-500">{d.duplicated_of || "—"}</td>
                        <td className="px-2 py-1.5 text-center">
                          {d.url && (
                            <a href={d.url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">
                              <ExternalLink size={11}/> Origen
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="px-4 py-2 border-t bg-slate-50 text-[11px] text-slate-500">
              Total: <b className="font-mono">{data?.count ?? count}</b> duplicados
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Iter195/231 · LimboBadge: ofertas en POR DECIDIR / BORRADOR / PUBLICADA
// llevan >7 días sin decisión estratégica. Antes vivía en el header de
// Licitaciones y era sólo de lectura ("no me deja actuar sobre ellas, no
// las encontramos en Mercado"). Ahora vive en Mercado y permite: (a)
// "Marcar en estudio" (retoma la decisión, resetea el reloj), (b) "Descartar"
// (mismo flujo/modal que el resto de Mercado) — ambas acciones escriben en
// `ofertas_comercial.estado_licitacion`, el mismo campo que leen Licitaciones/
// KPIs/Agenda, así que la decisión se propaga sola por todo el pipeline.
// ═══════════════════════════════════════════════════════════════════════════
function LimboBadge({ onActioned }) {
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [descarteTarget, setDescarteTarget] = useState(null); // item | null
  const navigate = useNavigate();

  const abrirExpediente = (it) => {
    if (!it?.id) return;
    setOpen(false);
    navigate(`/ofertas-comercial?tab=list&open=${it.id}`);
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/ofertas-comercial/alerts/stagnation`, { params: { days: 7 } });
      setCount(r.data?.count || 0);
      setItems(r.data?.items || []);
    } catch (_) { /* silent */ } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const applyDecision = async (item, patch, successMsg) => {
    setBusyId(item.id);
    try {
      await axios.put(`${API}/ofertas-comercial/${item.id}`, patch);
      setItems(prev => prev.filter(i => i.id !== item.id));
      setCount(c => Math.max(0, c - 1));
      toast.success(successMsg);
      onActioned && onActioned(); // refresca la tabla de Mercado al instante
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo actualizar la oferta");
    } finally {
      setBusyId(null);
    }
  };

  const marcarEnEstudio = (item) => applyDecision(
    item, { estado_licitacion: "ESTUDIO" },
    "Marcada en estudio · vuelve a la mesa de decisión"
  );

  const confirmarDescarte = ({ motivo_descarte, comment }) => {
    const item = descarteTarget;
    setDescarteTarget(null);
    if (!item) return;
    applyDecision(
      item,
      { estado_licitacion: "NO PRESENTADA", motivo_descarte, motivo_descarte_comment: comment },
      "Descartada · pasa a 'No presentadas'"
    );
  };

  if (count === 0 && !loading) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="limbo-badge"
        title="Ofertas en limbo estratégico > 7 días sin decisión · click para actuar sobre ellas"
        className="bg-amber-50 hover:bg-amber-100 border border-amber-300 text-amber-800 text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1.5 shadow-sm transition">
        <AlertTriangle size={12} className="text-amber-600"/>
        <span data-testid="limbo-badge-count">{count}</span> en limbo
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setOpen(false)}
             data-testid="limbo-modal">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col"
               onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-amber-100 bg-amber-50">
              <div>
                <h3 className="text-sm font-bold text-amber-900 flex items-center gap-2">
                  <AlertTriangle size={16}/> Estancamiento estratégico · {count} ofertas
                </h3>
                <p className="text-[11px] text-amber-800 mt-0.5">
                  Llevan &gt; 7 días en POR DECIDIR / BORRADOR / PUBLICADA sin decidir si son estratégicas o descartables. Decide aquí — se refleja al instante en Licitaciones, KPIs y Agenda.
                </p>
              </div>
              <button onClick={() => setOpen(false)} data-testid="limbo-modal-close"
                      className="text-amber-700 hover:text-amber-900"><X size={18}/></button>
            </div>
            <div className="overflow-y-auto p-4 space-y-2">
              {loading && <div className="text-center text-gray-400 text-xs py-6"><Loader2 className="animate-spin inline mr-1" size={12}/> Cargando…</div>}
              {!loading && items.length === 0 && (
                <p className="text-center text-gray-400 text-xs py-6">Sin ofertas en limbo 🎉</p>
              )}
              {items.map(it => (
                <div key={it.id} className="border border-gray-200 rounded-lg p-3 hover:border-amber-300 transition"
                     data-testid={`limbo-item-${it.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-gray-900 truncate" title={it.cliente}>{it.cliente || "—"}</div>
                      <div className="text-[11px] text-gray-500 mt-0.5 line-clamp-1" title={it.objeto_contrato}>{it.objeto_contrato}</div>
                      <div className="flex items-center gap-2 mt-1.5 text-[10px]">
                        <span className="bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded font-semibold">{it.estado_licitacion}</span>
                        {it.actividad_comercial && <span className="text-gray-500">{it.actividad_comercial}</span>}
                        {it.responsables && <span className="text-gray-500">· {it.responsables}</span>}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-lg font-bold text-red-600" data-testid={`limbo-item-days-${it.id}`}>{it.dias_en_limbo}d</div>
                      <div className="text-[10px] text-gray-400">en limbo</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-2.5 pt-2 border-t border-gray-100">
                    <button
                      onClick={() => abrirExpediente(it)}
                      data-testid={`limbo-item-abrir-${it.id}`}
                      title="Abrir el expediente de esta oferta"
                      className="text-[11px] font-semibold px-2.5 py-1 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 flex items-center gap-1">
                      <ExternalLink size={10}/> Abrir expediente
                    </button>
                    <button
                      onClick={() => marcarEnEstudio(it)}
                      disabled={busyId === it.id}
                      data-testid={`limbo-item-estudio-${it.id}`}
                      title="Retoma la decisión — pasa a ESTUDIO y sale del limbo"
                      className="text-[11px] font-semibold px-2.5 py-1 rounded-md border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 disabled:opacity-50 flex items-center gap-1">
                      {busyId === it.id ? <Loader2 size={10} className="animate-spin"/> : <Check size={10}/>} Marcar en estudio
                    </button>
                    <button
                      onClick={() => setDescarteTarget(it)}
                      disabled={busyId === it.id}
                      data-testid={`limbo-item-descartar-${it.id}`}
                      title="Descartar — pasa a NO PRESENTADA en todo el pipeline"
                      className="text-[11px] font-semibold px-2.5 py-1 rounded-md border border-red-300 bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50 flex items-center gap-1">
                      <X size={10}/> Descartar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      <DescarteModal
        open={!!descarteTarget}
        opp={descarteTarget ? { ...descarteTarget, objeto: descarteTarget.objeto_contrato } : null}
        onCancel={() => setDescarteTarget(null)}
        onConfirm={confirmarDescarte}
      />
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SU07 (valoriza-verificacion-ola3-feedback-ofertas-su-2026-10-13.md) ·
// "Mover accesos de duplicados, posibles vínculos y motivos pendientes a un
// bloque «Calidad y revisión» dentro de Mercado." Agrupa aquí los 3 accesos
// que hoy viven dispersos (Admin/por-oferta) y que son reales y verificados
// en el código: duplicados comerciales, motivos de "No presentada" sin
// clasificar y candidatos de vínculo con anuncio oficial PLACSP. Los
// duplicados cross-source y las licitaciones en limbo YA viven en la
// cabecera de Mercado (badges arriba) — no se duplican aquí.
// ═══════════════════════════════════════════════════════════════════════════
// SU09 (valoriza-crm-comercial-unificado-auditoria-2026-10-13) · El bloque
// "Calidad y revisión" de Mercado ya NO cuenta por su cuenta: lee la porción
// COMERCIAL del inventario único de calidad (misma fuente que Admin y la
// página Calidad), proyectada sobre la línea de vida canónica
// detectado→revisión→confirmado/falso positivo/resuelto. Así las tres
// pantallas muestran exactamente el mismo número del mismo problema.
const _FASES_CAL = [
  { k: "detectado",      label: "Detectado",      icon: Inbox,        cls: "bg-red-50 text-red-700 border-red-200" },
  { k: "en_revision",    label: "En revisión",    icon: Eye,          cls: "bg-amber-50 text-amber-700 border-amber-200" },
  { k: "confirmado",     label: "Confirmado",     icon: AlertTriangle,cls: "bg-blue-50 text-blue-700 border-blue-200" },
  { k: "falso_positivo", label: "Falso positivo", icon: XCircle,      cls: "bg-gray-50 text-gray-500 border-gray-200" },
  { k: "resuelto",       label: "Resuelto",       icon: CheckCircle2, cls: "bg-green-50 text-green-700 border-green-200" },
];

function CalidadRevisionBlock() {
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [vinculosPlacsp, setVinculosPlacsp] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/quality-incidents/inventario`, { params: { origen: "comercial" } })
      .then(r => setInv(r.data)).catch(() => setInv(null));
    // PLACSP sigue siendo un flujo propio (SU08, rechazo permanente por oferta):
    // se muestra aparte como acceso, nunca se cuenta dentro del inventario.
    axios.get(`${API}/ofertas-comercial/merge-suggestions`, { params: { limit: 1 } })
      .then(r => setVinculosPlacsp(r.data?.count ?? 0)).catch(() => setVinculosPlacsp(null));
  }, []);

  const comercial = inv?.por_origen?.comercial || null;
  const abiertas = comercial?.abiertas ?? null;

  const irACalidad = (fase) => {
    const qs = new URLSearchParams({ origen: "comercial" });
    if (fase) qs.set("fase", fase);
    navigate(`/calidad?${qs.toString()}`);
  };

  return (
    <div className="border border-slate-200 rounded-lg bg-slate-50/60" data-testid="mercado-calidad-revision-block">
      <button type="button" onClick={() => setOpen(o => !o)}
              className="w-full flex items-center justify-between px-3 py-2 text-left" data-testid="mercado-calidad-revision-toggle">
        <span className="text-xs font-bold text-slate-700 uppercase tracking-wide flex items-center gap-1.5">
          <ClipboardCheck size={13}/> Calidad y revisión
          {abiertas > 0 && <span className="text-[10px] bg-slate-700 text-white px-1.5 py-0.5 rounded-full font-bold">{abiertas}</span>}
        </span>
        {open ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2.5">
          <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
            <Layers size={11}/> Inventario único de calidad · porción <b>comercial</b>
            {inv?.ultima_sync
              ? <span className="text-slate-400">· sincronizado {new Date(inv.ultima_sync).toLocaleDateString("es-ES")}</span>
              : <span className="text-amber-600">· detectores aún sin sincronizar</span>}
          </div>

          {/* Línea de vida canónica compartida con Admin y Calidad */}
          <div className="grid grid-cols-5 gap-1.5" data-testid="mercado-calidad-fase-strip">
            {_FASES_CAL.map(f => {
              const Icon = f.icon;
              const count = comercial?.[f.k] ?? 0;
              return (
                <button key={f.k} type="button" onClick={() => irACalidad(f.k)}
                        data-testid={`mercado-calidad-fase-${f.k}`}
                        className={`text-left border rounded-lg px-2 py-1.5 transition hover:brightness-95 ${f.cls}`}>
                  <div className="flex items-center gap-1 text-[9px] font-semibold uppercase tracking-tight"><Icon size={10}/> {f.label}</div>
                  <div className="text-base font-bold mt-0.5">{comercial === null ? "—" : count}</div>
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-between gap-2 flex-wrap">
            <button type="button" onClick={() => irACalidad("")} data-testid="mercado-calidad-ver-inventario"
                    className="text-[11px] font-semibold text-[#007934] hover:underline inline-flex items-center gap-1">
              Ver inventario completo de calidad comercial <ArrowRight size={11}/>
            </button>
            {comercial && (comercial.sin_responsable > 0) && (
              <span className="text-[10px] text-amber-600"><b>{comercial.sin_responsable}</b> abiertas sin responsable</span>
            )}
          </div>

          {/* PLACSP: flujo aparte (SU08), acceso desde cada oferta, no se
              suma dentro del inventario para no duplicar lifecycle. */}
          <div className="flex items-center gap-2 text-[11px] text-slate-600 border-t border-slate-200 pt-2" data-testid="mercado-calidad-vinculos-placsp">
            <Link2 size={12} className="text-slate-400"/>
            <span>Vincular con anuncio PLACSP:</span>
            <b className="text-amber-700">{vinculosPlacsp ?? "—"}</b>
            <span className="text-slate-400">ofertas con candidato pendiente · se revisa desde cada oferta (flujo aparte)</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Iter142 · SinDGCard: contador de oportunidades sin `direccion_general` +
// botón Auto-clasificar (CPV + Claude Haiku). Sólo admin/offer_manager pueden
// disparar la clasificación; el contador es visible para todos.
// ═══════════════════════════════════════════════════════════════════════════
function SinDGCard({ testid = "mercado-sin-dg", role = "", onDrillDown }) {
  const [count, setCount] = useState(null);
  const [total, setTotal] = useState(null);
  const [running, setRunning] = useState(false);
  const [phase, setPhase] = useState("");

  const load = async () => {
    try {
      const r = await axios.get(`${API}/mercado/admin/unclassified-count`);
      setCount(r.data.unresolved);
      setTotal(r.data.total);
    } catch { /* no-op */ }
  };
  useEffect(() => { load(); }, []);

  const canRun = ["admin", "offer_manager"].includes(role);

  const runAutoClassify = async () => {
    if (!canRun || running) return;
    setRunning(true);
    setPhase("CPV…");
    let iterations = 0;
    let lastUnresolved = count || 0;
    let consecutiveErrors = 0;
    try {
      // Bucle de lotes 10 con Pando. En producción cada lote tarda ~15-30s.
      // Máx 30 iteraciones (~300 docs) o hasta que no progresa.
      while (iterations < 30) {
        iterations += 1;
        setPhase(`Lote ${iterations} · Claude Haiku`);
        try {
          const r = await axios.post(`${API}/mercado/admin/classify-dg?limit=10&use_ai=true`, null, { timeout: 90000 });
          const cpvOK = r.data?.classified || 0;
          const aiOK  = r.data?.ai?.classified || 0;
          consecutiveErrors = 0;
          const cntR = await axios.get(`${API}/mercado/admin/unclassified-count`);
          setCount(cntR.data.unresolved);
          setTotal(cntR.data.total);
          toast.success(`Lote ${iterations}: +${cpvOK} CPV · +${aiOK} Pando · quedan ${cntR.data.unresolved}`);
          if (cpvOK + aiOK === 0) break;
          if (cntR.data.unresolved >= lastUnresolved) break;
          lastUnresolved = cntR.data.unresolved;
        } catch (batchErr) {
          consecutiveErrors += 1;
          const msg = batchErr.response?.status === 524
            ? "Timeout del proxy (producción). Reintento…"
            : (batchErr.message || "");
          if (consecutiveErrors >= 3) {
            toast.error(`Auto-clasificar detenido: ${consecutiveErrors} errores seguidos. ${msg}`);
            break;
          }
          toast.warning(`Lote ${iterations} falló (${msg}). Reintento en 5s…`);
          await new Promise(res => setTimeout(res, 5000));
        }
      }
      try { window.dispatchEvent(new CustomEvent("mercado-sync-completed")); } catch { /* no-op */ }
    } catch (e) {
      toast.error("Error auto-clasificando: " + (e.response?.data?.detail || e.message));
    } finally {
      setRunning(false);
      setPhase("");
    }
  };

  const label = count === null ? "…" : count;
  const totalLabel = total === null ? "" : ` / ${total}`;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onDrillDown && onDrillDown()}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onDrillDown && onDrillDown(); }}
      className={`border rounded-lg px-2 py-1.5 text-center transition hover:shadow-md hover:scale-[1.02] cursor-pointer ${count > 0 ? "bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100" : "bg-slate-50 text-slate-500 border-slate-200"}`}
      data-testid={testid}
      title={count > 0 ? `${count} oportunidades sin DG asignada · click para filtrar el listado por estas` : "Todas las oportunidades están clasificadas"}
    >
      <div className="text-[9px] uppercase opacity-70 flex items-center gap-1 justify-center">
        <Tag size={9}/> Sin DG <span className="text-orange-400">↓</span>
      </div>
      <div className="text-base font-bold" data-testid={`${testid}-count`}>{label}<span className="text-[9px] opacity-60 font-normal">{totalLabel}</span></div>
      {canRun && count > 0 && (
        <button
          onClick={(e) => { e.stopPropagation(); runAutoClassify(); }}
          disabled={running}
          data-testid={`${testid}-classify-btn`}
          title="Clasifica automáticamente los docs sin DG usando el mapa CPV + Claude Haiku"
          className="mt-1 text-[9px] font-semibold bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white px-1.5 py-0.5 rounded flex items-center gap-0.5 mx-auto">
          {running ? <Loader2 size={8} className="animate-spin"/> : <Sparkles size={8}/>}
          {running ? phase || "…" : "Auto-clasificar"}
        </button>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Iter 129 · MiniSparkline · 40×12 inline en filas Mercado (score trend)
// ═══════════════════════════════════════════════════════════════════════════
function MiniSparkline({ data, onClick, testId }) {
  if (!data || data.length < 2) return null;
  const scores = data.map(d => d.score ?? 0);
  const minS = Math.min(...scores, 0);
  const maxS = Math.max(...scores, 100);
  const range = Math.max(maxS - minS, 1);
  const w = 40, h = 12;
  const points = scores.map((s, i) => {
    const x = (i / (scores.length - 1)) * w;
    const y = h - ((s - minS) / range) * (h - 2) - 1;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const delta = scores[scores.length - 1] - scores[0];
  const stroke = delta > 0 ? "#059669" : delta < 0 ? "#e11d48" : "#64748b";
  const lastPoint = points.split(" ").pop().split(",");

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      title={`Evolución score Pando: ${scores[0]} → ${scores[scores.length-1]} (${delta >= 0 ? "+" : ""}${delta} pts en ${data.length} rescores)`}
      className="inline-flex items-center justify-center hover:bg-slate-50 rounded p-0.5">
      <svg width={w} height={h}>
        <polyline
          fill="none"
          stroke={stroke}
          strokeWidth="1.4"
          strokeLinejoin="round"
          strokeLinecap="round"
          points={points}/>
        {lastPoint && (
          <circle cx={lastPoint[0]} cy={lastPoint[1]} r="1.4" fill={stroke}/>
        )}
      </svg>
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Iter 128 · ScoreHistoryChart · sparkline evolución score Pando + confidence
// ═══════════════════════════════════════════════════════════════════════════
function ScoreHistoryChart({ history }) {
  const data = history.map((h, i) => ({
    idx:            i + 1,
    at:             (h.at || "").slice(0, 10),
    score:          h.score ?? null,
    confidence:     h.confidence ?? null,
    recommendation: h.recommendation || "",
  }));
  const last = data[data.length - 1] || {};
  const first = data[0] || {};
  const delta = (last.score ?? 0) - (first.score ?? 0);
  const trendCls = delta > 0 ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                  : delta < 0 ? "text-rose-700   bg-rose-50   border-rose-200"
                  :             "text-slate-600  bg-slate-50  border-slate-200";
  return (
    <div className="bg-slate-50 rounded-lg border border-slate-200 p-3">
      <div className="flex items-center justify-between mb-2 text-[11px]">
        <span className="text-slate-600 font-semibold">
          {data.length} rescore{data.length === 1 ? "" : "s"} · {first.at} → {last.at}
        </span>
        <span className={`px-2 py-0.5 rounded font-bold border ${trendCls}`} data-testid="score-history-delta">
          Δ {delta >= 0 ? "+" : ""}{delta} pts
        </span>
      </div>
      <div style={{ width: "100%", height: 140 }} data-testid="score-history-chart">
        <ResponsiveContainer>
          <LineChart data={data} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
            <XAxis
              dataKey="idx"
              tick={{ fontSize: 9, fill: "#64748b" }}
              tickFormatter={(v) => `#${v}`}/>
            <YAxis domain={[0, 100]} tick={{ fontSize: 9, fill: "#64748b" }} width={26}/>
            <Tooltip
              contentStyle={{ fontSize: 11 }}
              labelFormatter={(idx) => {
                const d = data[idx - 1];
                return `#${idx} · ${d?.at || ""} · ${d?.recommendation || ""}`;
              }}/>
            <ReferenceLine y={75} stroke="#94a3b8" strokeDasharray="3 3" label={{ value: "min 75", fontSize: 9, fill: "#94a3b8", position: "insideRight" }}/>
            <Line
              type="monotone" dataKey="score" name="Score"
              stroke="#007934" strokeWidth={2}
              dot={{ r: 3, fill: "#007934" }} isAnimationActive={false}/>
            <Line
              type="monotone" dataKey="confidence" name="Confianza"
              stroke="#6366f1" strokeWidth={1.5} strokeDasharray="4 2"
              dot={{ r: 2, fill: "#6366f1" }} isAnimationActive={false}/>
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="flex items-center gap-3 text-[10px] text-slate-500 mt-1">
        <span><span className="inline-block w-2 h-2 rounded-full bg-[#007934] mr-1"/> Score Pando</span>
        <span><span className="inline-block w-2 h-2 rounded-full bg-indigo-500 mr-1"/> Confianza</span>
      </div>
    </div>
  );
}

function L({ label, children, help }) {
  return (
    <label className="block">
      <span className="text-[9px] uppercase text-gray-500 font-semibold inline-flex items-center gap-1">
        {label}{help ? <HelpTip text={help}/> : null}
      </span>
      {children}
    </label>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// Iter211 · Toggle rápido "Es estratégica" para triage express de PLACSP.
// Persiste vía PATCH /api/mercado/{id}/estrategica sin cerrar el modal.
// ═════════════════════════════════════════════════════════════════════════
function EstrategicaToggle({ mid, initial, onChange }) {
  const [checked, setChecked] = useState(initial !== false);
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    const next = !checked;
    setBusy(true);
    // Optimista
    setChecked(next);
    try {
      await axios.patch(`${API}/mercado/${mid}/estrategica`, null, { params: { value: next } });
      toast.success(next ? "Marcada estratégica" : "Marcada NO estratégica");
      onChange?.(next);
    } catch (e) {
      // Revertir en error
      setChecked(!next);
      toast.error(e.response?.data?.detail || "No se pudo cambiar");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      data-testid="mercado-toggle-estrategica"
      title={checked ? "Es estratégica · click para desmarcar" : "NO estratégica · click para marcar como estratégica"}
      className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full border text-[11px] font-semibold transition disabled:opacity-50 ${
        checked
          ? "bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100"
          : "bg-gray-100 border-gray-300 text-gray-500 hover:bg-gray-200"
      }`}
    >
      <span className={`w-8 h-4 rounded-full relative transition ${checked ? "bg-emerald-500" : "bg-gray-400"}`}>
        <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-all ${checked ? "left-4" : "left-0.5"}`}/>
      </span>
      {busy ? "Guardando…" : (checked ? "Es estratégica" : "No estratégica")}
    </button>
  );
}

function MercadoForm({ initial, catalog, onClose }) {
  const isEdit = !!initial;
  const [f, setF] = useState(initial ? { ...initial } : {
    expediente: "", pais: "España", provincia: "",
    empresa_g_valoriza: "", porcentaje_empresa_g_valoriza: 100,
    empresa_oferta: "", procedimiento: "",
    direccion_general: "", direccion_zona: "",
    tipo_contrato: "", tipo_cliente: "Ayuntamiento",
    actividad_informe_comercial: "",
    cliente: "", objeto_contrato: "",
    presupuesto: "", presupuesto_licitacion_ute_mm: "", presupuesto_anual_con_ute: "",
    duracion_anios: "", prorroga: "",
    fecha_presentacion: "", limite_preguntas: "",
    estado_licitacion: "POR_DECIDIR", motivo_no_presentacion: "",
    observaciones: "", plataforma_licitacion_electronica: "",
    incumbent: "", winner: "", cpv: "", url_externa: "",
  });
  const [saving, setSaving] = useState(false);
  // Iter176 · Autocompletado predictivo (mismo endpoint que Licitaciones)
  const [autocomplete, setAutocomplete] = useState(null);
  useEffect(() => {
    axios.get(`${API}/ofertas-comercial/autocomplete`)
      .then(r => setAutocomplete(r.data))
      .catch(() => setAutocomplete({}));
  }, []);

  const setField = (k, v) => setF(prev => ({...prev, [k]: v}));

  // Auto-mapeo CPV ↔ DG/Actividad
  useEffect(() => {
    if (!f.cpv || !catalog?.cpv_catalog) return;
    const match = catalog.cpv_catalog.find(c => c.cpv === String(f.cpv).replace(/\D/g, "").slice(0,8));
    if (match && !initial) {
      setF(prev => ({
        ...prev,
        direccion_general: prev.direccion_general || match.direccion_general,
        actividad_informe_comercial: prev.actividad_informe_comercial || match.actividad,
      }));
    }
  }, [f.cpv]); // eslint-disable-line

  const save = async () => {
    if (!f.cliente || !f.objeto_contrato) {
      toast.error("Cliente y objeto del contrato son obligatorios");
      return;
    }
    setSaving(true);
    try {
      // Cast numéricos
      const payload = { ...f };
      ["presupuesto", "presupuesto_licitacion_ute_mm", "presupuesto_anual_con_ute",
       "duracion_anios", "prorroga", "porcentaje_empresa_g_valoriza"].forEach(k => {
        if (payload[k] === "" || payload[k] == null) payload[k] = null;
        else payload[k] = parseFloat(payload[k]);
      });
      if (isEdit) {
        await axios.put(`${API}/mercado/${initial.id}`, payload);
        toast.success("Actualizada");
      } else {
        await axios.post(`${API}/mercado`, payload);
        toast.success("Creada");
      }
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-5" onClick={e=>e.stopPropagation()}>
        {/* Iter176 · Datalists para autocompletado (compartidos con OfertaForm) */}
        {autocomplete && (
          <>
            <datalist id="ac-cliente">{(autocomplete.cliente || []).map(v => <option key={v} value={v}/>)}</datalist>
            <datalist id="ac-provincia">{(autocomplete.provincia || []).map(v => <option key={v} value={v}/>)}</datalist>
            <datalist id="ac-empresa-g-valoriza">{(autocomplete.empresa_g_valoriza || []).map(v => <option key={v} value={v}/>)}</datalist>
            <datalist id="ac-empresa-oferta">{(autocomplete.empresas_socias || []).map(v => <option key={v} value={v}/>)}</datalist>
            <datalist id="ac-winner">{(autocomplete.winner || []).map(v => <option key={v} value={v}/>)}</datalist>
            <datalist id="ac-incumbent">{(autocomplete.incumbent || []).map(v => <option key={v} value={v}/>)}</datalist>
            <datalist id="ac-direccion-zona">{(autocomplete.direccion_zona || []).map(v => <option key={v} value={v}/>)}</datalist>
          </>
        )}
        <div className="flex items-center justify-between mb-4 sticky top-0 bg-white pb-2 border-b">
          <div className="flex items-center gap-3">
            <h3 className="text-sm font-bold text-gray-900">{isEdit ? "Editar oportunidad MERCADO" : "Nueva oportunidad MERCADO"}</h3>
            {/* Iter211 · Switch rápido "Es estratégica" (triage express PLACSP).
                Toggle inline sin cerrar el modal — persiste vía PATCH dedicado. */}
            {isEdit && (
              <EstrategicaToggle
                mid={initial.id}
                initial={f.estrategica !== false}
                onChange={(v) => setF(prev => ({ ...prev, estrategica: v }))}
              />
            )}
          </div>
          <button onClick={onClose} className="text-gray-400" data-testid="mercado-form-close"><X size={16}/></button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
          <F label="Estado *">
            <select value={f.estado_licitacion} onChange={e=>setField("estado_licitacion", e.target.value)} className="w-full border rounded px-2 py-1 bg-white" data-testid="mercado-form-estado">
              <option value="POR_DECIDIR">Por decidir</option>
              <option value="ESTUDIO">Estudio</option>
              <option value="PUBLICADA">Publicada</option>
              <option value="PRESENTADA">Presentada</option>
              <option value="NO_PRESENTADA">No presentada</option>
              <option value="SUSPENDIDA_REACTIVABLE">Suspendida (reactivable)</option>
              <option value="SUSPENDIDA_NO_REACTIVABLE">Suspendida (no reactivable)</option>
              <option value="ANULADA">Anulada</option>
            </select>
          </F>
          <F label="Expediente" value={f.expediente} onChange={v=>setField("expediente",v)}/>
          <F label="Plataforma">
            <select value={f.plataforma_licitacion_electronica} onChange={e=>setField("plataforma_licitacion_electronica", e.target.value)} className="w-full border rounded px-2 py-1 bg-white">
              <option value="">—</option>
              {catalog.plataformas.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </F>
          <F label="URL externa" value={f.url_externa} onChange={v=>setField("url_externa",v)}/>

          <F label="País" value={f.pais} onChange={v=>setField("pais",v)}/>
          <F label="Provincia" value={f.provincia} onChange={v=>setField("provincia",v)} list="ac-provincia"/>
          <F label="Dir. Zona" value={f.direccion_zona} onChange={v=>setField("direccion_zona",v)} list="ac-direccion-zona"/>

          <F label="Cliente *" value={f.cliente} onChange={v=>setField("cliente",v)} list="ac-cliente"/>
          <F label="Tipo Cliente">
            <select value={f.tipo_cliente} onChange={e=>setField("tipo_cliente", e.target.value)} className="w-full border rounded px-2 py-1 bg-white">
              <option value="">—</option>
              {catalog.tipos_cliente.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </F>
          <F label="Tipo Contrato">
            <select value={f.tipo_contrato} onChange={e=>setField("tipo_contrato", e.target.value)} className="w-full border rounded px-2 py-1 bg-white">
              <option value="">—</option>
              {catalog.tipos_contrato.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </F>

          <F label="Procedimiento">
            <select value={f.procedimiento} onChange={e=>setField("procedimiento", e.target.value)} className="w-full border rounded px-2 py-1 bg-white">
              <option value="">—</option>
              {catalog.procedimientos.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </F>
          <F label="CPV" value={f.cpv} onChange={v=>setField("cpv",v)} placeholder="90511000"/>
          <F label="Dirección General">
            <select value={f.direccion_general} onChange={e=>setField("direccion_general", e.target.value)} className="w-full border rounded px-2 py-1 bg-white">
              <option value="">—</option>
              <option value="SERVICIOS">SERVICIOS</option>
              <option value="TRATAMIENTO">TRATAMIENTO</option>
            </select>
          </F>
          <F label="Actividad">
            <select value={f.actividad_informe_comercial} onChange={e=>setField("actividad_informe_comercial", e.target.value)} className="w-full border rounded px-2 py-1 bg-white">
              <option value="">—</option>
              <option value="C&W">C&amp;W</option>
              <option value="GA">GA</option>
              <option value="MOB">MOB</option>
              <option value="Light Capex">Light Capex</option>
              <option value="PPP">PPP</option>
            </select>
          </F>

          <F label="Objeto del contrato *" span={3}>
            <textarea value={f.objeto_contrato} onChange={e=>setField("objeto_contrato", e.target.value)} rows={2} className="w-full border rounded px-2 py-1"/>
          </F>

          <F label="Presupuesto (€)" value={f.presupuesto} onChange={v=>setField("presupuesto",v)} type="number"/>
          <F label="Pres. UTE M€" value={f.presupuesto_licitacion_ute_mm} onChange={v=>setField("presupuesto_licitacion_ute_mm",v)} type="number"/>
          <F label="Pres. Anual UTE (€)" value={f.presupuesto_anual_con_ute} onChange={v=>setField("presupuesto_anual_con_ute",v)} type="number"/>

          <F label="Duración (años)" value={f.duracion_anios} onChange={v=>setField("duracion_anios",v)} type="number"/>
          <F label="Prórroga (años)" value={f.prorroga} onChange={v=>setField("prorroga",v)} type="number"/>
          <F label="F. Presentación" value={f.fecha_presentacion} onChange={v=>setField("fecha_presentacion",v)} type="date"/>

          <F label="Límite preguntas" value={f.limite_preguntas} onChange={v=>setField("limite_preguntas",v)} type="date"/>
          <F label="Empresa G. Valoriza" value={f.empresa_g_valoriza} onChange={v=>setField("empresa_g_valoriza",v)} list="ac-empresa-g-valoriza"/>
          <F label="% G. Valoriza" value={f.porcentaje_empresa_g_valoriza} onChange={v=>setField("porcentaje_empresa_g_valoriza",v)} type="number"/>

          <F label="Empresa Oferta" value={f.empresa_oferta} onChange={v=>setField("empresa_oferta",v)} list="ac-empresa-oferta"/>
          <F label="Incumbent" value={f.incumbent} onChange={v=>setField("incumbent",v)} list="ac-incumbent"/>
          <F label="Winner" value={f.winner} onChange={v=>setField("winner",v)} list="ac-winner"/>

          <F label="Estado">
            <select value={f.estado_licitacion} onChange={e=>setField("estado_licitacion", e.target.value)} className="w-full border rounded px-2 py-1 bg-white" data-testid="mercado-form-estado">
              {catalog.estados.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
            </select>
          </F>
          {f.estado_licitacion === "NO_PRESENTADA" && (
            <F label="Motivo NO">
              <select value={f.motivo_no_presentacion} onChange={e=>setField("motivo_no_presentacion", e.target.value)} className="w-full border rounded px-2 py-1 bg-white" data-testid="mercado-form-motivo">
                <option value="">—</option>
                {catalog.motivos_no_presentacion.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </F>
          )}
          <F label="Observaciones" span={3}>
            <textarea value={f.observaciones} onChange={e=>setField("observaciones", e.target.value)} rows={2} className="w-full border rounded px-2 py-1"/>
          </F>
        </div>

        <div className="flex justify-end gap-2 mt-4 pt-3 border-t">
          <button onClick={onClose} className="text-xs border px-3 py-1.5 rounded">Cancelar</button>
          <button onClick={save} disabled={saving} className="text-xs bg-[#007934] text-white px-3 py-1.5 rounded flex items-center gap-1" data-testid="mercado-save-btn">
            {saving ? <><Loader2 size={11} className="animate-spin"/>Guardando…</> : (isEdit?"Guardar":"Crear")}
          </button>
        </div>
      </div>
    </div>
  );
}

function F({ label, value, onChange, type, placeholder, span, children, list }) {
  const colSpan = span === 3 ? "md:col-span-3" : span === 2 ? "md:col-span-2" : "";
  return (
    <label className={`block ${colSpan}`}>
      <span className="text-[9px] uppercase text-gray-500 font-semibold">{label}</span>
      {children || (
        <input
          type={type || "text"}
          value={value ?? ""}
          onChange={e=>onChange(e.target.value)}
          placeholder={placeholder}
          list={list}
          className="w-full border rounded px-2 py-1"
        />
      )}
    </label>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// Iter136 · Ranking territorial CCAA
// ══════════════════════════════════════════════════════════════════════════
function CCAARanking({ items, dg }) {
  const ranking = useMemo(() => {
    const agg = {};
    (items || []).forEach(m => {
      const c = m.comunidad_autonoma || "";
      if (!c) return;
      if (!agg[c]) agg[c] = { ccaa: c, count: 0, presupuesto: 0 };
      agg[c].count += 1;
      const v = Number(m.presupuesto) || 0;
      if (v > 0) agg[c].presupuesto += v;
    });
    return Object.values(agg)
      .sort((a, b) => b.presupuesto - a.presupuesto || b.count - a.count)
      .slice(0, 5);
  }, [items]);

  if (ranking.length === 0) return null;
  const maxBudget = ranking[0]?.presupuesto || 1;
  const label = dg === "TRATAMIENTO" ? "Residuos" : dg === "SERVICIOS" ? "Servicios Urbanos" : "Todas las DGs";
  const totalBudget = ranking.reduce((s, r) => s + r.presupuesto, 0);
  const fmtEur = v => v >= 1_000_000 ? `${(v/1_000_000).toFixed(2)} M€` : v >= 1000 ? `${(v/1000).toFixed(0)} K€` : `${Math.round(v)} €`;

  return (
    <div className="mt-3 border border-emerald-100 rounded-xl p-3 bg-gradient-to-br from-emerald-50/50 to-teal-50/50" data-testid="mercado-ccaa-ranking">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-[10px] uppercase font-bold text-emerald-800 tracking-widest">Ranking territorial · Top 5 CCAA</div>
          <div className="text-[11px] text-emerald-700">Por presupuesto disponible · <b>{label}</b> · Total top 5: {fmtEur(totalBudget)}</div>
        </div>
        <MapPin size={16} className="text-emerald-600"/>
      </div>
      <div className="space-y-1.5">
        {ranking.map((r, i) => {
          const pct = (r.presupuesto / maxBudget) * 100;
          return (
            <div key={r.ccaa} className="flex items-center gap-2 text-xs" data-testid={`ccaa-rank-${i+1}`}>
              <div className="text-[10px] font-bold w-4 text-slate-500">#{i+1}</div>
              <div className="w-32 truncate font-semibold text-slate-800">{r.ccaa}</div>
              <div className="flex-1 h-4 bg-white rounded-full overflow-hidden border border-emerald-100">
                <div
                  className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full transition-all"
                  style={{width: `${pct}%`}}
                  title={`${pct.toFixed(0)}% del líder`}
                />
              </div>
              <div className="w-20 text-right font-mono font-bold text-emerald-800" data-testid={`ccaa-rank-${i+1}-budget`}>
                {fmtEur(r.presupuesto)}
              </div>
              <div className="w-12 text-right text-[10px] text-slate-500">{r.count} opps</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// Iter138 · Win Score cell (Perplexity + Claude)
// ══════════════════════════════════════════════════════════════════════════
function WinScoreCell({ m, onScore }) {
  const [running, setRunning] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const score = m.ai_win_score;
  const scored = score !== undefined && score !== null;

  const run = async (e) => {
    e?.stopPropagation();
    setRunning(true);
    try {
      const r = await axios.post(`${API}/mercado/${m.id}/win-score`);
      if (r.data?.win_probability != null) {
        toast.success(`Win score ${r.data.win_probability}% (${r.data.source})`);
        onScore && onScore();
      } else if (r.data?.error) {
        toast.error(r.data.error);
      }
    } catch (e2) {
      toast.error("Error: " + (e2.response?.data?.detail || e2.message));
    } finally {
      setRunning(false);
    }
  };

  if (!scored) {
    return (
      <button
        onClick={run}
        disabled={running}
        data-testid={`win-score-run-${m.id}`}
        title="Calcular probabilidad de ganar (Pando + noticias web)"
        className="text-[9px] font-bold border border-slate-300 hover:border-slate-500 bg-white text-slate-600 rounded px-1.5 py-0.5 disabled:opacity-50 inline-flex items-center gap-0.5">
        {running ? <Loader2 size={9} className="animate-spin"/> : <Sparkles size={9}/>}
        {running ? "…" : "Score"}
      </button>
    );
  }

  const color =
    score >= 70 ? "bg-emerald-100 text-emerald-800 border-emerald-300" :
    score >= 45 ? "bg-amber-100 text-amber-800 border-amber-300" :
                  "bg-rose-100 text-rose-800 border-rose-300";
  return (
    <>
      <button
        onClick={(e) => { e.stopPropagation(); setShowModal(true); }}
        data-testid={`win-score-badge-${m.id}`}
        title={m.ai_win_reasoning || "Click para ver detalle"}
        className={`text-[10px] font-bold border rounded-full px-2 py-0.5 ${color} inline-flex items-center gap-1`}>
        <Sparkles size={9}/> {score}%
      </button>
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowModal(false)} data-testid="win-score-modal">
          <div className="bg-white rounded-2xl max-w-lg w-full p-5 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Win Probability</div>
                <div className={`text-3xl font-bold ${score >= 70 ? "text-emerald-700" : score >= 45 ? "text-amber-700" : "text-rose-700"}`}>
                  {score}%
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  Confianza: <b>{m.ai_win_confidence || "—"}</b> · Fuente: <code className="bg-slate-100 px-1 rounded">{m.ai_win_source || "—"}</code>
                </div>
              </div>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-800 text-lg">✕</button>
            </div>
            {m.ai_win_reasoning && (
              <p className="text-sm text-slate-700 mb-3">{m.ai_win_reasoning}</p>
            )}
            {m.ai_win_signals?.length > 0 && (
              <div className="mb-3">
                <div className="text-[10px] font-bold text-emerald-800 uppercase tracking-wide mb-1">Señales positivas</div>
                <ul className="text-xs text-slate-700 list-disc pl-5 space-y-0.5">
                  {m.ai_win_signals.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            )}
            {m.ai_win_flags?.length > 0 && (
              <div className="mb-3">
                <div className="text-[10px] font-bold text-rose-800 uppercase tracking-wide mb-1">Riesgos / red flags</div>
                <ul className="text-xs text-slate-700 list-disc pl-5 space-y-0.5">
                  {m.ai_win_flags.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            )}
            <div className="flex justify-between items-center pt-3 border-t border-slate-100">
              <span className="text-[10px] text-slate-400">Actualizado {(m.ai_win_scored_at || "").slice(0, 10)}</span>
              <button
                onClick={run}
                disabled={running}
                data-testid={`win-score-recompute-${m.id}`}
                className="text-xs bg-slate-900 hover:bg-slate-800 text-white rounded-lg px-3 py-1.5 disabled:opacity-50 inline-flex items-center gap-1">
                {running ? <Loader2 size={11} className="animate-spin"/> : <Sparkles size={11}/>} Recalcular
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


// Iter169 · Celda editable inline para Incumbent/Winner con sugerencia Pando
// Valores de `winner` que NO son un adjudicatario real sino un marcador de
// "todavía sin adjudicar" (vienen del Excel/scraper: "PENDIENTE ADJ", "Buscar",
// "PTE. ADJUDICAR"…). NO deben pintarse como si fueran el ganador — se muestran
// como "Pendiente de adjudicación" en gris claro (feedback usuario).
const _normWinner = (s) => (s || "").toString().toUpperCase().replace(/\./g, " ").replace(/\s+/g, " ").trim();
function isPendingWinnerValue(v) {
  const n = _normWinner(v);
  if (!n) return false;
  if (n === "BUSCAR") return true;
  if (n.startsWith("PENDIENTE ADJ") || n.startsWith("PENDIENTE DE ADJ")) return true;
  if (n.startsWith("PTE ADJ") || n.startsWith("PDTE ADJ")) return true;
  return false;
}
function IncumbentWinnerCell({ m, field, onSaved }) {
  const _pendingInit = field === "winner" && isPendingWinnerValue(m[field]);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(_pendingInit ? "" : (m[field] || ""));
  const [loading, setLoading] = useState(false);
  const [suggest, setSuggest] = useState(null);

  const save = async () => {
    try {
      await axios.patch(`${API}/mercado/${m.id}/incumbent-winner`, { [field]: value });
      toast.success(field === "incumbent" ? "Incumbent guardado" : "Winner guardado");
      setEditing(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo guardar");
    }
  };

  const predict = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/mercado/${m.id}/predict-incumbent-winner`);
      setSuggest(r.data);
      const key = field === "incumbent" ? "predicted_incumbent" : "predicted_winner";
      if (r.data[key]) setValue(r.data[key]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Pando no pudo predecir");
    } finally { setLoading(false); }
  };

  if (!editing) {
    const pendingWinner = field === "winner" && isPendingWinnerValue(m.winner);
    const realWinner = field === "winner" && m.winner && !pendingWinner;
    const cls = realWinner
      ? "text-[9px] bg-yellow-100 text-yellow-800 px-1 py-0.5 rounded font-semibold cursor-pointer hover:bg-yellow-200"
      : "text-[10px] cursor-pointer hover:bg-emerald-50 px-1 py-0.5 rounded";
    let content;
    if (pendingWinner) {
      content = <span className="text-gray-400 italic">Pendiente de adjudicación</span>;
    } else if (m[field]) {
      content = m[field];
    } else {
      content = <span className="text-gray-300 italic">— editar —</span>;
    }
    return (
      <span
        onClick={() => setEditing(true)}
        title={pendingWinner ? "Sin adjudicatario confirmado · click para editar" : "Click para editar"}
        data-testid={`mercado-cell-${field}-${m.id}`}
        className={cls}>
        {content}
      </span>
    );
  }
  return (
    <div className="flex items-center gap-1" data-testid={`mercado-edit-${field}-${m.id}`}>
      <input
        type="text"
        value={value}
        autoFocus
        onChange={e => setValue(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter") save(); if (e.key === "Escape") { setValue(m[field] || ""); setEditing(false); } }}
        className="text-[10px] border rounded px-1 py-0.5 flex-1 min-w-0 focus:outline-none focus:ring-1 focus:ring-emerald-400"
      />
      <button onClick={save} className="text-[10px] text-emerald-700 hover:text-emerald-900" title="Guardar" data-testid={`mercado-save-${field}-${m.id}`}>✓</button>
      <button onClick={() => { setValue(m[field] || ""); setEditing(false); }} className="text-[10px] text-gray-400 hover:text-red-600" title="Cancelar">✕</button>
      <button onClick={predict} disabled={loading}
              title="Sugerir con Pando"
              data-testid={`mercado-predict-${field}-${m.id}`}
              className="text-[10px] text-indigo-600 hover:text-indigo-800 disabled:opacity-50">
        {loading ? "…" : "✨"}
      </button>
      {suggest && (
        <span className="text-[9px] text-gray-500 ml-1" title={suggest.rationale}>
          ({suggest.confidence})
        </span>
      )}
    </div>
  );
}

